import React, { useState, useMemo, useEffect, useRef } from 'react';
import { TanTable } from '../../components/TanTable';
import { ColumnDef } from '@tanstack/react-table';
import { Drawer } from '../../components/ui/Drawer';
import { DrawerPush } from '../../components/ui/DrawerPush';
import { Dropdown } from '../../components/ui/Dropdown';
import { Toast } from '../../components/ui/Toast';
import { Tooltip } from '../../components/ui/Tooltip';
import Calendar from '../../components/Calendar';
import DateTimePicker from '../../components/DateTimePicker';
import MultiSelect from '../../components/MultiSelect';
import TypeAhead, { TypeAheadOption } from '../../components/TypeAhead';
import LookupResultCard, { LookupResultCardProps } from '../order-management/components/LookupResultCard';
import Toggle from '../../components/Toggle';
import ButtonGroup from '../../components/ButtonGroup';
import TabBar from '../../components/TabBar';
import StatusBadge from '../../components/StatusBadge';
import TagBadge from '../../components/TagBadge';
import ContactCard from '../../components/ContactCard';
import StatButton from '../../components/StatButton';
import StatsGrid, { StatItem, TimePeriod } from '../../components/StatsGrid';
import TrackerButton from '../../components/TrackerButton';
import FilterBar from '../../components/FilterBar';
import ModernFilterModal from '../../components/ModernFilterModal';
import BarChartComponent from '../../components/charts/BarChartComponent';
import LineChartComponent from '../../components/charts/LineChartComponent';
import AreaChartComponent from '../../components/charts/AreaChartComponent';
import PieChartComponent from '../../components/charts/PieChartComponent';
import DonutChartComponent from '../../components/charts/DonutChartComponent';
import RadialChartComponent from '../../components/charts/RadialChartComponent';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import timezoneService from '../../services/timezone.service';
import trimbleService, { CompareRouteRequest, CompareRouteResponse, CMSLocation } from '../../services/trimble.service';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { getGoogleMapsLoaderOptions } from '../../config/googleMaps';
import { ChevronDownIcon, ListBulletIcon, Squares2X2Icon, TableCellsIcon, HomeIcon, UserIcon, Cog6ToothIcon, UsersIcon, ShoppingCartIcon, CurrencyDollarIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { MoveCard, DriverLane, RiskSection, SettingsDrawer as DSMSettingsDrawer, RemoveConfirmModal, CompactHosWidget } from '../../components/driver-status-monitor';
import type { MoveRisk, DriverWithMoves, DisplaySettings } from '../../components/driver-status-monitor';
import { DEFAULT_DISPLAY_SETTINGS } from '../../components/driver-status-monitor';

interface Component {
  id: string;
  name: string;
  category: string;
  description: string;
}

const components: Component[] = [
  // UI Components
  { id: 'drawer', name: 'Drawer', category: 'UI', description: 'Slide-out panel component' },
  { id: 'drawer-push', name: 'Drawer Push', category: 'UI', description: 'Push-style drawer component' },
  { id: 'dropdown', name: 'Dropdown', category: 'UI', description: 'Dropdown menu component' },
  { id: 'toast', name: 'Toast', category: 'UI', description: 'Toast notification component' },
  { id: 'tooltip', name: 'Tooltip', category: 'UI', description: 'Tooltip component' },

  // Core Components
  { id: 'tantable', name: 'TanTable', category: 'Core', description: 'Advanced data table with TanStack Table - Features sorting, filtering, grouping, Excel export, column locking, tabs, dynamic tab grouping by any field, and related row filtering (keeps linked rows together when filtering)' },
  { id: 'calendar', name: 'Calendar', category: 'Core', description: 'Full-featured calendar component' },
  { id: 'datetimepicker', name: 'DateTimePicker', category: 'Core', description: 'Date and time picker with calendar and scrollable time selection' },
  { id: 'multiselect', name: 'MultiSelect', category: 'Core', description: 'Multi-selection dropdown' },
  { id: 'typeahead', name: 'TypeAhead', category: 'Core', description: 'Autocomplete search input with single/multi-select, auto-highlight, Tab-to-confirm, portal rendering for modals, and custom rich result rendering (renderOption)' },
  { id: 'toggle', name: 'Toggle', category: 'Core', description: 'Toggle switch component' },
  { id: 'buttongroup', name: 'Button Group', category: 'Core', description: 'Group of related buttons' },
  { id: 'tabbar', name: 'Tab Bar', category: 'Core', description: 'Tab navigation component' },
  { id: 'filterbar', name: 'Filter Bar', category: 'Core', description: 'Sticky top bar for page filters with expandable rows, dividers, and flexible layout' },

  // Display Components
  { id: 'statusbadge', name: 'Status Badge', category: 'Display', description: 'Status indicator badge' },
  { id: 'tagbadge', name: 'Tag Badge', category: 'Display', description: 'Tag badge component' },
  { id: 'contactcard', name: 'Contact Card', category: 'Display', description: 'Contact information card' },
  { id: 'statbutton', name: 'Stat Button', category: 'Display', description: 'Statistical button component' },
  { id: 'statsgrid', name: 'Stats Grid', category: 'Display', description: '2x5 grid display for dispatch bucket counts with time period selector' },
  { id: 'trackerbutton', name: 'Tracker Button', category: 'Display', description: 'Progress tracker button with name, min/max values, and fill bar' },

  // Modals & Drawers
  { id: 'modernfiltermodal', name: 'Modern Filter Modal', category: 'Modals', description: 'Advanced filtering modal' },

  // Charts
  { id: 'charts', name: 'Charts', category: 'Charts', description: 'Chart components collection' },

  // System Specific
  { id: 'orderdetailsdrawer', name: 'Order Details Drawer', category: 'System Specific', description: 'Expandable drawer showing order details with appointment numbers, arrival/departure times, and grouped leg display' },

  // Fleet Management
  { id: 'movecard', name: 'MoveCard', category: 'Fleet Management', description: 'Driver move card showing PRO, route, status, deadlines, projected ETA, and risk level with optional drag, remove, and quick-assign interactions' },
  { id: 'compacthoswidget', name: 'CompactHosWidget', category: 'Fleet Management', description: 'Compact HOS widget with circular progress rings showing Break, Drive, Shift, Cycle times with values inside circles and duty status badge' },
  { id: 'driverlane', name: 'DriverLane', category: 'Fleet Management', description: 'Full driver row with header stats (HOS, utilization), cascading ETA calculations, move cards, deadhead indicators, and drag-drop reordering' },
  { id: 'risksection', name: 'RiskSection', category: 'Fleet Management', description: 'Collapsible section grouping drivers by risk level (Critical, At Risk, On Track, Completed) with color-coded headers' },
  { id: 'settingsdrawer-dsm', name: 'SettingsDrawer (DSM)', category: 'Fleet Management', description: 'Slide-out settings drawer for toggling status types, endorsements, and ghost card count' },
  { id: 'removeconfirmmodal', name: 'RemoveConfirmModal', category: 'Fleet Management', description: 'Confirmation modal for removing moves from a driver with linked/grouped move awareness' },

  // Development/Testing
  { id: 'timezone-lookup', name: 'Timezone Lookup', category: 'Development', description: 'Test component for timezone API - enter lat/lng coordinates to get timezone info' },
  { id: 'trimble-comparison', name: 'Trimble Maps Comparison', category: 'Development', description: 'Compare Google Maps vs Trimble Maps routing - distance, time, tolls, state mileage with diff highlighting' },
];

// Component implementation data - maps component IDs to their usage throughout the codebase
const componentImplementations: Record<string, Array<{ route: string; count: number; isReusable?: boolean }>> = {
  'drawer': [
    { route: '/administration/users', count: 1 },
    { route: 'CollapsibleDashboard (Reusable Component)', count: 5, isReusable: true },
    { route: 'ColumnCustomizationDrawer (Reusable Component)', count: 1, isReusable: true },
    { route: 'GeofenceDrawer (Reusable Component)', count: 1, isReusable: true },
    { route: 'LocationEditDrawer (Reusable Component)', count: 1, isReusable: true },
    { route: 'OrderDetailsModalContent (Reusable Component)', count: 1, isReusable: true },
    { route: 'layout/DashboardLayout (Reusable Component)', count: 1, isReusable: true },
  ],
  'tooltip': [
    { route: '/data-management/equipment-list', count: 2 },
    { route: '/data-management/field-manager', count: 1 },
    { route: '/data-management/join-manager', count: 1 },
    { route: '/orders/dispatch-planner', count: 16 },
    { route: 'CollapsibleDashboard (Reusable Component)', count: 14, isReusable: true },
    { route: 'DriverCard (Reusable Component)', count: 4, isReusable: true },
    { route: 'ExpandedLegsTable (Reusable Component)', count: 1, isReusable: true },
    { route: 'ExportsCard (Reusable Component)', count: 11, isReusable: true },
    { route: 'ImportsCard (Reusable Component)', count: 11, isReusable: true },
    { route: 'MobileProCard (Reusable Component)', count: 13, isReusable: true },
    { route: 'PermissionConfigurable (Reusable Component)', count: 1, isReusable: true },
    { route: 'TanTable (Reusable Component)', count: 23, isReusable: true },
    { route: 'charts/AreaChartComponent (Reusable Component)', count: 1, isReusable: true },
    { route: 'charts/BarChartComponent (Reusable Component)', count: 1, isReusable: true },
    { route: 'charts/DonutChartComponent (Reusable Component)', count: 1, isReusable: true },
    { route: 'charts/LineChartComponent (Reusable Component)', count: 1, isReusable: true },
    { route: 'charts/PieChartComponent (Reusable Component)', count: 1, isReusable: true },
    { route: 'charts/RadialChartComponent (Reusable Component)', count: 1, isReusable: true },
    { route: 'orderCardHelpers (Utility)', count: 1, isReusable: true },
  ],
  'tantable': [
    { route: '/data-management/equipment-list', count: 1 },
    { route: '/data-management/field-manager', count: 1 },
    { route: '/data-management/locations', count: 1 },
    { route: '/data-management/join-manager', count: 1 },
    { route: '/orders/dispatch-dashboard', count: 1 },
    { route: '/orders/dispatch-planner', count: 1 },
    { route: 'NotSentToGwiMoveCard (Reusable Component)', count: 1, isReusable: true },
  ],
  'datetimepicker': [
    { route: 'OrderDetailsModalContent (Reusable Component)', count: 4, isReusable: true },
  ],
  'multiselect': [
    { route: '/data-management/equipment-list', count: 1 },
    { route: '/fleet-management/live-map', count: 2 },
    { route: '/orders/dispatch-dashboard', count: 2 },
    { route: '/orders/dispatch-planner', count: 6 },
    { route: 'DriverDrawer (Reusable Component)', count: 1, isReusable: true },
  ],
  'typeahead': [
    { route: '/administration/users', count: 1 },
    { route: '/development/samsara-api', count: 1 },
    { route: 'PermissionConfigurable (Reusable Component)', count: 1, isReusable: true },
    { route: 'PermissionsModeToolbar (Reusable Component)', count: 1, isReusable: true },
  ],
  'toggle': [
    { route: '/data-management/equipment-list', count: 7 },
    { route: 'CollapsibleDashboard (Reusable Component)', count: 3, isReusable: true },
    { route: 'GeofenceDrawer (Reusable Component)', count: 1, isReusable: true },
    { route: 'OrderDetailsModalContent (Reusable Component)', count: 1, isReusable: true },
    { route: 'common/DataTable (Reusable Component)', count: 1, isReusable: true },
  ],
  'tabbar': [
    { route: '/orders/dispatch-dashboard', count: 1 },
  ],
  'tagbadge': [
    { route: '/analytics/aircall', count: 1 },
  ],
  'toast': [
    { route: 'ToastContext (Context)', count: 1, isReusable: true },
  ],
  'statsgrid': [
    { route: '/orders/dispatch-dashboard', count: 2 },
  ],
  'orderdetailsdrawer': [
    { route: '/orders/dispatch-planner', count: 1 },
    { route: 'ProDetailsView (Reusable Component)', count: 1, isReusable: true },
  ],
  'charts': [
    { route: '/analytics/aircall', count: 1 },
  ],
  'movecard': [
    { route: 'driver-status-monitor/DriverLane (Reusable Component)', count: 2, isReusable: true },
  ],
  'driverlane': [
    { route: 'driver-status-monitor/RiskSection (Reusable Component)', count: 1, isReusable: true },
    { route: '/fleet-management/driver-status-monitor', count: 1 },
  ],
  'risksection': [
    { route: '/fleet-management/driver-status-monitor', count: 4 },
  ],
  'settingsdrawer-dsm': [
    { route: '/fleet-management/driver-status-monitor', count: 1 },
  ],
  'removeconfirmmodal': [
    { route: '/fleet-management/driver-status-monitor', count: 1 },
  ],
};

// Mock data for Driver Status Monitor components
const mockMoveCompleted: MoveRisk = {
  pronumber: 100234, legnum: 1, movetype: 'Import Delivery', driver: 'SMITH J', dispatcher: 'Dispatcher A',
  region: 'South', billto_name: 'Acme Corp', customer_name: 'Global Logistics', origin_name: 'Houston Terminal',
  origin_city_state: 'Houston, TX', dest_name: 'Dallas Distribution', dest_city_state: 'Dallas, TX',
  dest_timezone: 'America/Chicago', origcode: 1001, destcode: 1002,
  origin_lat: 29.76, origin_lng: -95.36, dest_lat: 32.77, dest_lng: -96.79,
  appointment_time: '', deadline_date: '02/27/2026', deadline_date_raw: '2026-02-27',
  deadline_time: '2:00 PM', deadline_type: 'Appointment', per_diem_date: null, cutoff_date: null,
  container: 'ABCD1234567', size: '40', equiptype: 'Dry', legdrop: 'Drop', legaction2: '',
  status: 'Completed', estimated_minutes: 0, route_duration_minutes: 240,
  time_remaining_minutes: null, risk_score: 0, risk_level: 'low', risk_factor: null,
  driver_lat: 32.77, driver_lng: -96.79, distance_to_dest_miles: 0, driver_speed_mph: 0,
  has_realtime_location: true, can_reassign: false, requires_hazmat: false, requires_tank: false,
  requires_93k: false, requires_daycab: false, completion_order: 1, dispatch_priority: 1,
  dest_arrival_time: '1:45 PM',
};

const mockMoveInTransit: MoveRisk = {
  pronumber: 100567, legnum: 1, movetype: 'Export Pickup', driver: 'SMITH J', dispatcher: 'Dispatcher A',
  region: 'South', billto_name: 'Beta Industries', customer_name: 'Quick Ship LLC', origin_name: 'Dallas Distribution',
  origin_city_state: 'Dallas, TX', dest_name: 'Austin Yard', dest_city_state: 'Austin, TX',
  dest_timezone: 'America/Chicago', origcode: 1002, destcode: 1003,
  origin_lat: 32.77, origin_lng: -96.79, dest_lat: 30.27, dest_lng: -97.74,
  appointment_time: '5:00 PM', deadline_date: '02/27/2026', deadline_date_raw: '2026-02-27',
  deadline_time: '5:00 PM', deadline_type: 'Cutoff', per_diem_date: null, cutoff_date: null,
  container: 'EFGH7654321', size: '20', equiptype: 'Reefer', legdrop: 'Live', legaction2: '',
  status: 'In Transit', estimated_minutes: 180, route_duration_minutes: 180,
  time_remaining_minutes: 120, risk_score: 45, risk_level: 'medium', risk_factor: null,
  driver_lat: 31.5, driver_lng: -97.2, distance_to_dest_miles: 95, driver_speed_mph: 62,
  has_realtime_location: true, can_reassign: false, requires_hazmat: false, requires_tank: false,
  requires_93k: false, requires_daycab: false, dispatch_priority: 2,
};

const mockMoveNotStarted: MoveRisk = {
  pronumber: 100890, legnum: 1, movetype: 'Import Delivery', driver: 'SMITH J', dispatcher: 'Dispatcher A',
  region: 'South', billto_name: 'Gamma Solutions', customer_name: 'Express Freight', origin_name: 'Austin Yard',
  origin_city_state: 'Austin, TX', dest_name: 'San Antonio Hub', dest_city_state: 'San Antonio, TX',
  dest_timezone: 'America/Chicago', origcode: 1003, destcode: 1004,
  origin_lat: 30.27, origin_lng: -97.74, dest_lat: 29.42, dest_lng: -98.49,
  appointment_time: '', deadline_date: '02/27/2026', deadline_date_raw: '2026-02-27',
  deadline_time: '8:00 PM', deadline_type: null, per_diem_date: '2026-02-28', cutoff_date: null,
  container: 'IJKL9876543', size: '40', equiptype: 'Dry', legdrop: 'Drop', legaction2: '',
  status: 'Dispatched', estimated_minutes: 90, route_duration_minutes: 90,
  time_remaining_minutes: 300, risk_score: 15, risk_level: 'low', risk_factor: null,
  driver_lat: null, driver_lng: null, distance_to_dest_miles: null, driver_speed_mph: null,
  has_realtime_location: false, can_reassign: true, requires_hazmat: false, requires_tank: false,
  requires_93k: false, requires_daycab: false, dispatch_priority: 3,
  travel_to_next_minutes: 45,
};

const mockDriver1: DriverWithMoves = {
  unitnumber: '1234', driver_name: 'Smith, John', driver_dispatcher: 'Dispatcher A', region: 'South',
  has_hazmat: true, has_tank: false, has_93k: true, is_daycab: false, is_light_truck: false,
  driver_lat: 31.5, driver_lng: -97.2, driver_location_time: '2026-02-27T14:30:00',
  samsara_vehicle_id: 'v123', samsara_driver_id: 'd456', textmsgnumber: '5551234567', homephone: null,
  preference: 'OTR', truck_license_plate: 'ABC1234', time_utilization_pct: 75, hos_utilization_pct: 60, limiting_factor: 'time',
  drive_time_remaining_mins: 420, cycle_time_remaining_mins: 2400,
  break_time_remaining_mins: 451, shift_time_remaining_mins: 631, time_in_current_status: null,
  total_drive_time_needed_mins: 270, missing_hos: false, stale_gps: false, vin_mismatch_issue: null,
  out_of_hours: false, off_duty: false, current_duty_status: 'driving',
  risk_level: 'at_risk', today_comment: 'Running behind schedule', total_moves: 3,
  moves: [mockMoveCompleted, mockMoveInTransit, mockMoveNotStarted],
};

const mockDriver2: DriverWithMoves = {
  unitnumber: '5678', driver_name: 'Doe, Jane', driver_dispatcher: 'Dispatcher B', region: 'South',
  has_hazmat: false, has_tank: true, has_93k: false, is_daycab: true, is_light_truck: false,
  driver_lat: 30.0, driver_lng: -96.5, driver_location_time: '2026-02-27T14:00:00',
  samsara_vehicle_id: 'v789', samsara_driver_id: null, textmsgnumber: null, homephone: '5559876543',
  preference: 'LOCAL', truck_license_plate: null, time_utilization_pct: 45, hos_utilization_pct: 30, limiting_factor: 'time',
  drive_time_remaining_mins: 540, cycle_time_remaining_mins: 3000,
  break_time_remaining_mins: 480, shift_time_remaining_mins: 840, time_in_current_status: null,
  total_drive_time_needed_mins: 90, missing_hos: false, stale_gps: false, vin_mismatch_issue: null,
  out_of_hours: false, off_duty: false, current_duty_status: 'onduty',
  risk_level: 'on_track', today_comment: null, total_moves: 1,
  moves: [{
    ...mockMoveNotStarted, pronumber: 200123, driver: 'DOE J', dispatcher: 'Dispatcher B',
    origin_name: 'College Station Depot', origin_city_state: 'College Station, TX',
    dest_name: 'Bryan Terminal', dest_city_state: 'Bryan, TX',
    risk_level: 'low', risk_score: 5,
  }],
};

// Sample data for TanTable
interface SamplePerson {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  age: number;
  status: 'Active' | 'Inactive' | 'Pending';
  department: string;
}

const ComponentLibrary: React.FC = () => {
  // Google Maps loader for Trimble comparison
  const { isLoaded: googleMapsLoaded } = useJsApiLoader(getGoogleMapsLoaderOptions());

  const [selectedComponent, setSelectedComponent] = useState<Component | null>(null);

  // Collapsible sections state
  const [sectionsExpanded, setSectionsExpanded] = useState({
    features: true,
    props: true,
    configurableProps: true,
    implementations: true,
  });

  // Navigation dropdown state
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  // TanTable interactive props state
  const [tanTableProps, setTanTableProps] = useState({
    loading: false,
    enableGrouping: true,
    enableColumnFilters: true,
    showTabs: false,
    showDeletedToggle: false,
    cardHeight: false,
    lockedColumnsLeft: false,
    lockedColumnsRight: false,
    enableExcelExport: true,
    enableSearch: true,
    enableReset: true,
    enableShowHideCols: true,
    enableRowsPerPage: true,
    enableZebraStriping: false,
    searchPlaceholder: 'Search people...',
    cardTitle: 'Employee Directory',
    showEditFiltersButton: false,
    editFiltersButtonIcon: 'pencil' as 'pencil' | 'plus' | 'cog',
    editFiltersButtonTooltip: 'Edit Filters',
    showDuplicate: false,
    duplicateTooltip: 'Duplicate',
  });

  // Drawer interactive props state
  const [drawerProps, setDrawerProps] = useState({
    position: 'right' as 'left' | 'right',
    maxWidth: 'md' as 'sm' | 'md' | 'lg' | 'xl' | 'full',
    noBackdrop: false,
    withFooter: false,
  });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // DrawerPush interactive props state
  const [drawerPushProps, setDrawerPushProps] = useState({
    position: 'right' as 'left' | 'right',
    withFooter: false,
  });
  const [isDrawerPushOpen, setIsDrawerPushOpen] = useState(false);

  // Dropdown interactive props state
  const [dropdownProps, setDropdownProps] = useState({
    placement: 'bottom-end' as 'bottom' | 'bottom-start' | 'bottom-end',
    withSeparator: false,
  });
  const [selectedDropdownItem, setSelectedDropdownItem] = useState('Option 1');

  // Toast interactive props state
  const [toastProps, setToastProps] = useState({
    type: 'success' as 'success' | 'error' | 'info' | 'warning',
    persistent: false,
  });
  const [showToast, setShowToast] = useState(false);

  // Tooltip interactive props state
  const [tooltipProps, setTooltipProps] = useState({
    placement: 'top' as 'top' | 'right' | 'bottom' | 'left',
    disabled: false,
  });

  // Calendar interactive props state
  const [calendarProps, setCalendarProps] = useState({
    defaultView: 'month' as 'month' | 'week' | 'day' | 'year',
  });

  // DateTimePicker interactive props state
  const [dateTimePickerProps, setDateTimePickerProps] = useState({
    mode: 'datetime' as 'date' | 'time' | 'datetime',
    selectionType: 'single' as 'single' | 'range',
    use24Hour: false,
    disabled: false,
    showTimeQualifier: false,
    labelType: 'auto' as 'auto' | 'date' | 'datetime' | 'date-range' | 'datetime-range' | 'time' | 'time-range' | 'custom' | 'none',
    customLabel: '',
    autoApply: false,
    openDirection: 'down' as 'down' | 'up' | 'left' | 'right' | 'downleft' | 'downright' | 'upleft' | 'upright',
    inputVariant: 'default' as 'default' | 'compact' | 'modal',
  });
  const [dateTimePickerValue, setDateTimePickerValue] = useState<Date | null>(null);
  const [dateTimePickerRangeValue, setDateTimePickerRangeValue] = useState<{ startDate?: Date; endDate?: Date }>({});

  // MultiSelect interactive props state
  const [multiSelectProps, setMultiSelectProps] = useState({
    enableSearch: false,
    showClearOption: false,
    disabled: false,
  });
  const [selectedMultiSelectValues, setSelectedMultiSelectValues] = useState<string[]>(['react', 'typescript']);

  const multiSelectOptions = useMemo(() => [
    { value: 'react', label: 'React' },
    { value: 'typescript', label: 'TypeScript' },
    { value: 'javascript', label: 'JavaScript' },
    { value: 'tailwind', label: 'Tailwind CSS' },
    { value: 'nodejs', label: 'Node.js' },
    { value: 'python', label: 'Python' },
    { value: 'java', label: 'Java' },
    { value: 'csharp', label: 'C#' },
  ], []);

  // TypeAhead interactive props state
  const [typeAheadProps, setTypeAheadProps] = useState({
    showSearch: true,
    showDescriptions: false,
    disabled: false,
    groupBy: false,
    multiple: false,
  });
  const [selectedTypeAheadValue, setSelectedTypeAheadValue] = useState('usa');
  const [selectedTypeAheadMultiValue, setSelectedTypeAheadMultiValue] = useState<string[]>([]);
  // Rich-results (renderOption) demo — Bill To-style lookup rows rendered as
  // multi-line cards via the LookupResultCard component.
  const [selectedRichLookupValue, setSelectedRichLookupValue] = useState('');
  const richLookupOptions = useMemo<TypeAheadOption[]>(() => [
    {
      value: '5946', label: 'ACME LOGISTICS LLC', description: 'Dallas, TX',
      data: {
        name: 'ACME LOGISTICS LLC', code: 5946,
        address1: '1200 Commerce St', city: 'Dallas', state: 'TX', zip: '75201',
        details: [
          { label: 'Terms', value: 'Net 30' },
          { label: 'Credit Hold', value: 'No' },
          { label: 'AR Bal', value: '$12,480' },
          { label: 'Sales Rep', value: 'J. Rivera' },
        ],
      } as LookupResultCardProps,
    },
    {
      value: '6012', label: 'GULF COAST FREIGHT CO', description: 'Houston, TX',
      data: {
        name: 'GULF COAST FREIGHT CO', code: 6012,
        address1: '4400 Navigation Blvd', city: 'Houston', state: 'TX', zip: '77011',
        details: [
          { label: 'Terms', value: 'Net 15' },
          { label: 'Credit Hold', value: 'Yes', danger: true },
          { label: 'AR Bal', value: '$48,905', danger: true },
          { label: 'Sales Rep', value: 'M. Chen' },
        ],
      } as LookupResultCardProps,
    },
    {
      value: '4318', label: 'HARBOR POINT DISTRIBUTION', description: 'Savannah, GA',
      data: {
        name: 'HARBOR POINT DISTRIBUTION', code: 4318,
        address1: '88 Ocean Terminal Dr', city: 'Savannah', state: 'GA', zip: '31401',
        details: [
          { label: 'Terms', value: 'Net 30' },
          { label: 'Credit Hold', value: 'No' },
          { label: 'AR Bal', value: '$3,120' },
          { label: 'Sales Rep', value: 'A. Patel' },
        ],
      } as LookupResultCardProps,
    },
  ], []);

  // Toggle interactive props state
  const [toggleProps, setToggleProps] = useState({
    variant: 'simple' as 'simple' | 'centered' | 'with-icons' | 'with-label',
    size: 'md' as 'sm' | 'md' | 'lg',
    disabled: false,
  });
  const [toggleStates, setToggleStates] = useState({
    simple: false,
    centered: false,
    withIcons: false,
    withLabel: false,
  });

  // ButtonGroup interactive props state
  const [buttonGroupProps, setButtonGroupProps] = useState({
    withIcons: false,
  });
  const [selectedView, setSelectedView] = useState('list');

  // TabBar interactive props state
  const [tabBarProps, setTabBarProps] = useState({
    withIcons: false,
    withBadges: false,
    noBorder: false,
  });
  const [currentTab, setCurrentTab] = useState('home');

  // StatusBadge interactive props state
  const [statusBadgeProps, setStatusBadgeProps] = useState({
    color: 'green' as 'red' | 'yellow' | 'green' | 'blue' | 'indigo' | 'purple' | 'pink' | 'gray',
  });

  // TagBadge interactive props state
  const [tagBadgeProps, setTagBadgeProps] = useState({
    color: 'blue' as 'gray' | 'red' | 'yellow' | 'green' | 'blue' | 'indigo' | 'purple' | 'pink',
    removable: false,
  });
  const [activeTags, setActiveTags] = useState(['React', 'TypeScript', 'Tailwind']);

  // ContactCard interactive props state
  const [contactCardProps, setContactCardProps] = useState({
    roleColor: 'green' as 'red' | 'yellow' | 'green' | 'blue' | 'indigo' | 'purple' | 'pink' | 'gray',
  });

  // StatButton interactive props state
  const [statButtonProps, setStatButtonProps] = useState({
    withIcon: true,
  });

  // StatsGrid interactive props state
  const [statsGridProps, setStatsGridProps] = useState({
    showTimePeriod: true,
    loading: false,
    columns: 5,
    rows: 2,
  });
  const [statsGridTimePeriod, setStatsGridTimePeriod] = useState<TimePeriod>('today');
  const statsGridSampleData: StatItem[] = useMemo(() => [
    { id: 'per-diem-not-dispatched', label: 'Per Diem Not Dispatched', value: 12 },
    { id: 'cutoff-not-dispatched', label: 'Cutoff Not Dispatched', value: 8 },
    { id: 'no-outgate', label: 'No Outgate', value: 0 },
    { id: 'live-load-unload', label: 'Live Load/Unload', value: 5 },
    { id: 'dispatched-driver-oos', label: 'Dispatched Driver OOS', value: 3 },
    { id: 'past-due', label: 'Past Due', value: 15 },
    { id: 'hold', label: 'Hold', value: 2 },
    { id: 'no-driver', label: 'No Driver', value: 7 },
    { id: 'no-rate', label: 'No Rate', value: 0 },
  ], []);

  // TrackerButton interactive props state
  const [trackerButtonProps, setTrackerButtonProps] = useState({
    fillColor: 'bg-blue-500',
    barThickness: 8,
    size: 'sm' as 'xs' | 'sm' | 'md' | 'lg' | 'xl',
  });

  // Driver Status Monitor component interactive props
  const [driverLaneProps, setDriverLaneProps] = useState({
    orientation: 'horizontal' as 'horizontal' | 'vertical',
    showCompleted: true,
    hideHeader: false,
  });
  const [dsmSettingsOpen, setDsmSettingsOpen] = useState(false);
  const [dsmDisplaySettings, setDsmDisplaySettings] = useState<DisplaySettings>(DEFAULT_DISPLAY_SETTINGS);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [riskSectionExpanded, setRiskSectionExpanded] = useState(true);

  // FilterBar demo state
  const [filterBarValues, setFilterBarValues] = useState({
    orderDispatcher: [] as string[],
    driverDispatcher: [] as string[],
    region: [] as string[],
    status: [] as string[],
    showCompleted: false,
  });
  const filterBarOptions = {
    orderDispatcher: [
      { value: 'john', label: 'John Smith' },
      { value: 'jane', label: 'Jane Doe' },
      { value: 'bob', label: 'Bob Johnson' },
    ],
    driverDispatcher: [
      { value: 'mike', label: 'Mike Wilson' },
      { value: 'sarah', label: 'Sarah Brown' },
      { value: 'tom', label: 'Tom Davis' },
    ],
    region: [
      { value: 'north', label: 'North' },
      { value: 'south', label: 'South' },
      { value: 'east', label: 'East' },
      { value: 'west', label: 'West' },
    ],
    status: [
      { value: 'active', label: 'Active' },
      { value: 'inactive', label: 'Inactive' },
      { value: 'pending', label: 'Pending' },
    ],
  };

  // ModernFilterModal interactive props state
  const [isModernFilterModalOpen, setIsModernFilterModalOpen] = useState(false);

  // Charts interactive props state
  const [chartProps, setChartProps] = useState({
    chartType: 'bar' as 'bar' | 'line' | 'area' | 'pie' | 'donut' | 'radial',
    showLegend: true,
    horizontal: false,
  });

  // Order Details Drawer state
  const [isOrderDetailsDrawerOpen, setIsOrderDetailsDrawerOpen] = useState(false);

  // Timezone Lookup state
  const [timezoneLat, setTimezoneLat] = useState('29.76');
  const [timezoneLng, setTimezoneLng] = useState('-95.36');
  const [timezoneResult, setTimezoneResult] = useState<{
    tz_identifier: string;
    utc_offset: string | null;
    abbreviation: string | null;
    is_dst: boolean;
  } | null>(null);
  const [timezoneLoading, setTimezoneLoading] = useState(false);
  const [timezoneError, setTimezoneError] = useState<string | null>(null);

  // Trimble comparison state
  const [trimbleInputMode, setTrimbleInputMode] = useState<'address' | 'coords' | 'lane'>('address');
  const [trimbleOriginAddress, setTrimbleOriginAddress] = useState('Houston, TX');
  const [trimbleDestAddress, setTrimbleDestAddress] = useState('Dallas, TX');
  const [trimbleOriginLat, setTrimbleOriginLat] = useState('29.76');
  const [trimbleOriginLng, setTrimbleOriginLng] = useState('-95.36');
  const [trimbleDestLat, setTrimbleDestLat] = useState('32.77');
  const [trimbleDestLng, setTrimbleDestLng] = useState('-96.79');
  const [trimbleLoading, setTrimbleLoading] = useState(false);
  const [trimbleError, setTrimbleError] = useState<string | null>(null);
  const [trimbleResult, setTrimbleResult] = useState<any>(null);
  const [trimbleRouteView, setTrimbleRouteView] = useState<'google' | 'trimble' | 'combined'>('google');
  const [googleFrontendMetrics, setGoogleFrontendMetrics] = useState<{ distance_miles: number; duration_minutes: number } | null>(null);
  const trimbleMapRef = useRef<google.maps.Map | null>(null);
  const trimbleRouteRendererRef = useRef<google.maps.DirectionsRenderer | null>(null);
  const [trimbleMapReady, setTrimbleMapReady] = useState(false);
  // CMS Locations for Lane mode
  const [cmsLocations, setCmsLocations] = useState<CMSLocation[]>([]);
  const [cmsLocationsLoading, setCmsLocationsLoading] = useState(false);
  const [selectedOriginLocation, setSelectedOriginLocation] = useState<CMSLocation | null>(null);
  const [selectedDestLocation, setSelectedDestLocation] = useState<CMSLocation | null>(null);
  const [avoidTolls, setAvoidTolls] = useState(false);

  // Sample chart data
  const barLineChartData = useMemo(() => [
    { name: 'Jan', sales: 4000, revenue: 2400 },
    { name: 'Feb', sales: 3000, revenue: 1398 },
    { name: 'Mar', sales: 2000, revenue: 9800 },
    { name: 'Apr', sales: 2780, revenue: 3908 },
    { name: 'May', sales: 1890, revenue: 4800 },
    { name: 'Jun', sales: 2390, revenue: 3800 },
  ], []);

  const pieDonutChartData = useMemo(() => [
    { name: 'Engineering', value: 35 },
    { name: 'Marketing', value: 25 },
    { name: 'Sales', value: 20 },
    { name: 'Support', value: 15 },
    { name: 'HR', value: 5 },
  ], []);

  const radialChartData = useMemo(() => [
    { name: 'Engineering', value: 35, fill: '#3b82f6' },
    { name: 'Marketing', value: 25, fill: '#10b981' },
    { name: 'Sales', value: 20, fill: '#f59e0b' },
    { name: 'Support', value: 15, fill: '#ef4444' },
    { name: 'HR', value: 5, fill: '#8b5cf6' },
  ], []);

  const typeAheadOptions = useMemo(() => [
    { value: 'usa', label: 'United States', description: 'North America', group: 'Americas' },
    { value: 'canada', label: 'Canada', description: 'North America', group: 'Americas' },
    { value: 'mexico', label: 'Mexico', description: 'North America', group: 'Americas' },
    { value: 'uk', label: 'United Kingdom', description: 'Western Europe', group: 'Europe' },
    { value: 'france', label: 'France', description: 'Western Europe', group: 'Europe' },
    { value: 'germany', label: 'Germany', description: 'Western Europe', group: 'Europe' },
    { value: 'japan', label: 'Japan', description: 'East Asia', group: 'Asia' },
    { value: 'china', label: 'China', description: 'East Asia', group: 'Asia' },
    { value: 'india', label: 'India', description: 'South Asia', group: 'Asia' },
  ], []);

  // Sample calendar events
  const calendarEvents = useMemo(() => [
    {
      id: '1',
      name: 'Team Meeting',
      datetime: '2025-01-15T10:00',
      time: '10:00 AM',
      color: 'blue' as const,
    },
    {
      id: '2',
      name: 'Client Presentation',
      datetime: '2025-01-18T14:00',
      time: '2:00 PM',
      color: 'indigo' as const,
    },
    {
      id: '3',
      name: 'Project Deadline',
      datetime: '2025-01-22T17:00',
      time: '5:00 PM',
      color: 'pink' as const,
    },
    {
      id: '4',
      name: 'Code Review',
      datetime: '2025-01-25T11:00',
      time: '11:00 AM',
      location: 'Conference Room A',
      color: 'blue' as const,
    },
  ], []);

  // TanTable sample data
  const sampleData: SamplePerson[] = useMemo(() => [
    { id: 1, firstName: 'John', lastName: 'Doe', email: 'john.doe@example.com', age: 32, status: 'Active', department: 'Engineering' },
    { id: 2, firstName: 'Jane', lastName: 'Smith', email: 'jane.smith@example.com', age: 28, status: 'Active', department: 'Marketing' },
    { id: 3, firstName: 'Bob', lastName: 'Johnson', email: 'bob.johnson@example.com', age: 45, status: 'Inactive', department: 'Sales' },
    { id: 4, firstName: 'Alice', lastName: 'Williams', email: 'alice.williams@example.com', age: 35, status: 'Active', department: 'Engineering' },
    { id: 5, firstName: 'Charlie', lastName: 'Brown', email: 'charlie.brown@example.com', age: 29, status: 'Pending', department: 'HR' },
    { id: 6, firstName: 'Diana', lastName: 'Davis', email: 'diana.davis@example.com', age: 41, status: 'Active', department: 'Finance' },
    { id: 7, firstName: 'Eve', lastName: 'Martinez', email: 'eve.martinez@example.com', age: 26, status: 'Active', department: 'Marketing' },
    { id: 8, firstName: 'Frank', lastName: 'Garcia', email: 'frank.garcia@example.com', age: 38, status: 'Inactive', department: 'Sales' },
  ], []);

  const sampleColumns: ColumnDef<SamplePerson>[] = useMemo(() => [
    {
      accessorKey: 'id',
      header: 'ID',
      size: 60,
    },
    {
      accessorKey: 'firstName',
      header: 'First Name',
      size: 120,
    },
    {
      accessorKey: 'lastName',
      header: 'Last Name',
      size: 120,
    },
    {
      accessorKey: 'email',
      header: 'Email',
      size: 200,
    },
    {
      accessorKey: 'age',
      header: 'Age',
      size: 80,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 100,
      cell: ({ getValue }) => {
        const status = getValue() as string;
        const colorMap: Record<string, string> = {
          Active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
          Inactive: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
          Pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
        };
        return (
          <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${colorMap[status]}`}>
            {status}
          </span>
        );
      },
    },
    {
      accessorKey: 'department',
      header: 'Department',
      size: 120,
    },
  ], []);

  // Generate usage code based on selected props
  const generateUsageCode = () => {
    if (!selectedComponent) return '';

    switch (selectedComponent.id) {
      case 'tantable':
        const lines = [
          'import { TanTable } from \'./components/TanTable\';',
          'import { ColumnDef } from \'@tanstack/react-table\';',
          '',
          'interface Person {',
          '  id: number;',
          '  name: string;',
          '  email: string;',
          '}',
          '',
          'const columns: ColumnDef<Person>[] = [',
          '  { accessorKey: \'id\', header: \'ID\' },',
          '  { accessorKey: \'name\', header: \'Name\' },',
          '  { accessorKey: \'email\', header: \'Email\' },',
          '];',
          '',
          'const data: Person[] = [',
          '  { id: 1, name: \'John Doe\', email: \'john@example.com\' },',
          '  // ... more data',
          '];',
          '',
          '<TanTable',
          '  data={data}',
          '  columns={columns}',
          '  settingsKey="my-table"',
          `  searchPlaceholder="${tanTableProps.searchPlaceholder}"`,
          `  cardTitle="${tanTableProps.cardTitle}"`,
        ];

        if (tanTableProps.loading) lines.push('  loading={true}');
        if (tanTableProps.showEditFiltersButton) {
          lines.push('  showEditFiltersButton={true}');
          lines.push('  onEditFilters={() => console.log("Edit filters")}');
          if (tanTableProps.editFiltersButtonIcon !== 'pencil') {
            lines.push(`  editFiltersButtonIcon="${tanTableProps.editFiltersButtonIcon}"`);
          }
          if (tanTableProps.editFiltersButtonTooltip !== 'Edit Filters') {
            lines.push(`  editFiltersButtonTooltip="${tanTableProps.editFiltersButtonTooltip}"`);
          }
        }
        if (tanTableProps.showDuplicate) {
          lines.push('  onDuplicate={() => console.log("Duplicate")}');
          if (tanTableProps.duplicateTooltip !== 'Duplicate') {
            lines.push(`  duplicateTooltip="${tanTableProps.duplicateTooltip}"`);
          }
        }
        if (tanTableProps.enableExcelExport) lines.push('  enableExcelExport={true}');
        if (tanTableProps.enableGrouping) {
          lines.push('  enableGrouping={true}');
          lines.push('  groupableColumns={[\'department\', \'status\']}');
        }
        if (tanTableProps.enableColumnFilters) lines.push('  enableColumnFilters={true}');
        if (tanTableProps.showTabs) {
          lines.push('  showTabs={true}');
          lines.push('  tabData={[{ id: \'active\', label: \'Active\' }]}');
        }
        if (tanTableProps.showDeletedToggle) {
          lines.push('  showDeletedToggle={true}');
          lines.push('  showDeleted={false}');
        }
        if (tanTableProps.cardHeight) {
          lines.push('  cardHeight="md"');
          lines.push('  onCardHeightChange={(height) => console.log(height)}');
        }
        if (tanTableProps.lockedColumnsLeft || tanTableProps.lockedColumnsRight) {
          const lockedCols = [];
          if (tanTableProps.lockedColumnsLeft) {
            lockedCols.push("{ id: 'id', side: 'left' }");
          }
          if (tanTableProps.lockedColumnsRight) {
            lockedCols.push("{ id: 'department', side: 'right' }");
          }
          lines.push(`  lockedColumns={[${lockedCols.join(', ')}]}`);
        }

        // Show control props if they're disabled (since true is default)
        if (!tanTableProps.enableSearch) lines.push('  enableSearch={false}');
        if (!tanTableProps.enableReset) lines.push('  enableReset={false}');
        if (!tanTableProps.enableShowHideCols) lines.push('  enableShowHideCols={false}');
        if (!tanTableProps.enableRowsPerPage) lines.push('  enableRowsPerPage={false}');

        lines.push('/>');
        return lines.join('\n');
      case 'drawer':
        const drawerLines = [
          'import { Drawer } from \'./components/ui/Drawer\';',
          '',
          'const [isOpen, setIsOpen] = useState(false);',
          '',
          '<button onClick={() => setIsOpen(true)}>',
          '  Open Drawer',
          '</button>',
          '',
          '<Drawer',
          '  isOpen={isOpen}',
          '  onClose={() => setIsOpen(false)}',
          '  title="Example Drawer"',
        ];

        if (drawerProps.position !== 'right') drawerLines.push(`  position="${drawerProps.position}"`);
        if (drawerProps.maxWidth !== 'md') drawerLines.push(`  maxWidth="${drawerProps.maxWidth}"`);
        if (drawerProps.noBackdrop) drawerLines.push('  noBackdrop={true}');
        if (drawerProps.withFooter) {
          drawerLines.push('  footer={');
          drawerLines.push('    <div className="flex justify-end gap-3">');
          drawerLines.push('      <button onClick={() => setIsOpen(false)}>Cancel</button>');
          drawerLines.push('      <button onClick={() => setIsOpen(false)}>Save</button>');
          drawerLines.push('    </div>');
          drawerLines.push('  }');
        }

        drawerLines.push('>');
        drawerLines.push('  {/* Your content here */}');
        drawerLines.push('</Drawer>');

        return drawerLines.join('\n');
      case 'drawer-push':
        const drawerPushLines = [
          'import { DrawerPush } from \'./components/ui/DrawerPush\';',
          '',
          'const [isOpen, setIsOpen] = useState(false);',
          '',
          '<DrawerPush',
          '  open={isOpen}',
          '  onOpenChange={setIsOpen}',
          '  title="Push Drawer"',
          '  width="400px"',
        ];

        if (drawerPushProps.position !== 'right') drawerPushLines.push(`  position="${drawerPushProps.position}"`);
        if (drawerPushProps.withFooter) {
          drawerPushLines.push('  footer={<div>Footer content</div>}');
        }

        drawerPushLines.push('  mainContent={');
        drawerPushLines.push('    <div>');
        drawerPushLines.push('      <button onClick={() => setIsOpen(!isOpen)}>');
        drawerPushLines.push('        Toggle Drawer');
        drawerPushLines.push('      </button>');
        drawerPushLines.push('    </div>');
        drawerPushLines.push('  }');
        drawerPushLines.push('>');
        drawerPushLines.push('  {/* Drawer content */}');
        drawerPushLines.push('</DrawerPush>');

        return drawerPushLines.join('\n');
      case 'dropdown':
        const dropdownLines = [
          'import { Dropdown } from \'./components/ui/Dropdown\';',
          'import { ChevronDownIcon } from \'@heroicons/react/24/outline\';',
          '',
          'const items = [',
          '  { label: \'Option 1\', onClick: () => handleAction(1) },',
          '  { label: \'Option 2\', onClick: () => handleAction(2) },',
          '  { label: \'Option 3\', onClick: () => handleAction(3) },',
        ];

        if (dropdownProps.withSeparator) {
          dropdownLines.push('  { label: \'\', separator: true },');
          dropdownLines.push('  { label: \'Delete\', onClick: () => handleDelete(), className: \'text-red-600\' },');
        }

        dropdownLines.push('];');
        dropdownLines.push('');
        dropdownLines.push('<Dropdown');
        dropdownLines.push('  trigger={');
        dropdownLines.push('    <button className="...">');
        dropdownLines.push('      Actions');
        dropdownLines.push('      <ChevronDownIcon className="h-5 w-5" />');
        dropdownLines.push('    </button>');
        dropdownLines.push('  }');
        dropdownLines.push('  items={items}');
        if (dropdownProps.placement !== 'bottom-end') {
          dropdownLines.push(`  placement="${dropdownProps.placement}"`);
        }
        dropdownLines.push('/>');

        return dropdownLines.join('\n');
      case 'toast':
        const toastLines = [
          'import { Toast } from \'./components/ui/Toast\';',
          '',
          'const [showToast, setShowToast] = useState(false);',
          '',
          '<button onClick={() => setShowToast(true)}>',
          '  Show Toast',
          '</button>',
          '',
          '{showToast && (',
          '  <Toast',
          '    message="Your message here"',
          `    type="${toastProps.type}"`,
        ];

        if (toastProps.persistent) {
          toastLines.push('    duration={0}');
        } else {
          toastLines.push('    duration={5000}');
        }

        toastLines.push('    onClose={() => setShowToast(false)}');
        toastLines.push('  />');
        toastLines.push(')}');

        return toastLines.join('\n');
      case 'tooltip':
        const tooltipLines = [
          'import { Tooltip } from \'./components/ui/Tooltip\';',
          '',
          '<Tooltip',
          '  content="Helpful tooltip text"',
          `  placement="${tooltipProps.placement}"`,
        ];

        if (tooltipProps.disabled) {
          tooltipLines.push('  disabled={true}');
        }

        tooltipLines.push('>');
        tooltipLines.push('  <button>Hover me</button>');
        tooltipLines.push('</Tooltip>');

        return tooltipLines.join('\n');
      case 'calendar':
        const calendarLines = [
          'import Calendar from \'./components/Calendar\';',
          '',
          'const events = [',
          '  {',
          '    id: \'1\',',
          '    name: \'Team Meeting\',',
          '    datetime: \'2025-01-15T10:00\',',
          '    time: \'10:00 AM\',',
          '    color: \'blue\',',
          '  },',
          '  // ... more events',
          '];',
          '',
          '<Calendar',
          '  events={events}',
        ];

        if (calendarProps.defaultView !== 'month') {
          calendarLines.push(`  defaultView="${calendarProps.defaultView}"`);
        }

        calendarLines.push('  onAddEvent={() => handleAddEvent()}');
        calendarLines.push('  onEventClick={(event) => handleEventClick(event)}');
        calendarLines.push('/>');

        return calendarLines.join('\n');
      case 'datetimepicker':
        const dateTimePickerLines = [
          'import DateTimePicker from \'./components/DateTimePicker\';',
          '',
          'const [selectedDate, setSelectedDate] = useState<Date | null>(null);',
          '',
          '<DateTimePicker',
        ];

        if (dateTimePickerProps.mode !== 'datetime') {
          dateTimePickerLines.push(`  mode="${dateTimePickerProps.mode}"`);
        }
        if (dateTimePickerProps.selectionType !== 'single') {
          dateTimePickerLines.push(`  selectionType="${dateTimePickerProps.selectionType}"`);
        }
        if (dateTimePickerProps.use24Hour) {
          dateTimePickerLines.push('  use24Hour={true}');
        }
        if (dateTimePickerProps.disabled) {
          dateTimePickerLines.push('  disabled={true}');
        }
        dateTimePickerLines.push('  value={selectedDate}');
        dateTimePickerLines.push('  onChange={(date) => setSelectedDate(date)}');
        dateTimePickerLines.push('  placeholder="Select date and time..."');
        dateTimePickerLines.push('/>');

        return dateTimePickerLines.join('\n');
      case 'multiselect':
        const multiSelectLines = [
          'import MultiSelect from \'./components/MultiSelect\';',
          '',
          'const options = [',
          '  { value: \'react\', label: \'React\' },',
          '  { value: \'typescript\', label: \'TypeScript\' },',
          '  // ... more options',
          '];',
          '',
          'const [selected, setSelected] = useState<string[]>([]);',
          '',
          '<MultiSelect',
          '  options={options}',
          '  value={selected}',
          '  onChange={setSelected}',
          '  placeholder="Select technologies..."',
        ];

        if (multiSelectProps.enableSearch) {
          multiSelectLines.push('  enableSearch={true}');
          multiSelectLines.push('  searchPlaceholder="Search..."');
        }
        if (multiSelectProps.showClearOption) {
          multiSelectLines.push('  showClearOption={true}');
          multiSelectLines.push('  clearOptionLabel="All"');
        }
        if (multiSelectProps.disabled) {
          multiSelectLines.push('  disabled={true}');
        }

        multiSelectLines.push('/>');

        return multiSelectLines.join('\n');
      case 'typeahead':
        const typeAheadLines = [
          'import TypeAhead from \'./components/TypeAhead\';',
          '',
          'const options = [',
          '  { value: \'usa\', label: \'United States\', description: \'North America\', group: \'Americas\' },',
          '  { value: \'uk\', label: \'United Kingdom\', description: \'Western Europe\', group: \'Europe\' },',
          '  // ... more options',
          '];',
          '',
        ];

        if (typeAheadProps.multiple) {
          typeAheadLines.push('const [selected, setSelected] = useState<string[]>([]);');
        } else {
          typeAheadLines.push('const [selected, setSelected] = useState(\'usa\');');
        }

        typeAheadLines.push('');
        typeAheadLines.push('<TypeAhead');
        typeAheadLines.push('  options={options}');
        typeAheadLines.push('  value={selected}');
        typeAheadLines.push('  onChange={setSelected}');
        typeAheadLines.push('  placeholder="Search..."');

        if (typeAheadProps.multiple) {
          typeAheadLines.push('  multiple={true}');
        }
        if (!typeAheadProps.showSearch) {
          typeAheadLines.push('  showSearch={false}');
        }
        if (typeAheadProps.showDescriptions) {
          typeAheadLines.push('  showDescriptions={true}');
        }
        if (typeAheadProps.groupBy) {
          typeAheadLines.push('  groupBy={true}');
        }
        if (typeAheadProps.disabled) {
          typeAheadLines.push('  disabled={true}');
        }

        typeAheadLines.push('/>');

        return typeAheadLines.join('\n');
      case 'toggle':
        const toggleLines = [
          'import Toggle from \'./components/Toggle\';',
          '',
          'const [checked, setChecked] = useState(false);',
          '',
          '<Toggle',
          '  checked={checked}',
          '  onChange={setChecked}',
        ];

        if (toggleProps.variant !== 'simple') {
          toggleLines.push(`  variant="${toggleProps.variant}"`);
        }
        if (toggleProps.size !== 'md') {
          toggleLines.push(`  size="${toggleProps.size}"`);
        }
        if (toggleProps.disabled) {
          toggleLines.push('  disabled={true}');
        }
        if (toggleProps.variant === 'with-label') {
          toggleLines.push('  label="Setting Label"');
          toggleLines.push('  description="Optional description"');
        }

        toggleLines.push('/>');

        return toggleLines.join('\n');
      case 'buttongroup':
        const buttonGroupLines = [
          'import ButtonGroup from \'./components/ButtonGroup\';',
        ];

        if (buttonGroupProps.withIcons) {
          buttonGroupLines.push('import { ListBulletIcon, Squares2X2Icon, TableCellsIcon } from \'@heroicons/react/24/outline\';');
        }

        buttonGroupLines.push('');
        buttonGroupLines.push('const [selected, setSelected] = useState(\'list\');');
        buttonGroupLines.push('');

        if (buttonGroupProps.withIcons) {
          buttonGroupLines.push('const options = [');
          buttonGroupLines.push('  { label: \'List\', value: \'list\', icon: ListBulletIcon },');
          buttonGroupLines.push('  { label: \'Grid\', value: \'grid\', icon: Squares2X2Icon },');
          buttonGroupLines.push('  { label: \'Table\', value: \'table\', icon: TableCellsIcon },');
          buttonGroupLines.push('];');
        } else {
          buttonGroupLines.push('const options = [');
          buttonGroupLines.push('  { label: \'List\', value: \'list\' },');
          buttonGroupLines.push('  { label: \'Grid\', value: \'grid\' },');
          buttonGroupLines.push('  { label: \'Table\', value: \'table\' },');
          buttonGroupLines.push('];');
        }

        buttonGroupLines.push('');
        buttonGroupLines.push('<ButtonGroup');
        buttonGroupLines.push('  options={options}');
        buttonGroupLines.push('  value={selected}');
        buttonGroupLines.push('  onChange={setSelected}');
        buttonGroupLines.push('/>');

        return buttonGroupLines.join('\n');
      case 'tabbar':
        const tabBarLines = [
          'import TabBar from \'./components/TabBar\';',
        ];

        if (tabBarProps.withIcons) {
          tabBarLines.push('import { HomeIcon, UserIcon, Cog6ToothIcon } from \'@heroicons/react/24/outline\';');
        }

        tabBarLines.push('');
        tabBarLines.push('const [activeTab, setActiveTab] = useState(\'home\');');
        tabBarLines.push('');
        tabBarLines.push('const tabs = [');

        const exampleTabs = ['home', 'profile', 'settings'];
        exampleTabs.forEach((tab, idx) => {
          const icon = tab === 'home' ? 'HomeIcon' : tab === 'profile' ? 'UserIcon' : 'Cog6ToothIcon';
          const badge = tabBarProps.withBadges && idx < 2 ? (idx === 0 ? '3' : '12') : null;

          let tabLine = `  { id: '${tab}', label: '${tab.charAt(0).toUpperCase() + tab.slice(1)}', current: activeTab === '${tab}'`;
          if (tabBarProps.withIcons) tabLine += `, icon: <${icon} className="h-5 w-5" />`;
          if (badge) tabLine += `, badge: ${badge}`;
          tabLine += ' },';
          tabBarLines.push(tabLine);
        });

        tabBarLines.push('];');
        tabBarLines.push('');
        tabBarLines.push('<TabBar');
        tabBarLines.push('  tabs={tabs}');
        tabBarLines.push('  onTabChange={setActiveTab}');
        if (tabBarProps.noBorder) {
          tabBarLines.push('  noBorder={true}');
        }
        tabBarLines.push('/>');

        return tabBarLines.join('\n');
      case 'statusbadge':
        const statusBadgeLines = [
          'import StatusBadge from \'./components/StatusBadge\';',
          '',
          `<StatusBadge`,
          `  label="Active"`,
          `  color="${statusBadgeProps.color}"`,
          '/>',
        ];

        return statusBadgeLines.join('\n');
      case 'tagbadge':
        const tagBadgeLines = [
          'import TagBadge from \'./components/TagBadge\';',
          '',
          `<TagBadge`,
          `  label="React"`,
          `  color="${tagBadgeProps.color}"`,
        ];

        if (tagBadgeProps.removable) {
          tagBadgeLines.push('  onRemove={() => handleRemoveTag()}');
        }

        tagBadgeLines.push('/>');

        return tagBadgeLines.join('\n');
      case 'contactcard':
        const contactCardLines = [
          'import ContactCard from \'./components/ContactCard\';',
          '',
          '<ContactCard',
          '  name="Sarah Johnson"',
          '  title="Senior Engineer"',
          '  role="Engineering"',
          `  roleColor="${contactCardProps.roleColor}"`,
          '  imageUrl="https://example.com/avatar.jpg"',
          '  email="sarah.johnson@example.com"',
          '  phone="+1 (555) 123-4567"',
          '/>',
        ];

        return contactCardLines.join('\n');
      case 'orderdetailsdrawer':
        const orderDetailsDrawerLines = [
          'import ProDetailSlideOver from \'./components/pro/ProDetailSlideOver\';',
          '',
          '<ProDetailSlideOver',
          '  pronumber={selectedPro}',
          '  isOpen={isDrawerOpen && selectedPro !== null}',
          '  onClose={() => setIsDrawerOpen(false)}',
          '/>',
        ];

        return orderDetailsDrawerLines.join('\n');
      case 'statbutton':
        const statButtonLines = [
          'import StatButton from \'./components/StatButton\';',
        ];

        if (statButtonProps.withIcon) {
          statButtonLines.push('import { UsersIcon } from \'@heroicons/react/24/outline\';');
        }

        statButtonLines.push('');
        statButtonLines.push('<StatButton');
        statButtonLines.push('  label="Total Users"');
        statButtonLines.push('  value="1,234"');
        if (statButtonProps.withIcon) {
          statButtonLines.push('  icon={UsersIcon}');
        }
        statButtonLines.push('  onClick={() => handleViewUsers()}');
        statButtonLines.push('/>');

        return statButtonLines.join('\n');
      case 'statsgrid':
        const statsGridLines = [
          'import StatsGrid, { StatItem, TimePeriod } from \'./components/StatsGrid\';',
          '',
          'const [timePeriod, setTimePeriod] = useState<TimePeriod>(\'today\');',
          'const [loading, setLoading] = useState(false);',
          '',
          'const stats: StatItem[] = [',
          '  { id: \'bucket-1\', label: \'Per Diem Not Dispatched\', value: 12 },',
          '  { id: \'bucket-2\', label: \'Cutoff Not Dispatched\', value: 8 },',
          '  { id: \'bucket-3\', label: \'No Outgate\', value: 0 },',
          `  // ... more stats (supports up to ${statsGridProps.columns * statsGridProps.rows} in ${statsGridProps.rows}x${statsGridProps.columns} grid)`,
          '];',
          '',
          '<StatsGrid',
          '  stats={stats}',
        ];

        if (statsGridProps.showTimePeriod) {
          statsGridLines.push('  timePeriod={timePeriod}');
          statsGridLines.push('  onTimePeriodChange={setTimePeriod}');
        }
        if (statsGridProps.loading) {
          statsGridLines.push('  loading={loading}');
        }
        if (statsGridProps.columns !== 5) {
          statsGridLines.push(`  columns={${statsGridProps.columns}}`);
        }
        if (statsGridProps.rows !== 2) {
          statsGridLines.push(`  rows={${statsGridProps.rows}}`);
        }
        statsGridLines.push('/>');

        return statsGridLines.join('\n');
      case 'trackerbutton':
        const trackerButtonLines = [
          'import TrackerButton from \'./components/TrackerButton\';',
          '',
          '<TrackerButton',
          '  name="Thu, Feb 19"',
          '  min={25}',
          '  max={50}',
          '  descriptor="capacity"',
          '  bgColor="bg-gray-200 dark:bg-gray-700"',
          '  fillColor="bg-blue-500"',
          '  barThickness={8}',
          '  onClick={() => handleDayClick()}',
          '/>',
        ];

        return trackerButtonLines.join('\n');
      case 'filterbar':
        const filterBarLines = [
          'import FilterBar from \'./components/FilterBar\';',
          '',
          '<FilterBar',
          '  label="Filters:"',
          '  expandedContent={',
          '    <FilterBar.Row>',
          '      {/* Additional filters */}',
          '    </FilterBar.Row>',
          '  }',
          '>',
          '  <MultiSelect ... />',
          '  <MultiSelect ... />',
          '  <FilterBar.Divider />',
          '  <input type="text" placeholder="Search..." />',
          '  <FilterBar.Spacer />',
          '  <Toggle ... />',
          '</FilterBar>',
        ];

        return filterBarLines.join('\n');
      case 'modernfiltermodal':
        const modernFilterLines = [
          'import ModernFilterModal from \'./components/ModernFilterModal\';',
          '',
          'const [isOpen, setIsOpen] = useState(false);',
          '',
          '<ModernFilterModal',
          '  isOpen={isOpen}',
          '  onClose={() => setIsOpen(false)}',
          '  onSubmit={(conditions, view) => {',
          '    console.log(\'Filters:\', conditions, view);',
          '    setIsOpen(false);',
          '  }}',
          '  fields={[\'Name\', \'Email\', \'Status\']}',
          '  views={[\'All\', \'Active\', \'Inactive\']}',
          '  initialView="All"',
          '/>',
        ];

        return modernFilterLines.join('\n');
      case 'charts':
        const chartLines = [];

        switch (chartProps.chartType) {
          case 'bar':
            chartLines.push('import BarChartComponent from \'./components/charts/BarChartComponent\';');
            chartLines.push('');
            chartLines.push('const data = [');
            chartLines.push('  { name: \'Jan\', sales: 4000, revenue: 2400 },');
            chartLines.push('  { name: \'Feb\', sales: 3000, revenue: 1398 },');
            chartLines.push('  // ... more data');
            chartLines.push('];');
            chartLines.push('');
            chartLines.push('const dataKeys = [');
            chartLines.push('  { key: \'sales\', name: \'Sales\', color: \'#3b82f6\' },');
            chartLines.push('  { key: \'revenue\', name: \'Revenue\', color: \'#10b981\' },');
            chartLines.push('];');
            chartLines.push('');
            chartLines.push('<BarChartComponent');
            chartLines.push('  data={data}');
            chartLines.push('  dataKeys={dataKeys}');
            chartLines.push('  title="Monthly Sales"');
            if (chartProps.horizontal) chartLines.push('  horizontal={true}');
            if (!chartProps.showLegend) chartLines.push('  showLegend={false}');
            chartLines.push('/>');
            break;
          case 'line':
            chartLines.push('import LineChartComponent from \'./components/charts/LineChartComponent\';');
            chartLines.push('');
            chartLines.push('<LineChartComponent');
            chartLines.push('  data={data}');
            chartLines.push('  dataKeys={dataKeys}');
            chartLines.push('  title="Monthly Trend"');
            chartLines.push('/>');
            break;
          case 'area':
            chartLines.push('import AreaChartComponent from \'./components/charts/AreaChartComponent\';');
            chartLines.push('');
            chartLines.push('<AreaChartComponent');
            chartLines.push('  data={data}');
            chartLines.push('  dataKeys={dataKeys}');
            chartLines.push('  title="Monthly Area"');
            chartLines.push('/>');
            break;
          case 'pie':
            chartLines.push('import PieChartComponent from \'./components/charts/PieChartComponent\';');
            chartLines.push('');
            chartLines.push('const data = [');
            chartLines.push('  { name: \'Engineering\', value: 35 },');
            chartLines.push('  { name: \'Marketing\', value: 25 },');
            chartLines.push('  // ... more data');
            chartLines.push('];');
            chartLines.push('');
            chartLines.push('<PieChartComponent');
            chartLines.push('  data={data}');
            chartLines.push('  title="Distribution"');
            chartLines.push('/>');
            break;
          case 'donut':
            chartLines.push('import DonutChartComponent from \'./components/charts/DonutChartComponent\';');
            chartLines.push('');
            chartLines.push('<DonutChartComponent');
            chartLines.push('  data={data}');
            chartLines.push('  title="Breakdown"');
            chartLines.push('/>');
            break;
          case 'radial':
            chartLines.push('import RadialChartComponent from \'./components/charts/RadialChartComponent\';');
            chartLines.push('');
            chartLines.push('<RadialChartComponent');
            chartLines.push('  data={data}');
            chartLines.push('  title="Progress"');
            chartLines.push('/>');
            break;
        }

        return chartLines.join('\n');
      case 'timezone-lookup':
        return [
          'import timezoneService from \'../../services/timezone.service\';',
          '',
          'const [lat, setLat] = useState(\'29.76\');',
          'const [lng, setLng] = useState(\'-95.36\');',
          'const [result, setResult] = useState(null);',
          'const [loading, setLoading] = useState(false);',
          '',
          'const handleLookup = async () => {',
          '  setLoading(true);',
          '  try {',
          '    const data = await timezoneService.getTimezone(',
          '      parseFloat(lat),',
          '      parseFloat(lng)',
          '    );',
          '    setResult(data);',
          '  } catch (error) {',
          '    console.error(error);',
          '  } finally {',
          '    setLoading(false);',
          '  }',
          '};',
        ].join('\n');
      case 'movecard':
        return [
          "import { MoveCard } from './components/driver-status-monitor';",
          "import type { MoveRisk } from './components/driver-status-monitor';",
          '',
          '<MoveCard',
          '  move={move}',
          '  priority={1}',
          '  isCurrentMove={true}',
          '  projectedStatus="on_track"',
          '  projectedETA="3:45 PM"',
          '  isETAToDestination={true}',
          '  onProClick={(pro, leg) => openOrder(pro, leg)}',
          '  onRemove={(move) => handleRemove(move)}',
          '/>',
        ].join('\n');
      case 'compacthoswidget':
        return [
          "import { CompactHosWidget } from './components/driver-status-monitor';",
          '',
          '<CompactHosWidget',
          '  currentDutyStatus="driving"',
          '  breakTimeRemainingMins={451}',
          '  driveTimeRemainingMins={631}',
          '  shiftTimeRemainingMins={668}',
          '  cycleTimeRemainingMins={3968}',
          '  samsaraDriverId="12345"',
          '/>',
        ].join('\n');
      case 'driverlane':
        return [
          "import { DriverLane } from './components/driver-status-monitor';",
          "import type { DriverWithMoves, DisplaySettings } from './components/driver-status-monitor';",
          "import { DEFAULT_DISPLAY_SETTINGS } from './components/driver-status-monitor';",
          '',
          '<DriverLane',
          '  driver={driver}',
          '  showCompleted={true}',
          '  displaySettings={displaySettings}',
          `  orientation="${driverLaneProps.orientation}"`,
          driverLaneProps.hideHeader ? '  hideHeader' : '',
          '  onProClick={(pro, leg) => openOrder(pro, leg)}',
          '  onRemove={(move) => handleRemove(move)}',
          '  // All interaction callbacks are optional',
          '  // Omit them for read-only display',
          '/>',
        ].join('\n');
      case 'risksection':
        return [
          "import { RiskSection } from './components/driver-status-monitor';",
          "import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';",
          '',
          '<RiskSection',
          '  title="Critical Risk"',
          '  icon={<ExclamationTriangleIcon className="w-5 h-5" />}',
          '  headerClass={styles.sectionHeaderCritical}',
          '  drivers={data.critical_drivers}',
          '  showCompleted={showCompleted}',
          '  displaySettings={displaySettings}',
          '  isSectionExpanded={expanded}',
          '  onToggleSection={() => setExpanded(!expanded)}',
          '  orientation="horizontal"',
          '  onProClick={openOrder}',
          '  onRemove={handleRemove}',
          '/>',
        ].join('\n');
      case 'settingsdrawer-dsm':
        return [
          "import { SettingsDrawer } from './components/driver-status-monitor';",
          "import type { DisplaySettings } from './components/driver-status-monitor';",
          "import { DEFAULT_DISPLAY_SETTINGS } from './components/driver-status-monitor';",
          '',
          'const [settings, setSettings] = useState<DisplaySettings>(DEFAULT_DISPLAY_SETTINGS);',
          '',
          '<SettingsDrawer',
          '  isOpen={settingsOpen}',
          '  onClose={() => setSettingsOpen(false)}',
          '  displaySettings={settings}',
          '  onSettingsChange={setSettings}',
          '/>',
        ].join('\n');
      case 'removeconfirmmodal':
        return [
          "import { RemoveConfirmModal } from './components/driver-status-monitor';",
          "import type { MoveRisk, MoveGroup } from './components/driver-status-monitor';",
          '',
          '<RemoveConfirmModal',
          '  move={moveToRemove}',
          '  group={moveGroup}  // null for single moves',
          '  loading={removing}',
          '  onClose={() => setRemoveMove(null)}',
          '  onConfirm={handleConfirmRemove}',
          '/>',
        ].join('\n');
      case 'trimble-comparison':
        return [
          'import trimbleService from \'../../services/trimble.service\';',
          '',
          'const result = await trimbleService.compareRoute({',
          '  origin: { address: \'Houston, TX\' },',
          '  destination: { address: \'Dallas, TX\' },',
          '});',
          '',
          '// Result includes:',
          '// result.google.distance_miles, result.google.duration_minutes',
          '// result.trimble.distance_miles, result.trimble.duration_minutes',
          '// result.trimble.toll_cost, result.trimble.state_mileage',
        ].join('\n');
      default:
        return '';
    }
  };

  // Render component example based on selected component
  const renderComponentExample = () => {
    if (!selectedComponent) return null;

    switch (selectedComponent.id) {
      case 'tantable':
        const lockedColumns = [];
        if (tanTableProps.lockedColumnsLeft) {
          lockedColumns.push({ id: 'id', side: 'left' as const });
        }
        if (tanTableProps.lockedColumnsRight) {
          lockedColumns.push({ id: 'department', side: 'right' as const });
        }

        return (
          <TanTable
            data={sampleData}
            columns={sampleColumns}
            settingsKey="component-library-tantable"
            searchPlaceholder={tanTableProps.searchPlaceholder}
            cardTitle={tanTableProps.cardTitle}
            loading={tanTableProps.loading}
            showEditFiltersButton={tanTableProps.showEditFiltersButton}
            onEditFilters={tanTableProps.showEditFiltersButton ? () => console.log('Edit filters') : undefined}
            editFiltersButtonIcon={tanTableProps.editFiltersButtonIcon}
            editFiltersButtonTooltip={tanTableProps.editFiltersButtonTooltip}
            onDuplicate={tanTableProps.showDuplicate ? () => console.log('Duplicate') : undefined}
            duplicateTooltip={tanTableProps.duplicateTooltip}
            enableGrouping={tanTableProps.enableGrouping}
            groupableColumns={tanTableProps.enableGrouping ? ['department', 'status'] : []}
            enableColumnFilters={tanTableProps.enableColumnFilters}
            showTabs={tanTableProps.showTabs}
            tabData={tanTableProps.showTabs ? [
              { id: 'all', label: 'All' },
              { id: 'active', label: 'Active', filterFn: (row: SamplePerson) => row.status === 'Active' },
              { id: 'inactive', label: 'Inactive', filterFn: (row: SamplePerson) => row.status === 'Inactive' },
            ] : []}
            showDeletedToggle={tanTableProps.showDeletedToggle}
            showDeleted={false}
            onShowDeletedChange={(show) => console.log('Show deleted:', show)}
            cardHeight={tanTableProps.cardHeight ? 'md' : undefined}
            onCardHeightChange={tanTableProps.cardHeight ? (height) => console.log('Card height:', height) : undefined}
            lockedColumns={lockedColumns.length > 0 ? lockedColumns : undefined}
            enableExcelExport={tanTableProps.enableExcelExport}
            enableSearch={tanTableProps.enableSearch}
            enableReset={tanTableProps.enableReset}
            enableShowHideCols={tanTableProps.enableShowHideCols}
            enableRowsPerPage={tanTableProps.enableRowsPerPage}
            enableZebraStriping={tanTableProps.enableZebraStriping}
          />
        );
      case 'drawer':
        return (
          <div className="flex h-full items-center justify-center p-8">
            <button
              onClick={() => setIsDrawerOpen(true)}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
            >
              Open Drawer
            </button>
            <Drawer
              isOpen={isDrawerOpen}
              onClose={() => setIsDrawerOpen(false)}
              title="Example Drawer"
              position={drawerProps.position}
              maxWidth={drawerProps.maxWidth}
              noBackdrop={drawerProps.noBackdrop}
              footer={drawerProps.withFooter ? (
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setIsDrawerOpen(false)}
                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-label hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setIsDrawerOpen(false)}
                    className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
                  >
                    Save
                  </button>
                </div>
              ) : undefined}
            >
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  This is example content inside the drawer. You can put any React components here.
                </p>
                <div className="rounded-md bg-gray-100 dark:bg-gray-700 p-4">
                  <h4 className="font-medium text-heading">Sample Section</h4>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    Drawers are perfect for forms, details panels, filters, and more.
                  </p>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-label">Example Input</label>
                  <input
                    type="text"
                    placeholder="Enter text..."
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
              </div>
            </Drawer>
          </div>
        );
      case 'drawer-push':
        return (
          <DrawerPush
            open={isDrawerPushOpen}
            onOpenChange={setIsDrawerPushOpen}
            position={drawerPushProps.position}
            width="400px"
            title="Push Drawer Example"
            mainContent={
              <div className="flex h-full flex-col items-center justify-center bg-gray-50 dark:bg-gray-800 p-8">
                <h3 className="text-lg font-semibold text-heading mb-4">Main Content</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 text-center max-w-md">
                  This content gets pushed when the drawer opens. Click the button below to toggle the drawer.
                </p>
                <button
                  onClick={() => setIsDrawerPushOpen(!isDrawerPushOpen)}
                  className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
                >
                  {isDrawerPushOpen ? 'Close' : 'Open'} Drawer
                </button>
              </div>
            }
            footer={drawerPushProps.withFooter ? (
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setIsDrawerPushOpen(false)}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-label hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setIsDrawerPushOpen(false)}
                  className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
                >
                  Apply
                </button>
              </div>
            ) : undefined}
          >
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                This is a push-style drawer that moves the main content instead of overlaying it.
              </p>
              <div className="rounded-md bg-gray-100 dark:bg-gray-700 p-4">
                <h4 className="font-medium text-heading">Key Difference</h4>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  Unlike regular drawers, this pushes the page content to the side, keeping everything visible.
                </p>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-label">Sample Input</label>
                <input
                  type="text"
                  placeholder="Enter text..."
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>
            </div>
          </DrawerPush>
        );
      case 'dropdown':
        const dropdownItems: any[] = [
          { label: 'Option 1', onClick: () => setSelectedDropdownItem('Option 1') },
          { label: 'Option 2', onClick: () => setSelectedDropdownItem('Option 2') },
          { label: 'Option 3', onClick: () => setSelectedDropdownItem('Option 3') },
        ];

        if (dropdownProps.withSeparator) {
          dropdownItems.push(
            { label: '', separator: true },
            { label: 'Delete', onClick: () => setSelectedDropdownItem('Delete'), className: 'text-red-600' }
          );
        }

        return (
          <div className="flex h-full flex-col items-center justify-center p-8 space-y-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Selected: <span className="font-semibold">{selectedDropdownItem}</span>
            </div>
            <Dropdown
              trigger={
                <button className="inline-flex items-center gap-x-2 rounded-md bg-white px-4 py-2 text-sm font-semibold text-heading shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:ring-gray-600 dark:hover:bg-gray-600">
                  Actions
                  <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                </button>
              }
              items={dropdownItems}
              placement={dropdownProps.placement}
            />
          </div>
        );
      case 'toast':
        const toastMessages = {
          success: 'Operation completed successfully!',
          error: 'An error occurred. Please try again.',
          info: 'Here is some information for you.',
          warning: 'Warning: This action cannot be undone.',
        };

        return (
          <div className="flex h-full flex-col items-center justify-center p-8 space-y-4">
            <div className="space-y-2">
              <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-4">
                Click a button to show a toast notification
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <button
                  onClick={() => {
                    setToastProps(prev => ({ ...prev, type: 'success' }));
                    setShowToast(true);
                  }}
                  className="rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500"
                >
                  Show Success
                </button>
                <button
                  onClick={() => {
                    setToastProps(prev => ({ ...prev, type: 'error' }));
                    setShowToast(true);
                  }}
                  className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
                >
                  Show Error
                </button>
                <button
                  onClick={() => {
                    setToastProps(prev => ({ ...prev, type: 'info' }));
                    setShowToast(true);
                  }}
                  className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500"
                >
                  Show Info
                </button>
                <button
                  onClick={() => {
                    setToastProps(prev => ({ ...prev, type: 'warning' }));
                    setShowToast(true);
                  }}
                  className="rounded-md bg-yellow-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-yellow-500"
                >
                  Show Warning
                </button>
              </div>
            </div>
            {showToast && (
              <Toast
                message={toastMessages[toastProps.type]}
                type={toastProps.type}
                duration={toastProps.persistent ? 0 : 5000}
                onClose={() => setShowToast(false)}
              />
            )}
          </div>
        );
      case 'tooltip':
        return (
          <div className="flex h-full flex-col items-center justify-center p-8 space-y-8">
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
              Hover over the buttons to see tooltips in different positions
            </p>
            <div className="grid grid-cols-2 gap-8">
              <Tooltip content="This is a top tooltip" placement="top" disabled={tooltipProps.disabled}>
                <button className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500">
                  Top Tooltip
                </button>
              </Tooltip>
              <Tooltip content="This is a right tooltip" placement="right" disabled={tooltipProps.disabled}>
                <button className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500">
                  Right Tooltip
                </button>
              </Tooltip>
              <Tooltip content="This is a bottom tooltip" placement="bottom" disabled={tooltipProps.disabled}>
                <button className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500">
                  Bottom Tooltip
                </button>
              </Tooltip>
              <Tooltip content="This is a left tooltip" placement="left" disabled={tooltipProps.disabled}>
                <button className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500">
                  Left Tooltip
                </button>
              </Tooltip>
            </div>
            <div className="mt-8">
              <Tooltip content={<div className="text-xs">You can use <strong>rich content</strong> in tooltips!</div>} placement={tooltipProps.placement} disabled={tooltipProps.disabled}>
                <button className="rounded-md bg-purple-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-purple-500">
                  Rich Content Tooltip
                </button>
              </Tooltip>
            </div>
          </div>
        );
      case 'calendar':
        return (
          <div className="flex h-full flex-col overflow-hidden p-4">
            <Calendar
              key={calendarProps.defaultView}
              events={calendarEvents}
              defaultView={calendarProps.defaultView}
              onAddEvent={() => alert('Add event clicked')}
              onEventClick={(event) => alert(`Event clicked: ${event.name}`)}
            />
          </div>
        );
      case 'datetimepicker':
        return (
          <div className="flex h-full flex-col items-center justify-start p-8 pt-4 space-y-6 overflow-visible">
            <div className="w-full max-w-2xl space-y-6">
              <div className="relative z-20 p-6 bg-panel rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex justify-center">
                  <DateTimePicker
                    key={`${dateTimePickerProps.mode}-${dateTimePickerProps.selectionType}-${dateTimePickerProps.use24Hour}-${dateTimePickerProps.showTimeQualifier}-${dateTimePickerProps.labelType}-${dateTimePickerProps.customLabel}-${dateTimePickerProps.autoApply}-${dateTimePickerProps.openDirection}-${dateTimePickerProps.inputVariant}`}
                    mode={dateTimePickerProps.mode}
                    selectionType={dateTimePickerProps.selectionType}
                    use24Hour={dateTimePickerProps.use24Hour}
                    disabled={dateTimePickerProps.disabled}
                    showTimeQualifier={dateTimePickerProps.showTimeQualifier}
                    labelType={dateTimePickerProps.labelType}
                    label={dateTimePickerProps.labelType === 'custom' ? dateTimePickerProps.customLabel : undefined}
                    autoApply={dateTimePickerProps.autoApply}
                    openDirection={dateTimePickerProps.openDirection}
                    inputVariant={dateTimePickerProps.inputVariant}
                    value={dateTimePickerProps.selectionType === 'range' ? dateTimePickerRangeValue : dateTimePickerValue}
                    onChange={(val) => {
                      if (dateTimePickerProps.selectionType === 'range') {
                        setDateTimePickerRangeValue(val as { startDate?: Date; endDate?: Date });
                      } else {
                        setDateTimePickerValue(val as Date | null);
                      }
                    }}
                    placeholder={
                      dateTimePickerProps.mode === 'date' ? 'Select a date...' :
                      dateTimePickerProps.mode === 'time' ? 'Select a time...' :
                      'Select date and time...'
                    }
                  />
                </div>
              </div>
              <div className="relative z-10 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                <h4 className="text-sm font-medium text-label mb-2">Selected Value:</h4>
                <div className="text-sm text-body">
                  {dateTimePickerProps.selectionType === 'range' ? (
                    dateTimePickerRangeValue?.startDate || dateTimePickerRangeValue?.endDate ? (
                      <span className="inline-flex items-center rounded-md bg-indigo-100 dark:bg-indigo-900/50 px-3 py-1 text-sm font-medium text-indigo-700 dark:text-indigo-300">
                        {dateTimePickerRangeValue?.startDate ? (dateTimePickerProps.mode === 'date' ? dateTimePickerRangeValue.startDate.toLocaleDateString() : dateTimePickerRangeValue.startDate.toLocaleString()) : 'Not set'} → {dateTimePickerRangeValue?.endDate ? (dateTimePickerProps.mode === 'date' ? dateTimePickerRangeValue.endDate.toLocaleDateString() : dateTimePickerRangeValue.endDate.toLocaleString()) : 'Not set'}
                      </span>
                    ) : (
                      <span className="text-muted">No range selected</span>
                    )
                  ) : (
                    dateTimePickerValue ? (
                      <span className="inline-flex items-center rounded-md bg-indigo-100 dark:bg-indigo-900/50 px-3 py-1 text-sm font-medium text-indigo-700 dark:text-indigo-300">
                        {dateTimePickerProps.mode === 'date' ? dateTimePickerValue.toLocaleDateString() : dateTimePickerValue.toLocaleString()}
                      </span>
                    ) : (
                      <span className="text-muted">No date selected</span>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      case 'multiselect':
        return (
          <div className="flex h-full flex-col items-center justify-center p-8 space-y-6">
            <div className="w-full max-w-md space-y-4">
              <div>
                <label className="block text-sm font-medium text-label mb-2">
                  Select Technologies
                </label>
                <MultiSelect
                  options={multiSelectOptions}
                  value={selectedMultiSelectValues}
                  onChange={setSelectedMultiSelectValues}
                  placeholder="Select technologies..."
                  enableSearch={multiSelectProps.enableSearch}
                  showClearOption={multiSelectProps.showClearOption}
                  disabled={multiSelectProps.disabled}
                  clearOptionLabel="All Technologies"
                  searchPlaceholder="Search technologies..."
                />
              </div>
              <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                <h4 className="text-sm font-medium text-label mb-2">Selected Values:</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedMultiSelectValues.length === 0 ? (
                    <span className="text-sm text-muted">None selected</span>
                  ) : (
                    selectedMultiSelectValues.map(val => (
                      <span key={val} className="inline-flex items-center rounded-md bg-indigo-100 dark:bg-indigo-900/50 px-2 py-1 text-xs font-medium text-indigo-700 dark:text-indigo-300">
                        {multiSelectOptions.find(opt => opt.value === val)?.label || val}
                      </span>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      case 'typeahead':
        return (
          <div className="flex h-full flex-col items-center justify-center p-8 space-y-6">
            <div className="w-full max-w-md space-y-6">
              {/* Single Select Mode */}
              {!typeAheadProps.multiple && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-label mb-2">
                      Select Country (Single)
                    </label>
                    <TypeAhead
                      options={typeAheadOptions}
                      value={selectedTypeAheadValue}
                      onChange={setSelectedTypeAheadValue}
                      placeholder="Search countries..."
                      showSearch={typeAheadProps.showSearch}
                      showDescriptions={typeAheadProps.showDescriptions}
                      disabled={typeAheadProps.disabled}
                      groupBy={typeAheadProps.groupBy}
                    />
                  </div>
                  <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <h4 className="text-sm font-medium text-label mb-2">Selected Value:</h4>
                    <div className="text-sm text-body">
                      {selectedTypeAheadValue ? (
                        <span className="inline-flex items-center rounded-md bg-indigo-100 dark:bg-indigo-900/50 px-3 py-1 text-sm font-medium text-indigo-700 dark:text-indigo-300">
                          {typeAheadOptions.find(opt => opt.value === selectedTypeAheadValue)?.label || selectedTypeAheadValue}
                        </span>
                      ) : (
                        <span className="text-muted">None selected</span>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Multi-Select Mode */}
              {typeAheadProps.multiple && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-label mb-2">
                      Select Countries (Multiple)
                    </label>
                    <TypeAhead
                      options={typeAheadOptions}
                      value={selectedTypeAheadMultiValue}
                      onChange={setSelectedTypeAheadMultiValue}
                      placeholder="Search countries..."
                      showSearch={typeAheadProps.showSearch}
                      showDescriptions={typeAheadProps.showDescriptions}
                      disabled={typeAheadProps.disabled}
                      groupBy={typeAheadProps.groupBy}
                      multiple={true}
                    />
                  </div>
                  <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <h4 className="text-sm font-medium text-label mb-2">Selected Values:</h4>
                    <div className="text-sm text-gray-900 flex flex-wrap gap-2">
                      {selectedTypeAheadMultiValue.length > 0 ? (
                        selectedTypeAheadMultiValue.map(val => {
                          const option = typeAheadOptions.find(opt => opt.value === val);
                          return (
                            <span key={val} className="inline-flex items-center rounded-md bg-indigo-100 dark:bg-indigo-900/50 px-3 py-1 text-sm font-medium text-indigo-700 dark:text-indigo-300">
                              {option?.label || val}
                            </span>
                          );
                        })
                      ) : (
                        <span className="text-muted">None selected</span>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Rich results via renderOption */}
              <div>
                <label className="block text-sm font-medium text-label mb-2">
                  Rich Results (renderOption)
                </label>
                <TypeAhead
                  options={richLookupOptions}
                  value={selectedRichLookupValue}
                  onChange={setSelectedRichLookupValue}
                  placeholder="Search Bill To..."
                  maxHeight="max-h-96"
                  renderOption={(opt) => <LookupResultCard {...(opt.data as LookupResultCardProps)} />}
                />
                <p className="mt-2 text-xs text-muted">
                  The <span className="font-semibold text-label">renderOption</span> hook
                  replaces the default label/description with any React node — here a multi-line lookup card
                  (name + #id, address, and a Terms / Credit Hold / AR Bal detail line). It keeps the same
                  value / onChange contract; return <span className="font-semibold text-label">null</span> to
                  fall back to the default rendering for a given option.
                </p>
              </div>
            </div>
          </div>
        );
      case 'toggle':
        return (
          <div className="flex h-full flex-col items-center justify-center p-8 space-y-8">
            <div className="w-full max-w-2xl space-y-6">
              <div className="p-6 bg-panel rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-heading mb-4">Variants</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-label">Simple</span>
                    <Toggle
                      checked={toggleStates.simple}
                      onChange={(checked) => setToggleStates(prev => ({ ...prev, simple: checked }))}
                      variant="simple"
                      size={toggleProps.size}
                      disabled={toggleProps.disabled}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-label">Centered</span>
                    <Toggle
                      checked={toggleStates.centered}
                      onChange={(checked) => setToggleStates(prev => ({ ...prev, centered: checked }))}
                      variant="centered"
                      size={toggleProps.size}
                      disabled={toggleProps.disabled}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-label">With Icons</span>
                    <Toggle
                      checked={toggleStates.withIcons}
                      onChange={(checked) => setToggleStates(prev => ({ ...prev, withIcons: checked }))}
                      variant="with-icons"
                      size={toggleProps.size}
                      disabled={toggleProps.disabled}
                    />
                  </div>
                  <div className="border-t border-gray-200 pt-4">
                    <Toggle
                      checked={toggleStates.withLabel}
                      onChange={(checked) => setToggleStates(prev => ({ ...prev, withLabel: checked }))}
                      variant="with-label"
                      size={toggleProps.size}
                      disabled={toggleProps.disabled}
                      label="With Label Variant"
                      description="This variant shows a label and description"
                    />
                  </div>
                </div>
              </div>
              <div className="p-6 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                <h4 className="text-sm font-semibold text-heading mb-3">Sizes</h4>
                <div className="flex items-center gap-6">
                  <div className="flex flex-col items-center gap-2">
                    <Toggle checked={true} size="sm" />
                    <span className="text-xs text-gray-600 dark:text-gray-400">Small</span>
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <Toggle checked={true} size="md" />
                    <span className="text-xs text-gray-600 dark:text-gray-400">Medium</span>
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <Toggle checked={true} size="lg" />
                    <span className="text-xs text-gray-600 dark:text-gray-400">Large</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'buttongroup':
        const viewOptions = buttonGroupProps.withIcons
          ? [
              { label: 'List', value: 'list', icon: ListBulletIcon },
              { label: 'Grid', value: 'grid', icon: Squares2X2Icon },
              { label: 'Table', value: 'table', icon: TableCellsIcon },
            ]
          : [
              { label: 'List', value: 'list' },
              { label: 'Grid', value: 'grid' },
              { label: 'Table', value: 'table' },
            ];

        return (
          <div className="flex h-full flex-col items-center justify-center p-8 space-y-8">
            <div className="w-full max-w-md space-y-6">
              <div className="p-6 bg-panel rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-heading mb-4">View Selector</h3>
                <div className="flex justify-center">
                  <ButtonGroup
                    options={viewOptions}
                    value={selectedView}
                    onChange={setSelectedView}
                  />
                </div>
              </div>
              <div className="p-6 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                <h4 className="text-sm font-semibold text-heading mb-3">Current Selection:</h4>
                <div className="text-center">
                  <span className="inline-flex items-center rounded-md bg-indigo-100 dark:bg-indigo-900/50 px-3 py-1 text-sm font-medium text-indigo-700 dark:text-indigo-300 capitalize">
                    {selectedView}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );
      case 'tabbar':
        const tabBarTabs = [
          {
            id: 'home',
            label: 'Home',
            current: currentTab === 'home',
            icon: tabBarProps.withIcons ? <HomeIcon className="h-5 w-5" /> : undefined,
            badge: tabBarProps.withBadges ? 3 : undefined,
          },
          {
            id: 'profile',
            label: 'Profile',
            current: currentTab === 'profile',
            icon: tabBarProps.withIcons ? <UserIcon className="h-5 w-5" /> : undefined,
            badge: tabBarProps.withBadges ? 12 : undefined,
          },
          {
            id: 'settings',
            label: 'Settings',
            current: currentTab === 'settings',
            icon: tabBarProps.withIcons ? <Cog6ToothIcon className="h-5 w-5" /> : undefined,
          },
        ];

        return (
          <div className="flex h-full flex-col p-8 space-y-6">
            <div className="w-full max-w-3xl mx-auto space-y-4">
              <TabBar
                tabs={tabBarTabs}
                onTabChange={setCurrentTab}
                noBorder={tabBarProps.noBorder}
              />
              <div className="p-6 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                <h4 className="text-sm font-semibold text-heading mb-3">Active Tab Content:</h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentTab === 'home' && <p>Home tab content would go here...</p>}
                  {currentTab === 'profile' && <p>Profile tab content would go here...</p>}
                  {currentTab === 'settings' && <p>Settings tab content would go here...</p>}
                </div>
              </div>
            </div>
          </div>
        );
      case 'statusbadge':
        const statusExamples = [
          { label: 'Active', color: 'green' as const },
          { label: 'Pending', color: 'yellow' as const },
          { label: 'Error', color: 'red' as const },
          { label: 'Info', color: 'blue' as const },
          { label: 'Processing', color: 'indigo' as const },
          { label: 'Premium', color: 'purple' as const },
          { label: 'Featured', color: 'pink' as const },
          { label: 'Inactive', color: 'gray' as const },
        ];

        return (
          <div className="flex h-full flex-col items-center justify-center p-8 space-y-8">
            <div className="w-full max-w-2xl space-y-6">
              <div className="p-6 bg-panel rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-heading mb-4">All Colors</h3>
                <div className="flex flex-wrap gap-3">
                  {statusExamples.map((example) => (
                    <StatusBadge
                      key={example.color}
                      label={example.label}
                      color={example.color}
                    />
                  ))}
                </div>
              </div>
              <div className="p-6 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                <h4 className="text-sm font-semibold text-heading mb-3">Use Cases</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded">
                    <span className="text-sm text-label">User Status</span>
                    <StatusBadge label="Online" color="green" />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded">
                    <span className="text-sm text-label">Order Status</span>
                    <StatusBadge label="Pending Payment" color="yellow" />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded">
                    <span className="text-sm text-label">System Status</span>
                    <StatusBadge label="Operational" color="blue" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'tagbadge':
        const tagColors = ['gray', 'red', 'yellow', 'green', 'blue', 'indigo', 'purple', 'pink'] as const;

        return (
          <div className="flex h-full flex-col items-center justify-center p-8 space-y-8">
            <div className="w-full max-w-2xl space-y-6">
              <div className="p-6 bg-panel rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-heading mb-4">All Colors</h3>
                <div className="flex flex-wrap gap-2">
                  {tagColors.map((color) => (
                    <TagBadge
                      key={color}
                      label={color.charAt(0).toUpperCase() + color.slice(1)}
                      color={color}
                      onRemove={tagBadgeProps.removable ? () => alert(`Remove ${color}`) : undefined}
                    />
                  ))}
                </div>
              </div>
              <div className="p-6 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                <h4 className="text-sm font-semibold text-heading mb-3">Active Tags</h4>
                <div className="flex flex-wrap gap-2">
                  {activeTags.map((tag) => (
                    <TagBadge
                      key={tag}
                      label={tag}
                      color={tagBadgeProps.color}
                      onRemove={tagBadgeProps.removable ? () => {
                        setActiveTags(prev => prev.filter(t => t !== tag));
                      } : undefined}
                    />
                  ))}
                </div>
                {activeTags.length === 0 && (
                  <p className="text-sm text-muted text-center mt-4">All tags removed</p>
                )}
              </div>
            </div>
          </div>
        );
      case 'contactcard':
        return (
          <div className="flex h-full items-center justify-center p-8">
            <ul className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <ContactCard
                name="Sarah Johnson"
                title="Senior Engineer"
                role="Engineering"
                roleColor={contactCardProps.roleColor}
                imageUrl="https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
                email="sarah.johnson@example.com"
                phone="+1 (555) 123-4567"
              />
              <ContactCard
                name="Michael Chen"
                title="Product Manager"
                role="Product"
                roleColor="blue"
                imageUrl="https://images.unsplash.com/photo-1519244703995-f4e0f30006d5?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
                email="michael.chen@example.com"
                phone="+1 (555) 234-5678"
              />
              <ContactCard
                name="Emily Davis"
                title="Lead Designer"
                role="Design"
                roleColor="purple"
                imageUrl="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
                email="emily.davis@example.com"
                phone="+1 (555) 345-6789"
              />
            </ul>
          </div>
        );
      case 'statbutton':
        return (
          <div className="flex h-full flex-col items-center justify-center p-8 space-y-8">
            <div className="w-full max-w-2xl space-y-6">
              <div className="p-6 bg-panel rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-heading mb-4">Dashboard Stats</h3>
                <div className="flex flex-wrap gap-4">
                  <StatButton
                    label="Total Users"
                    value="1,234"
                    icon={statButtonProps.withIcon ? UsersIcon : undefined}
                    onClick={() => alert('View all users')}
                  />
                  <StatButton
                    label="Orders"
                    value="856"
                    icon={statButtonProps.withIcon ? ShoppingCartIcon : undefined}
                    onClick={() => alert('View orders')}
                  />
                  <StatButton
                    label="Revenue"
                    value="$45.2K"
                    icon={statButtonProps.withIcon ? CurrencyDollarIcon : undefined}
                    onClick={() => alert('View revenue')}
                  />
                </div>
              </div>
              <div className="p-6 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                <h4 className="text-sm font-semibold text-heading mb-3">Features</h4>
                <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                  <li>• Two-part button design (label + value)</li>
                  <li>• Click to trigger actions</li>
                  <li>• Optional icon support</li>
                  <li>• Perfect for dashboard metrics</li>
                </ul>
              </div>
            </div>
          </div>
        );
      case 'statsgrid':
        return (
          <div className="flex h-full flex-col items-center justify-center p-8 space-y-8 overflow-x-auto">
            <div className="w-full min-w-[900px] space-y-6">
              <StatsGrid
                stats={statsGridSampleData}
                timePeriod={statsGridProps.showTimePeriod ? statsGridTimePeriod : undefined}
                onTimePeriodChange={statsGridProps.showTimePeriod ? setStatsGridTimePeriod : undefined}
                loading={statsGridProps.loading}
                columns={statsGridProps.columns}
                rows={statsGridProps.rows}
              />
              <div className="p-6 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                <h4 className="text-sm font-semibold text-heading mb-3">Features</h4>
                <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                  <li>• Configurable grid layout ({statsGridProps.rows}x{statsGridProps.columns} = {statsGridProps.rows * statsGridProps.columns} cells)</li>
                  <li>• Time period selector (Today, Tomorrow, This Week)</li>
                  <li>• Green values with thumbs-up icon for zero counts</li>
                  <li>• Red values for non-zero counts</li>
                  <li>• Loading spinner state for async data fetching</li>
                  <li>• Clickable cells with hover states</li>
                  <li>• Dark mode support</li>
                </ul>
              </div>
            </div>
          </div>
        );
      case 'trackerbutton':
        return (
          <div className="flex h-full flex-col items-center justify-center p-8 space-y-8">
            <div className="w-full max-w-md space-y-4">
              <h3 className="text-sm font-semibold text-heading mb-4">Running Totals</h3>
              <TrackerButton
                name="Thu, Feb 19"
                min={25}
                max={50}
                descriptor="capacity"
                fillColor={trackerButtonProps.fillColor}
                barThickness={trackerButtonProps.barThickness}
                size={trackerButtonProps.size}
                onClick={() => alert('Clicked Thu, Feb 19')}
              />
              <TrackerButton
                name="Fri, Feb 20"
                min={40}
                max={50}
                descriptor="capacity"
                fillColor={trackerButtonProps.fillColor}
                barThickness={trackerButtonProps.barThickness}
                size={trackerButtonProps.size}
                onClick={() => alert('Clicked Fri, Feb 20')}
              />
              <TrackerButton
                name="Sat, Feb 21"
                min={48}
                max={50}
                descriptor="capacity"
                fillColor={trackerButtonProps.fillColor}
                barThickness={trackerButtonProps.barThickness}
                size={trackerButtonProps.size}
                onClick={() => alert('Clicked Sat, Feb 21')}
              />
              <TrackerButton
                name="Sun, Feb 22"
                min={0}
                max={50}
                descriptor="capacity"
                fillColor={trackerButtonProps.fillColor}
                barThickness={trackerButtonProps.barThickness}
                size={trackerButtonProps.size}
                onClick={() => alert('Clicked Sun, Feb 22')}
              />
              <TrackerButton
                name="Mon, Feb 23"
                min={12}
                max={50}
                descriptor="capacity"
                fillColor={trackerButtonProps.fillColor}
                barThickness={trackerButtonProps.barThickness}
                size={trackerButtonProps.size}
                onClick={() => alert('Clicked Mon, Feb 23')}
              />
            </div>
          </div>
        );
      case 'filterbar':
        return (
          <div className="flex flex-col h-full">
            <FilterBar
              label="Filters:"
              expandedContent={
                <>
                  <FilterBar.Row>
                    <FilterBar.Group label="More:">
                      <MultiSelect
                        options={filterBarOptions.region}
                        value={filterBarValues.region}
                        onChange={(values) => setFilterBarValues(prev => ({ ...prev, region: values }))}
                        placeholder="All Regions"
                        size="sm"
                        className="min-w-[130px]"
                      />
                      <MultiSelect
                        options={filterBarOptions.status}
                        value={filterBarValues.status}
                        onChange={(values) => setFilterBarValues(prev => ({ ...prev, status: values }))}
                        placeholder="All Statuses"
                        size="sm"
                        className="min-w-[130px]"
                      />
                    </FilterBar.Group>
                  </FilterBar.Row>
                </>
              }
            >
              <MultiSelect
                options={filterBarOptions.orderDispatcher}
                value={filterBarValues.orderDispatcher}
                onChange={(values) => setFilterBarValues(prev => ({ ...prev, orderDispatcher: values }))}
                placeholder="Order Dispatcher"
                size="sm"
                className="min-w-[140px]"
              />
              <MultiSelect
                options={filterBarOptions.driverDispatcher}
                value={filterBarValues.driverDispatcher}
                onChange={(values) => setFilterBarValues(prev => ({ ...prev, driverDispatcher: values }))}
                placeholder="Driver Dispatcher"
                size="sm"
                className="min-w-[140px]"
              />
              <FilterBar.Divider />
              <input
                type="text"
                placeholder="Filter drivers, PROs, locations..."
                className="text-sm border border-gray-300 dark:border-gray-600 rounded-md pl-3 pr-3 py-1 bg-panel text-body placeholder-gray-400 w-56"
              />
              <FilterBar.Spacer />
              <div className="flex items-center gap-2">
                <Toggle
                  checked={filterBarValues.showCompleted}
                  onChange={(checked) => setFilterBarValues(prev => ({ ...prev, showCompleted: checked }))}
                  size="sm"
                />
                <span className="text-sm text-label">Show Completed</span>
              </div>
            </FilterBar>
            <div className="flex-1 p-4 bg-gray-50 dark:bg-gray-800/50">
              <p className="text-sm text-muted">
                Page content goes here. The FilterBar above is sticky and will stay at the top when scrolling.
              </p>
            </div>
          </div>
        );
      case 'modernfiltermodal':
        return (
          <div className="flex h-full flex-col items-center justify-center p-8">
            <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
              Click the button to open the advanced filter modal
            </div>
            <button
              onClick={() => setIsModernFilterModalOpen(true)}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
            >
              Open Filter Modal
            </button>
            <ModernFilterModal
              isOpen={isModernFilterModalOpen}
              onClose={() => setIsModernFilterModalOpen(false)}
              onSubmit={(conditions, view) => {
                console.log('Filters applied:', conditions, view);
                alert(`Filters applied for view: ${view} with ${conditions.filter(c => c.field).length} conditions`);
                setIsModernFilterModalOpen(false);
              }}
              fields={['Name', 'Email', 'Status', 'Department', 'Created Date', 'Location']}
              views={['All Users', 'Active Only', 'Inactive Only', 'Pending Approval']}
              initialView="All Users"
            />
          </div>
        );
      case 'charts':
        const dataKeys = [
          { key: 'sales', name: 'Sales', color: '#3b82f6' },
          { key: 'revenue', name: 'Revenue', color: '#10b981' },
        ];

        return (
          <div className="flex h-full flex-col p-6 overflow-auto">
            {chartProps.chartType === 'bar' && (
              <BarChartComponent
                data={barLineChartData}
                dataKeys={dataKeys}
                title="Monthly Sales & Revenue"
                horizontal={chartProps.horizontal}
                showLegend={chartProps.showLegend}
                height={350}
              />
            )}
            {chartProps.chartType === 'line' && (
              <LineChartComponent
                data={barLineChartData}
                dataKeys={dataKeys}
                title="Monthly Trend"
                height={350}
              />
            )}
            {chartProps.chartType === 'area' && (
              <AreaChartComponent
                data={barLineChartData}
                dataKeys={dataKeys}
                title="Monthly Area Chart"
                height={350}
              />
            )}
            {chartProps.chartType === 'pie' && (
              <PieChartComponent
                data={pieDonutChartData}
                title="Department Distribution"
                height={350}
              />
            )}
            {chartProps.chartType === 'donut' && (
              <DonutChartComponent
                data={pieDonutChartData}
                title="Department Breakdown"
                height={350}
              />
            )}
            {chartProps.chartType === 'radial' && (
              <RadialChartComponent
                data={radialChartData}
                title="Radial Progress"
                height={350}
              />
            )}
          </div>
        );
      case 'orderdetailsdrawer':
        // Demo pronumber; ProDetailSlideOver fetches/refreshes the order itself.
        const demoPronumber = 123456;
        return (
          <div className="p-6">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-heading mb-2">
                Order Details Drawer Demo
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                This expandable drawer displays comprehensive order information with grouped leg display,
                appointment times, arrival/departure tracking, and driver assignment capabilities.
              </p>
            </div>

            <button
              onClick={() => setIsOrderDetailsDrawerOpen(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Open Order Details Drawer
            </button>

            <ProDetailSlideOver
              pronumber={demoPronumber}
              isOpen={isOrderDetailsDrawerOpen}
              onClose={() => setIsOrderDetailsDrawerOpen(false)}
            />
          </div>
        );
      case 'timezone-lookup':
        const handleTimezoneLookup = async () => {
          setTimezoneLoading(true);
          setTimezoneError(null);
          try {
            const data = await timezoneService.getTimezone(
              parseFloat(timezoneLat),
              parseFloat(timezoneLng)
            );
            setTimezoneResult(data);
          } catch (error: any) {
            setTimezoneError(error?.response?.data?.detail || error?.message || 'Lookup failed');
            setTimezoneResult(null);
          } finally {
            setTimezoneLoading(false);
          }
        };

        return (
          <div className="flex h-full flex-col p-6 gap-6">
            <div className="bg-panel rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4 text-heading">Timezone Lookup</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Enter coordinates to look up timezone information. Uses the GET /api/v1/timezone endpoint.
              </p>

              <div className="flex gap-4 items-end mb-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-label mb-1">
                    Latitude
                  </label>
                  <input
                    type="text"
                    value={timezoneLat}
                    onChange={(e) => setTimezoneLat(e.target.value)}
                    placeholder="29.76"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-label mb-1">
                    Longitude
                  </label>
                  <input
                    type="text"
                    value={timezoneLng}
                    onChange={(e) => setTimezoneLng(e.target.value)}
                    placeholder="-95.36"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <button
                  onClick={handleTimezoneLookup}
                  disabled={timezoneLoading}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {timezoneLoading ? 'Looking up...' : 'Lookup'}
                </button>
              </div>

              <div className="text-xs text-muted mb-4">
                <strong>Sample coordinates:</strong> Houston (29.76, -95.36) | NYC (40.71, -74.01) | LA (34.05, -118.24) | Denver (39.74, -104.99)
              </div>

              {timezoneError && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md mb-4">
                  <p className="text-sm text-red-600 dark:text-red-400">{timezoneError}</p>
                </div>
              )}

              {timezoneResult && (
                <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                  <h4 className="font-medium text-green-800 dark:text-green-300 mb-2">Result:</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="text-gray-600 dark:text-gray-400">Timezone:</div>
                    <div className="text-heading">{timezoneResult.tz_identifier}</div>
                    <div className="text-gray-600 dark:text-gray-400">UTC Offset:</div>
                    <div className="text-heading">{timezoneResult.utc_offset || 'N/A'}</div>
                    <div className="text-gray-600 dark:text-gray-400">Abbreviation:</div>
                    <div className="text-heading">{timezoneResult.abbreviation || 'N/A'}</div>
                    <div className="text-gray-600 dark:text-gray-400">DST Active:</div>
                    <div className="text-heading">{timezoneResult.is_dst ? 'Yes' : 'No'}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      case 'movecard':
        return (
          <div className="flex items-start justify-center p-6 gap-4 flex-wrap">
            <div style={{ width: 220 }}>
              <MoveCard
                move={mockMoveInTransit}
                priority={1}
                isCurrentMove={true}
                projectedStatus="at_risk"
                projectedETA="4:30 PM"
                isETAToDestination={true}
              />
            </div>
            <div style={{ width: 220 }}>
              <MoveCard
                move={mockMoveNotStarted}
                priority={2}
                visualPriority={1}
                isCurrentMove={false}
                projectedStatus="on_track"
                projectedETA="6:15 PM"
                isETAToDestination={false}
              />
            </div>
            <div style={{ width: 220 }}>
              <MoveCard
                move={mockMoveCompleted}
                priority={3}
                isCurrentMove={false}
              />
            </div>
          </div>
        );
      case 'compacthoswidget':
        return (
          <div className="flex flex-col items-center justify-center p-6 gap-8">
            <div className="text-sm text-muted mb-2">Driving status with all HOS times</div>
            <div className="bg-panel p-4 rounded-lg shadow">
              <CompactHosWidget
                currentDutyStatus="driving"
                breakTimeRemainingMins={451}
                driveTimeRemainingMins={631}
                shiftTimeRemainingMins={668}
                cycleTimeRemainingMins={3968}
                samsaraDriverId="12345"
              />
            </div>
            <div className="text-sm text-muted mb-2">Off Duty status</div>
            <div className="bg-panel p-4 rounded-lg shadow">
              <CompactHosWidget
                currentDutyStatus="offduty"
                breakTimeRemainingMins={480}
                driveTimeRemainingMins={660}
                shiftTimeRemainingMins={840}
                cycleTimeRemainingMins={4200}
                samsaraDriverId={null}
              />
            </div>
            <div className="text-sm text-muted mb-2">Sleeper Berth status</div>
            <div className="bg-panel p-4 rounded-lg shadow">
              <CompactHosWidget
                currentDutyStatus="sleeperbed"
                breakTimeRemainingMins={480}
                driveTimeRemainingMins={660}
                shiftTimeRemainingMins={840}
                cycleTimeRemainingMins={4200}
                samsaraDriverId={null}
              />
            </div>
            <div className="text-sm text-muted mb-2">Low HOS (critical)</div>
            <div className="bg-panel p-4 rounded-lg shadow">
              <CompactHosWidget
                currentDutyStatus="driving"
                breakTimeRemainingMins={30}
                driveTimeRemainingMins={45}
                shiftTimeRemainingMins={60}
                cycleTimeRemainingMins={120}
                samsaraDriverId="12345"
              />
            </div>
          </div>
        );
      case 'driverlane':
        return (
          <div className="p-4 overflow-auto">
            <DriverLane
              driver={mockDriver1}
              showCompleted={driverLaneProps.showCompleted}
              displaySettings={dsmDisplaySettings}
              orientation={driverLaneProps.orientation}
              hideHeader={driverLaneProps.hideHeader}
            />
          </div>
        );
      case 'risksection':
        return (
          <div className="p-4 overflow-auto">
            <RiskSection
              title="At Risk"
              icon={<ExclamationTriangleIcon className="w-5 h-5" />}
              headerClass="flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 font-semibold text-sm rounded-t-lg border border-amber-200 dark:border-amber-800"
              drivers={[mockDriver1, mockDriver2]}
              showCompleted={true}
              displaySettings={dsmDisplaySettings}
              isSectionExpanded={riskSectionExpanded}
              onToggleSection={() => setRiskSectionExpanded(!riskSectionExpanded)}
            />
          </div>
        );
      case 'settingsdrawer-dsm':
        return (
          <div className="flex items-center justify-center h-full">
            <button
              onClick={() => setDsmSettingsOpen(true)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Open Settings Drawer
            </button>
            <DSMSettingsDrawer
              isOpen={dsmSettingsOpen}
              onClose={() => setDsmSettingsOpen(false)}
              displaySettings={dsmDisplaySettings}
              onSettingsChange={setDsmDisplaySettings}
            />
          </div>
        );
      case 'removeconfirmmodal':
        return (
          <div className="flex items-center justify-center h-full">
            <button
              onClick={() => setShowRemoveModal(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Show Remove Modal
            </button>
            {showRemoveModal && (
              <RemoveConfirmModal
                move={mockMoveInTransit}
                group={null}
                loading={false}
                onClose={() => setShowRemoveModal(false)}
                onConfirm={() => setShowRemoveModal(false)}
              />
            )}
          </div>
        );
      case 'trimble-comparison':
        // Load CMS locations when switching to lane mode
        const loadCmsLocations = async () => {
          if (cmsLocations.length > 0) return; // Already loaded
          setCmsLocationsLoading(true);
          try {
            const locations = await trimbleService.getCmsLocations();
            setCmsLocations(locations);
          } catch (error) {
            console.error('Failed to load CMS locations:', error);
          } finally {
            setCmsLocationsLoading(false);
          }
        };

        const handleTrimbleCompare = async () => {
          setTrimbleLoading(true);
          setTrimbleError(null);
          setGoogleFrontendMetrics(null);
          try {
            let request: CompareRouteRequest;
            let originCoords: { lat: number; lng: number } | null = null;
            let destCoords: { lat: number; lng: number } | null = null;

            if (trimbleInputMode === 'address') {
              request = {
                origin: { address: trimbleOriginAddress },
                destination: { address: trimbleDestAddress },
              };
            } else if (trimbleInputMode === 'lane') {
              if (!selectedOriginLocation || !selectedDestLocation) {
                setTrimbleError('Please select both origin and destination locations');
                setTrimbleLoading(false);
                return;
              }
              originCoords = { lat: selectedOriginLocation.lat, lng: selectedOriginLocation.lng };
              destCoords = { lat: selectedDestLocation.lat, lng: selectedDestLocation.lng };
              request = {
                origin: { lat: selectedOriginLocation.lat, lng: selectedOriginLocation.lng },
                destination: { lat: selectedDestLocation.lat, lng: selectedDestLocation.lng },
              };
            } else {
              originCoords = { lat: parseFloat(trimbleOriginLat), lng: parseFloat(trimbleOriginLng) };
              destCoords = { lat: parseFloat(trimbleDestLat), lng: parseFloat(trimbleDestLng) };
              request = {
                origin: originCoords,
                destination: destCoords,
              };
            }

            // Add avoid_tolls to request
            request.avoid_tolls = avoidTolls;

            // Get Trimble data from backend
            const data = await trimbleService.compareRoute(request);
            setTrimbleResult(data);

            // Get Google metrics from frontend DirectionsService (avoids API key restrictions)
            if (googleMapsLoaded) {
              const directionsService = new google.maps.DirectionsService();
              let origin: string | google.maps.LatLngLiteral;
              let destination: string | google.maps.LatLngLiteral;

              if (trimbleInputMode === 'address') {
                origin = trimbleOriginAddress;
                destination = trimbleDestAddress;
              } else if (trimbleInputMode === 'lane' && originCoords && destCoords) {
                origin = originCoords;
                destination = destCoords;
              } else {
                origin = { lat: parseFloat(trimbleOriginLat), lng: parseFloat(trimbleOriginLng) };
                destination = { lat: parseFloat(trimbleDestLat), lng: parseFloat(trimbleDestLng) };
              }

              directionsService.route({
                origin,
                destination,
                travelMode: google.maps.TravelMode.DRIVING,
              }, (result, status) => {
                if (status === google.maps.DirectionsStatus.OK && result?.routes?.[0]?.legs?.[0]) {
                  const leg = result.routes[0].legs[0];
                  setGoogleFrontendMetrics({
                    distance_miles: (leg.distance?.value || 0) / 1609.34,
                    duration_minutes: (leg.duration?.value || 0) / 60,
                  });
                }
              });
            }
          } catch (error: any) {
            setTrimbleError(error?.response?.data?.detail || error?.message || 'Comparison failed');
            setTrimbleResult(null);
          } finally {
            setTrimbleLoading(false);
          }
        };

        const getDiffClass = (google: number | null, trimble: number | null) => {
          if (google === null || trimble === null) return '';
          const diff = Math.abs(google - trimble) / Math.max(google, trimble) * 100;
          if (diff <= 2) return 'text-green-600 dark:text-green-400';
          if (diff <= 5) return 'text-yellow-600 dark:text-yellow-400';
          return 'text-red-600 dark:text-red-400';
        };

        const formatTime = (minutes: number | null) => {
          if (minutes === null) return 'N/A';
          const hrs = Math.floor(minutes / 60);
          const mins = Math.round(minutes % 60);
          return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
        };

        // Haversine formula - calculates straight-line distance between two points
        const haversineDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
          const R = 3958.8; // Earth's radius in miles
          const dLat = (lat2 - lat1) * Math.PI / 180;
          const dLon = (lon2 - lon1) * Math.PI / 180;
          const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                    Math.sin(dLon / 2) * Math.sin(dLon / 2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
          return R * c;
        };

        // Calculate Haversine metrics if we have resolved coordinates
        const getHaversineMetrics = () => {
          if (!trimbleResult?.origin_resolved || !trimbleResult?.destination_resolved) return null;
          const distance = haversineDistance(
            trimbleResult.origin_resolved.lat,
            trimbleResult.origin_resolved.lng,
            trimbleResult.destination_resolved.lat,
            trimbleResult.destination_resolved.lng
          );
          const duration = (distance / 45) * 60; // 45 mph avg, convert to minutes
          return { distance, duration };
        };

        const haversineMetrics = trimbleResult ? getHaversineMetrics() : null;

        return (
          <div className="flex h-full flex-col p-6 gap-6">
            <div className="bg-panel rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4 text-heading">Trimble Maps Comparison</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Compare routing data between Google Maps and Trimble Maps (PC*Miler).
              </p>

              {/* Input mode tabs */}
              <div className="flex gap-2 mb-4">
                {(['address', 'coords', 'lane'] as const).map((mode) => (
                  <button
                    key={mode}
                    onClick={() => {
                      setTrimbleInputMode(mode);
                      if (mode === 'lane') loadCmsLocations();
                    }}
                    className={`px-3 py-1.5 text-sm rounded-md ${
                      trimbleInputMode === mode
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-label'
                    }`}
                  >
                    {mode === 'address' ? 'Manual Address' : mode === 'coords' ? 'Coordinates' : 'Lane'}
                  </button>
                ))}
                <div className="ml-auto flex items-center gap-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Avoid Tolls</span>
                  <Toggle
                    checked={avoidTolls}
                    onChange={setAvoidTolls}
                    size="sm"
                  />
                </div>
              </div>

              {/* Input fields */}
              {trimbleInputMode === 'address' && (
                <div className="flex gap-4 items-end mb-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-label mb-1">Origin</label>
                    <input
                      type="text"
                      value={trimbleOriginAddress}
                      onChange={(e) => setTrimbleOriginAddress(e.target.value)}
                      placeholder="Houston, TX"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-label mb-1">Destination</label>
                    <input
                      type="text"
                      value={trimbleDestAddress}
                      onChange={(e) => setTrimbleDestAddress(e.target.value)}
                      placeholder="Dallas, TX"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <button
                    onClick={handleTrimbleCompare}
                    disabled={trimbleLoading}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {trimbleLoading ? 'Comparing...' : 'Compare Routes'}
                  </button>
                </div>
              )}

              {trimbleInputMode === 'coords' && (
                <div className="grid grid-cols-5 gap-4 items-end mb-4">
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Origin Lat</label>
                    <input type="text" value={trimbleOriginLat} onChange={(e) => setTrimbleOriginLat(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Origin Lng</label>
                    <input type="text" value={trimbleOriginLng} onChange={(e) => setTrimbleOriginLng(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Dest Lat</label>
                    <input type="text" value={trimbleDestLat} onChange={(e) => setTrimbleDestLat(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-label mb-1">Dest Lng</label>
                    <input type="text" value={trimbleDestLng} onChange={(e) => setTrimbleDestLng(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" />
                  </div>
                  <button
                    onClick={handleTrimbleCompare}
                    disabled={trimbleLoading}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {trimbleLoading ? 'Comparing...' : 'Compare'}
                  </button>
                </div>
              )}

              {trimbleInputMode === 'lane' && (
                <div className="flex gap-4 items-end mb-4">
                  <div className="flex-1">
                    <TypeAhead
                      label="Origin Location"
                      options={cmsLocations.map(loc => ({
                        value: loc.recno.toString(),
                        label: loc.lname,
                        description: loc.lcity && loc.lstate ? `${loc.lcity}, ${loc.lstate}` : undefined,
                      }))}
                      value={selectedOriginLocation?.recno?.toString() || ''}
                      onChange={(value) => {
                        const loc = cmsLocations.find(l => l.recno === parseInt(value));
                        setSelectedOriginLocation(loc || null);
                      }}
                      placeholder={cmsLocationsLoading ? 'Loading...' : 'Search origin location...'}
                      disabled={cmsLocationsLoading}
                      showDescriptions
                    />
                  </div>
                  <div className="flex-1">
                    <TypeAhead
                      label="Destination Location"
                      options={cmsLocations.map(loc => ({
                        value: loc.recno.toString(),
                        label: loc.lname,
                        description: loc.lcity && loc.lstate ? `${loc.lcity}, ${loc.lstate}` : undefined,
                      }))}
                      value={selectedDestLocation?.recno?.toString() || ''}
                      onChange={(value) => {
                        const loc = cmsLocations.find(l => l.recno === parseInt(value));
                        setSelectedDestLocation(loc || null);
                      }}
                      placeholder={cmsLocationsLoading ? 'Loading...' : 'Search destination location...'}
                      disabled={cmsLocationsLoading}
                      showDescriptions
                    />
                  </div>
                  <button
                    onClick={handleTrimbleCompare}
                    disabled={trimbleLoading || !selectedOriginLocation || !selectedDestLocation}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {trimbleLoading ? 'Comparing...' : 'Compare Routes'}
                  </button>
                </div>
              )}

              {trimbleError && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md mb-4">
                  <p className="text-sm text-red-600 dark:text-red-400">{trimbleError}</p>
                </div>
              )}

              {trimbleResult && (
                <div className="grid grid-cols-2 gap-6">
                  {/* Comparison Table */}
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                    <h4 className="font-medium text-heading mb-3">Comparison Results</h4>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                          <th className="text-left py-2 text-gray-600 dark:text-gray-400">Metric</th>
                          <th className="text-right py-2 text-gray-600 dark:text-gray-400">Google</th>
                          <th className="text-right py-2 text-gray-600 dark:text-gray-400">Trimble</th>
                          <th className="text-right py-2 text-gray-600 dark:text-gray-400">Haversine</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-gray-100 dark:border-gray-800">
                          <td className="py-2 text-label">Distance</td>
                          <td className={`text-right py-2 ${getDiffClass(googleFrontendMetrics?.distance_miles ?? null, trimbleResult.trimble.distance_miles)}`}>
                            {googleFrontendMetrics?.distance_miles?.toFixed(1) || 'N/A'} mi
                          </td>
                          <td className={`text-right py-2 ${getDiffClass(googleFrontendMetrics?.distance_miles ?? null, trimbleResult.trimble.distance_miles)}`}>
                            {trimbleResult.trimble.distance_miles?.toFixed(1) || 'N/A'} mi
                          </td>
                          <td className="text-right py-2 text-muted">
                            {haversineMetrics?.distance?.toFixed(1) || 'N/A'} mi
                          </td>
                        </tr>
                        <tr className="border-b border-gray-100 dark:border-gray-800">
                          <td className="py-2 text-label">Duration</td>
                          <td className={`text-right py-2 ${getDiffClass(googleFrontendMetrics?.duration_minutes ?? null, trimbleResult.trimble.duration_minutes)}`}>
                            {formatTime(googleFrontendMetrics?.duration_minutes ?? null)}
                          </td>
                          <td className={`text-right py-2 ${getDiffClass(googleFrontendMetrics?.duration_minutes ?? null, trimbleResult.trimble.duration_minutes)}`}>
                            {formatTime(trimbleResult.trimble.duration_minutes)}
                          </td>
                          <td className="text-right py-2 text-muted">
                            {formatTime(haversineMetrics?.duration ?? null)}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-100 dark:border-gray-800">
                          <td className="py-2 text-label">Duration + Buffers</td>
                          <td className={`text-right py-2 ${getDiffClass(googleFrontendMetrics?.duration_minutes ? (googleFrontendMetrics.duration_minutes * 1.15) + 15 : null, trimbleResult.trimble.duration_minutes ? (trimbleResult.trimble.duration_minutes * 1.15) + 15 : null)}`}>
                            {formatTime(googleFrontendMetrics?.duration_minutes ? (googleFrontendMetrics.duration_minutes * 1.15) + 15 : null)}
                          </td>
                          <td className={`text-right py-2 ${getDiffClass(googleFrontendMetrics?.duration_minutes ? (googleFrontendMetrics.duration_minutes * 1.15) + 15 : null, trimbleResult.trimble.duration_minutes ? (trimbleResult.trimble.duration_minutes * 1.15) + 15 : null)}`}>
                            {formatTime(trimbleResult.trimble.duration_minutes ? (trimbleResult.trimble.duration_minutes * 1.15) + 15 : null)}
                          </td>
                          <td className="text-right py-2 text-muted">
                            {formatTime(haversineMetrics?.duration ? (haversineMetrics.duration * 1.15) + 15 : null)}
                          </td>
                        </tr>
                        <tr>
                          <td className="py-2 text-label">Tolls</td>
                          <td className="text-right py-2 text-gray-400">N/A</td>
                          <td className="text-right py-2 text-heading">
                            {trimbleResult.trimble.toll_cost != null ? `$${trimbleResult.trimble.toll_cost.toFixed(2)}` : 'N/A'}
                          </td>
                          <td className="text-right py-2 text-gray-400">N/A</td>
                        </tr>
                      </tbody>
                    </table>

                    {/* State Mileage */}
                    {trimbleResult.trimble.state_mileage?.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <h5 className="font-medium text-label mb-2">State Mileage (Trimble)</h5>
                        <div className="grid grid-cols-2 gap-1 text-sm">
                          {trimbleResult.trimble.state_mileage.map((s: any) => (
                            <div key={s.state} className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">{s.state}:</span>
                              <span className="text-heading">{s.miles?.toFixed(1)} mi</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Diff legend */}
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 text-xs">
                      <span className="text-green-600 dark:text-green-400 mr-3">● &lt;2% diff</span>
                      <span className="text-yellow-600 dark:text-yellow-400 mr-3">● 2-5% diff</span>
                      <span className="text-red-600 dark:text-red-400">● &gt;5% diff</span>
                    </div>
                  </div>

                  {/* Route Map */}
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-medium text-heading">Route Map</h4>
                      <div className="flex gap-1">
                        <button
                          onClick={() => {
                            setTrimbleRouteView('google');
                            // Draw Google route
                            if (trimbleMapRef.current && trimbleResult) {
                              if (trimbleRouteRendererRef.current) {
                                trimbleRouteRendererRef.current.setMap(null);
                              }
                              // Use resolved coordinates from API response (works for all modes)
                              if (trimbleResult?.origin_resolved && trimbleResult?.destination_resolved) {
                                const directionsService = new google.maps.DirectionsService();
                                const origin = { lat: trimbleResult.origin_resolved.lat, lng: trimbleResult.origin_resolved.lng };
                                const destination = { lat: trimbleResult.destination_resolved.lat, lng: trimbleResult.destination_resolved.lng };
                                directionsService.route({
                                  origin,
                                  destination,
                                  travelMode: google.maps.TravelMode.DRIVING,
                                }, (result, status) => {
                                  if (status === google.maps.DirectionsStatus.OK && result) {
                                    trimbleRouteRendererRef.current = new google.maps.DirectionsRenderer({
                                      map: trimbleMapRef.current,
                                      directions: result,
                                      suppressMarkers: false,
                                      polylineOptions: { strokeColor: '#4285F4', strokeWeight: 5 },
                                    });
                                  }
                                });
                              }
                            }
                          }}
                          className={`px-2 py-1 text-xs rounded ${trimbleRouteView === 'google' ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-label'}`}
                        >
                          Google
                        </button>
                        <button
                          onClick={() => {
                            setTrimbleRouteView('trimble');
                            // Clear existing routes
                            if (trimbleRouteRendererRef.current) {
                              trimbleRouteRendererRef.current.setMap(null);
                              trimbleRouteRendererRef.current = null;
                            }
                            // Draw Trimble route using Google Directions (red)
                            if (trimbleMapRef.current && trimbleResult?.origin_resolved && trimbleResult?.destination_resolved) {
                              const directionsService = new google.maps.DirectionsService();
                              const origin = { lat: trimbleResult.origin_resolved.lat, lng: trimbleResult.origin_resolved.lng };
                              const destination = { lat: trimbleResult.destination_resolved.lat, lng: trimbleResult.destination_resolved.lng };
                              directionsService.route({
                                origin,
                                destination,
                                travelMode: google.maps.TravelMode.DRIVING,
                              }, (result, status) => {
                                if (status === google.maps.DirectionsStatus.OK && result) {
                                  trimbleRouteRendererRef.current = new google.maps.DirectionsRenderer({
                                    map: trimbleMapRef.current,
                                    directions: result,
                                    suppressMarkers: false,
                                    polylineOptions: { strokeColor: '#EA4335', strokeWeight: 5 },
                                  });
                                }
                              });
                            }
                          }}
                          className={`px-2 py-1 text-xs rounded ${trimbleRouteView === 'trimble' ? 'bg-red-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-label'}`}
                        >
                          Trimble
                        </button>
                        <button
                          onClick={() => {
                            setTrimbleRouteView('combined');
                            // Clear existing routes
                            if (trimbleRouteRendererRef.current) {
                              trimbleRouteRendererRef.current.setMap(null);
                              trimbleRouteRendererRef.current = null;
                            }
                            // Draw both routes
                            if (trimbleMapRef.current && trimbleResult?.origin_resolved && trimbleResult?.destination_resolved) {
                              const directionsService = new google.maps.DirectionsService();
                              const origin = { lat: trimbleResult.origin_resolved.lat, lng: trimbleResult.origin_resolved.lng };
                              const destination = { lat: trimbleResult.destination_resolved.lat, lng: trimbleResult.destination_resolved.lng };
                              // Draw Google route (blue)
                              directionsService.route({
                                origin,
                                destination,
                                travelMode: google.maps.TravelMode.DRIVING,
                              }, (result, status) => {
                                if (status === google.maps.DirectionsStatus.OK && result) {
                                  new google.maps.DirectionsRenderer({
                                    map: trimbleMapRef.current,
                                    directions: result,
                                    suppressMarkers: false,
                                    polylineOptions: { strokeColor: '#4285F4', strokeWeight: 5, zIndex: 1 },
                                  });
                                  // Draw Trimble route (red) - slightly offset or on top
                                  new google.maps.DirectionsRenderer({
                                    map: trimbleMapRef.current,
                                    directions: result,
                                    suppressMarkers: true,
                                    polylineOptions: { strokeColor: '#EA4335', strokeWeight: 3, zIndex: 2 },
                                  });
                                }
                              });
                            }
                          }}
                          className={`px-2 py-1 text-xs rounded ${trimbleRouteView === 'combined' ? 'bg-purple-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-label'}`}
                        >
                          Combined
                        </button>
                      </div>
                    </div>
                    <div className="h-64 rounded overflow-hidden">
                      {googleMapsLoaded ? (
                        <GoogleMap
                          mapContainerStyle={{ width: '100%', height: '100%' }}
                          center={{ lat: 30.5, lng: -96.0 }}
                          zoom={6}
                          onLoad={(map) => {
                            trimbleMapRef.current = map;
                            setTrimbleMapReady(true);
                            // Auto-draw Google route on load using resolved coordinates
                            if (trimbleResult?.origin_resolved && trimbleResult?.destination_resolved) {
                              const directionsService = new google.maps.DirectionsService();
                              const origin = { lat: trimbleResult.origin_resolved.lat, lng: trimbleResult.origin_resolved.lng };
                              const destination = { lat: trimbleResult.destination_resolved.lat, lng: trimbleResult.destination_resolved.lng };
                              directionsService.route({
                                origin,
                                destination,
                                travelMode: google.maps.TravelMode.DRIVING,
                              }, (result, status) => {
                                if (status === google.maps.DirectionsStatus.OK && result) {
                                  trimbleRouteRendererRef.current = new google.maps.DirectionsRenderer({
                                    map: map,
                                    directions: result,
                                    suppressMarkers: false,
                                    polylineOptions: { strokeColor: '#4285F4', strokeWeight: 5 },
                                  });
                                }
                              });
                            }
                          }}
                          options={{
                            disableDefaultUI: true,
                            zoomControl: true,
                          }}
                        />
                      ) : (
                        <div className="h-full bg-gray-200 dark:bg-gray-800 flex items-center justify-center">
                          <span className="text-sm text-gray-500">Loading map...</span>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-muted mt-2">
                      {trimbleRouteView === 'google' && 'Blue: Google route'}
                      {trimbleRouteView === 'trimble' && 'Red: Trimble route'}
                      {trimbleRouteView === 'combined' && 'Blue: Google | Red: Trimble'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      default:
        return (
          <div className="flex h-full items-center justify-center">
            <p className="text-muted">Example coming soon for {selectedComponent.name}</p>
          </div>
        );
    }
  };

  // Get component details
  const getComponentDetails = () => {
    if (!selectedComponent) return null;

    switch (selectedComponent.id) {
      case 'tantable':
        return {
          props: [
            { name: 'data', type: 'T[]', required: true, description: 'Array of data objects to display in the table', interactive: false },
            { name: 'columns', type: 'ColumnDef<T>[]', required: true, description: 'Column definitions from TanStack Table', interactive: false },
            { name: 'loading', type: 'boolean', required: false, description: 'Show loading state', interactive: true, key: 'loading' },
            { name: 'settingsKey', type: 'string', required: false, description: 'Key for persisting table settings (localStorage)', interactive: false },
            { name: 'searchPlaceholder', type: 'string', required: false, description: 'Placeholder text for search input', interactive: true, key: 'searchPlaceholder', inputType: 'text' },
            { name: 'className', type: 'string', required: false, description: 'Additional CSS classes for the table container', interactive: false },
            { name: 'onRowClick', type: '(row: T) => void', required: false, description: 'Callback when a row is clicked', interactive: false },
            { name: 'getRowClassName', type: '(row: T) => string', required: false, description: 'Function to add custom classes to rows', interactive: false },
            { name: 'getGroupedRowClassName', type: '(groupValue: any, columnId: string, subRows: T[]) => string', required: false, description: 'Function to add custom classes to grouped rows. Applies custom styling to group headers based on group value and column. When set, disables hover effects on grouped rows.', interactive: false },
            { name: 'enableGrouping', type: 'boolean', required: false, description: 'Enable column grouping functionality', interactive: true, key: 'enableGrouping' },
            { name: 'groupableColumns', type: 'string[]', required: false, description: 'Array of column IDs that can be grouped', interactive: false },
            { name: 'enableColumnFilters', type: 'boolean', required: false, description: 'Enable column filtering with distinct values', interactive: true, key: 'enableColumnFilters' },
            { name: 'cardFilterConditions', type: 'FilterCondition[]', required: false, description: 'Filter conditions to apply to the data', interactive: false },
            { name: 'cardTitle', type: 'string', required: false, description: 'Title displayed in card header', interactive: true, key: 'cardTitle', inputType: 'text' },
            { name: 'onEditFilters', type: '() => void', required: false, description: 'Callback when edit filters button is clicked', interactive: false },
            { name: 'showEditFiltersButton', type: 'boolean', required: false, description: 'Show edit filters button in header', interactive: true, key: 'showEditFiltersButton' },
            { name: 'editFiltersButtonIcon', type: "'pencil' | 'plus' | 'cog'", required: false, description: 'Icon for edit filters button (default: pencil)', interactive: true, key: 'editFiltersButtonIcon', inputType: 'radio', options: ['pencil', 'plus', 'cog'] },
            { name: 'editFiltersButtonTooltip', type: 'string', required: false, description: 'Tooltip for edit filters button', interactive: true, key: 'editFiltersButtonTooltip', inputType: 'text' },
            { name: 'onDuplicate', type: '() => void', required: false, description: 'Callback when duplicate button is clicked', interactive: false },
            { name: 'showDuplicate', type: 'boolean', required: false, description: 'Show duplicate button in header', interactive: true, key: 'showDuplicate' },
            { name: 'duplicateTooltip', type: 'string', required: false, description: 'Tooltip for duplicate button (default: Duplicate)', interactive: true, key: 'duplicateTooltip', inputType: 'text' },
            { name: 'cardHeight', type: "'sm' | 'md' | 'lg'", required: false, description: 'Height preset for the table card', interactive: true, key: 'cardHeight' },
            { name: 'onCardHeightChange', type: '(height) => void', required: false, description: 'Callback when card height is changed', interactive: false },
            { name: 'paginationStyle', type: "'fullWidth' | 'compactWithButtons'", required: false, description: 'Style of pagination controls', interactive: false },
            { name: 'footerLeftContent', type: 'ReactNode', required: false, description: 'Content to display on the left side of footer', interactive: false },
            { name: 'collapsed', type: 'boolean', required: false, description: 'Whether the table is collapsed', interactive: false },
            { name: 'onToggleCollapse', type: '() => void', required: false, description: 'Callback when collapse button is clicked', interactive: false },
            { name: 'showTabs', type: 'boolean', required: false, description: 'Show vertical tabs for filtering', interactive: true, key: 'showTabs' },
            { name: 'tabData', type: 'TabData<T>[]', required: false, description: 'Tab configuration for filtering data', interactive: false },
            { name: 'activeTab', type: 'string', required: false, description: 'Currently active tab ID (single-tab mode)', interactive: false },
            { name: 'onTabChange', type: '(tabId: string) => void', required: false, description: 'Callback when tab is changed (single-tab mode)', interactive: false },
            { name: 'multiTabSelect', type: 'boolean', required: false, description: 'Enable multi-tab selection. Users can select multiple tabs at once; data is filtered as a union of all selected tabs. At least one tab must remain selected.', interactive: false },
            { name: 'activeTabs', type: 'string[]', required: false, description: 'Array of active tab IDs (multi-tab mode)', interactive: false },
            { name: 'onActiveTabsChange', type: '(tabIds: string[]) => void', required: false, description: 'Callback when selected tabs change (multi-tab mode)', interactive: false },
            { name: 'showDeletedToggle', type: 'boolean', required: false, description: 'Show deleted items toggle', interactive: true, key: 'showDeletedToggle' },
            { name: 'showDeleted', type: 'boolean', required: false, description: 'Whether to show deleted items', interactive: false },
            { name: 'onShowDeletedChange', type: '(show: boolean) => void', required: false, description: 'Callback when show deleted is toggled', interactive: false },
            { name: 'lockedColumns (left)', type: 'LockedColumn[]', required: false, description: 'Lock columns to the left side', interactive: true, key: 'lockedColumnsLeft' },
            { name: 'lockedColumns (right)', type: 'LockedColumn[]', required: false, description: 'Lock columns to the right side', interactive: true, key: 'lockedColumnsRight' },
            { name: 'enableExcelExport', type: 'boolean', required: false, description: 'Enable Excel export button (default: true)', interactive: true, key: 'enableExcelExport' },
            { name: 'extraExportColumns', type: '{ key: string; header: string }[]', required: false, description: 'Extra columns included in Excel export only (not displayed in the table). Values are pulled from row.original[key].', interactive: false },
            { name: 'defaultColumnVisibility', type: 'VisibilityState', required: false, description: 'Columns hidden by default. Keys are column IDs, values are booleans (false = hidden). Applied when no saved settings exist, and as fallback for new columns not yet in saved settings.', interactive: false },
            { name: 'enableSearch', type: 'boolean', required: false, description: 'Enable search bar toggle button (default: true)', interactive: true, key: 'enableSearch' },
            { name: 'enableReset', type: 'boolean', required: false, description: 'Enable reset/clear all button (default: true)', interactive: true, key: 'enableReset' },
            { name: 'enableShowHideCols', type: 'boolean', required: false, description: 'Enable show/hide columns button (default: true)', interactive: true, key: 'enableShowHideCols' },
            { name: 'enableRowsPerPage', type: 'boolean', required: false, description: 'Enable rows per page selector (default: true)', interactive: true, key: 'enableRowsPerPage' },
            { name: 'rowsPerPageOptions', type: 'number[]', required: false, description: 'Array of page size options (default: [10, 25, 50, 100], max: 500)', interactive: false },
            { name: 'enableZebraStriping', type: 'boolean', required: false, description: 'Enable alternating row background colors for better readability (default: false)', interactive: true, key: 'enableZebraStriping' },
          ],
          features: [
            'Sorting on any column',
            'Global search across all columns',
            'Column visibility toggle',
            'Column reordering via drag and drop',
            'Column resizing',
            'Column grouping',
            'Custom grouped row styling with getGroupedRowClassName',
            'Conditional styling for grouped rows (e.g., color-coded by urgency)',
            'Automatic hover effect disable on custom-styled grouped rows',
            'Column filtering with distinct values',
            'Pagination with configurable page sizes',
            'Row selection',
            'Persistent settings via settingsKey',
            'Responsive design',
            'Custom cell rendering',
            'Locked/pinned columns',
            'Excel export with visible columns and filtered data',
            'Configurable table controls (search, reset, show/hide columns, rows per page)',
            'Customizable rows per page options',
          ],
        };
      case 'drawer':
        return {
          props: [
            { name: 'isOpen', type: 'boolean', required: true, description: 'Whether the drawer is open', interactive: false },
            { name: 'onClose', type: '() => void', required: true, description: 'Callback when the drawer should close', interactive: false },
            { name: 'title', type: 'string | ReactNode', required: true, description: 'Title displayed in the drawer header', interactive: false },
            { name: 'children', type: 'ReactNode', required: true, description: 'Content to display in the drawer', interactive: false },
            { name: 'position', type: "'left' | 'right'", required: false, description: 'Position of the drawer (default: right)', interactive: true, key: 'position' },
            { name: 'maxWidth', type: "'sm' | 'md' | 'lg' | 'xl' | 'full'", required: false, description: 'Maximum width of the drawer', interactive: true, key: 'maxWidth' },
            { name: 'noBackdrop', type: 'boolean', required: false, description: 'Hide backdrop overlay and blur', interactive: true, key: 'noBackdrop' },
            { name: 'footer', type: 'ReactNode', required: false, description: 'Footer content (buttons, actions, etc.)', interactive: true, key: 'withFooter' },
            { name: 'width', type: 'string', required: false, description: 'Fixed width in pixels (overrides maxWidth)', interactive: false },
            { name: 'className', type: 'string', required: false, description: 'Custom className for the drawer panel', interactive: false },
          ],
          features: [
            'Smooth slide-in animation',
            'Backdrop with blur effect',
            'Close on ESC key or backdrop click',
            'Left or right positioning',
            'Responsive width options',
            'Optional footer for actions',
            'Portal-based rendering',
            'Dark mode support',
          ],
        };
      case 'drawer-push':
        return {
          props: [
            { name: 'open', type: 'boolean', required: true, description: 'Whether the drawer is open', interactive: false },
            { name: 'onOpenChange', type: '(open: boolean) => void', required: true, description: 'Callback when drawer state changes', interactive: false },
            { name: 'title', type: 'string', required: false, description: 'Title displayed in the drawer header', interactive: false },
            { name: 'children', type: 'ReactNode', required: true, description: 'Content to display in the drawer', interactive: false },
            { name: 'position', type: "'left' | 'right'", required: false, description: 'Position of the drawer (default: right)', interactive: true, key: 'position' },
            { name: 'width', type: 'string', required: false, description: 'Width of the drawer (e.g., "400px")', interactive: false },
            { name: 'footer', type: 'ReactNode', required: false, description: 'Footer content (buttons, actions)', interactive: true, key: 'withFooter' },
            { name: 'mainContent', type: 'ReactNode', required: false, description: 'Main content that gets pushed when drawer opens', interactive: false },
            { name: 'className', type: 'string', required: false, description: 'Custom className for container', interactive: false },
            { name: 'drawerClassName', type: 'string', required: false, description: 'Custom className for drawer panel', interactive: false },
          ],
          features: [
            'Pushes main content instead of overlaying',
            'Smooth slide-in animation',
            'Close on ESC key',
            'Left or right positioning',
            'Customizable width',
            'Optional header and footer',
            'Keeps main content visible',
            'Dark mode support',
          ],
        };
      case 'dropdown':
        return {
          props: [
            { name: 'trigger', type: 'ReactNode', required: true, description: 'Element that triggers the dropdown (button, icon, etc.)', interactive: false },
            { name: 'items', type: 'DropdownItem[]', required: true, description: 'Array of menu items to display', interactive: false },
            { name: 'placement', type: "'bottom' | 'bottom-start' | 'bottom-end'", required: false, description: 'Position of dropdown menu (default: bottom-end)', interactive: true, key: 'placement' },
            { name: 'className', type: 'string', required: false, description: 'Custom className for dropdown menu', interactive: false },
            { name: 'separator', type: 'boolean', required: false, description: 'Add separator between items', interactive: true, key: 'withSeparator' },
          ],
          features: [
            'Portal-based rendering',
            'Auto-positioning within viewport',
            'Click outside to close',
            'Support for links and buttons',
            'Optional separators',
            'Custom styling per item',
            'Keyboard accessible',
            'Smooth animations',
          ],
        };
      case 'toast':
        return {
          props: [
            { name: 'message', type: 'string', required: true, description: 'The message to display in the toast', interactive: false },
            { name: 'type', type: "'success' | 'error' | 'info' | 'warning'", required: false, description: 'Type of toast (default: success)', interactive: true, key: 'type' },
            { name: 'duration', type: 'number', required: false, description: 'Auto-close duration in ms (0 = persistent)', interactive: false },
            { name: 'persistent', type: 'boolean', required: false, description: 'Keep toast visible until manually closed', interactive: true, key: 'persistent' },
            { name: 'onClose', type: '() => void', required: true, description: 'Callback when toast is closed', interactive: false },
            { name: 'onClick', type: '() => void', required: false, description: 'Callback when toast is clicked', interactive: false },
          ],
          features: [
            'Four toast types (success, error, info, warning)',
            'Auto-dismiss after duration',
            'Manual close button',
            'Optional click handler',
            'Persistent mode (duration = 0)',
            'Smooth slide-in animation',
            'Fixed positioning (top-right)',
            'Dark mode support',
          ],
        };
      case 'tooltip':
        return {
          props: [
            { name: 'content', type: 'ReactNode', required: true, description: 'Content to display in the tooltip', interactive: false },
            { name: 'children', type: 'ReactNode', required: true, description: 'Element that triggers the tooltip on hover', interactive: false },
            { name: 'placement', type: 'TooltipPlacement', required: false, description: 'Position of tooltip (top, right, bottom, left, etc.)', interactive: true, key: 'placement' },
            { name: 'delay', type: 'number', required: false, description: 'Delay before showing tooltip in ms (default: 100)', interactive: false },
            { name: 'disabled', type: 'boolean', required: false, description: 'Disable tooltip from showing', interactive: true, key: 'disabled' },
            { name: 'offset', type: 'number', required: false, description: 'Distance from trigger element (default: 8)', interactive: false },
            { name: 'zIndex', type: 'number', required: false, description: 'Z-index for tooltip (default: 9999)', interactive: false },
            { name: 'className', type: 'string', required: false, description: 'Custom className for tooltip', interactive: false },
          ],
          features: [
            '12+ placement options (top, right, bottom, left, with -start/-end variants)',
            'Rich content support (JSX, HTML)',
            'Hover delay configuration',
            'Auto-positioning within viewport',
            'Arrow indicator',
            'Portal-based rendering',
            'Disable on demand',
            'Custom offset and z-index',
          ],
        };
      case 'calendar':
        return {
          props: [
            { name: 'events', type: 'CalendarEvent[]', required: false, description: 'Array of events to display on the calendar', interactive: false },
            { name: 'defaultView', type: "'month' | 'week' | 'day' | 'year'", required: false, description: 'Initial calendar view (default: month)', interactive: true, key: 'defaultView' },
            { name: 'onAddEvent', type: '() => void', required: false, description: 'Callback when add event button is clicked', interactive: false },
            { name: 'onEventClick', type: '(event: CalendarEvent) => void', required: false, description: 'Callback when an event is clicked', interactive: false },
          ],
          features: [
            'Four view modes: Month, Week, Day, Year',
            'View switcher dropdown',
            'Previous/Next navigation',
            'Today quick jump button',
            'Color-coded events (blue, pink, indigo, gray)',
            'Event time display',
            'Optional location info',
            'Add event button',
            'Mobile responsive',
            'Event click handlers',
          ],
        };
      case 'datetimepicker':
        return {
          props: [
            { name: 'mode', type: "'date' | 'time' | 'datetime'", required: false, description: 'Picker mode: date only, time only, or both (default: datetime)', interactive: true, key: 'mode' },
            { name: 'selectionType', type: "'single' | 'range'", required: false, description: 'Single value or range selection (default: single)', interactive: true, key: 'selectionType' },
            { name: 'value', type: 'Date | DateTimeValue | null', required: false, description: 'Current selected value', interactive: false },
            { name: 'onChange', type: '(value: Date | DateTimeValue | null) => void', required: false, description: 'Callback when value changes', interactive: false },
            { name: 'placeholder', type: 'string', required: false, description: 'Placeholder text for the input', interactive: false },
            { name: 'disabled', type: 'boolean', required: false, description: 'Disable the picker', interactive: true, key: 'disabled' },
            { name: 'minDate', type: 'Date', required: false, description: 'Minimum selectable date', interactive: false },
            { name: 'maxDate', type: 'Date', required: false, description: 'Maximum selectable date', interactive: false },
            { name: 'minuteStep', type: 'number', required: false, description: 'Step between minutes (default: 1)', interactive: false },
            { name: 'use24Hour', type: 'boolean', required: false, description: 'Use 24-hour format instead of AM/PM (default: false)', interactive: true, key: 'use24Hour' },
            { name: 'showTimeQualifier', type: 'boolean', required: false, description: 'Show Before/At/After qualifier selection for times', interactive: true, key: 'showTimeQualifier' },
            { name: 'labelType', type: "'auto' | 'date' | 'datetime' | 'date-range' | 'datetime-range' | 'time' | 'time-range' | 'custom' | 'none'", required: false, description: 'Auto-generate label based on mode/selection or use custom/none', interactive: true, key: 'labelType' },
            { name: 'label', type: 'string', required: false, description: 'Custom label text (only used when labelType is "custom")', interactive: false },
            { name: 'error', type: 'string', required: false, description: 'Error message to display', interactive: false },
            { name: 'required', type: 'boolean', required: false, description: 'Mark field as required', interactive: false },
            { name: 'autoApply', type: 'boolean', required: false, description: 'Auto-apply and close when selecting a date in single/date mode (default: false)', interactive: true, key: 'autoApply' },
            { name: 'openDirection', type: "'down' | 'up' | 'left' | 'right' | 'downleft' | 'downright' | 'upleft' | 'upright'", required: false, description: 'Direction the dropdown opens (default: down)', interactive: true, key: 'openDirection' },
            { name: 'inputVariant', type: "'default' | 'compact' | 'modal'", required: false, description: 'Input styling variant: default for forms, compact for inline filters, modal for modal forms', interactive: true, key: 'inputVariant' },
          ],
          features: [
            'Three modes: date, time, datetime',
            'Single or range selection',
            'Light-themed popup with calendar and time columns',
            'Scrollable hour, minute, AM/PM wheels',
            'Optional Before/At/After time qualifier',
            '12-hour or 24-hour format',
            'Date range selection with hover preview',
            'Min/max date constraints',
            'Cancel and OK buttons',
            'Click outside to close',
            'Keyboard accessible',
            'Mobile responsive',
            'Auto-apply option for single date mode',
            'Configurable dropdown direction',
            'Manual date typing support',
            'Clear button to reset value',
          ],
        };
      case 'multiselect':
        return {
          props: [
            { name: 'options', type: 'MultiSelectOption[]', required: true, description: 'Array of options to select from', interactive: false },
            { name: 'value', type: 'string[]', required: true, description: 'Array of selected option values', interactive: false },
            { name: 'onChange', type: '(value: string[]) => void', required: true, description: 'Callback when selection changes', interactive: false },
            { name: 'placeholder', type: 'string', required: false, description: 'Placeholder text when nothing selected', interactive: false },
            { name: 'disabled', type: 'boolean', required: false, description: 'Disable the multiselect', interactive: true, key: 'disabled' },
            { name: 'enableSearch', type: 'boolean', required: false, description: 'Enable search/filter functionality', interactive: true, key: 'enableSearch' },
            { name: 'searchPlaceholder', type: 'string', required: false, description: 'Placeholder for search input', interactive: false },
            { name: 'showClearOption', type: 'boolean', required: false, description: 'Show "All" option to clear selections', interactive: true, key: 'showClearOption' },
            { name: 'clearOptionLabel', type: 'string', required: false, description: 'Label for clear option (default: "All")', interactive: false },
            { name: 'countThreshold', type: 'number', required: false, description: 'Items to show before switching to count mode', interactive: false },
            { name: 'className', type: 'string', required: false, description: 'Custom className for container', interactive: false },
          ],
          features: [
            'Multi-selection with checkmarks',
            'Search/filter options',
            'Clear all option',
            'Count mode for many selections',
            'Selected items sorted to top',
            'Click outside to close',
            'Keyboard accessible',
            'Icon support per option',
            'Dark mode support',
            'Disabled state',
          ],
        };
      case 'typeahead':
        return {
          props: [
            { name: 'options', type: 'TypeAheadOption[]', required: true, description: 'Array of options with label, value, description, group', interactive: false },
            { name: 'value', type: 'string | string[]', required: true, description: 'Currently selected value (string for single, string[] for multi)', interactive: false },
            { name: 'onChange', type: '(value: string | string[]) => void', required: true, description: 'Callback when selection changes', interactive: false },
            { name: 'placeholder', type: 'string', required: false, description: 'Placeholder text for input', interactive: false },
            { name: 'label', type: 'string', required: false, description: 'Label for the input field', interactive: false },
            { name: 'multiple', type: 'boolean', required: false, description: 'Enable multi-select mode (default: false)', interactive: true, key: 'multiple' },
            { name: 'disabled', type: 'boolean', required: false, description: 'Disable the typeahead', interactive: true, key: 'disabled' },
            { name: 'showSearch', type: 'boolean', required: false, description: 'Show search icon (default: true)', interactive: true, key: 'showSearch' },
            { name: 'showDescriptions', type: 'boolean', required: false, description: 'Show option descriptions', interactive: true, key: 'showDescriptions' },
            { name: 'groupBy', type: 'boolean', required: false, description: 'Group options by group field', interactive: true, key: 'groupBy' },
            { name: 'showIcons', type: 'boolean', required: false, description: 'Show option icons if provided', interactive: false },
            { name: 'maxHeight', type: 'string', required: false, description: 'Max height class for dropdown', interactive: false },
            { name: 'onClear', type: '() => void', required: false, description: 'Callback when clear button clicked', interactive: false },
            { name: 'renderOption', type: '(option: TypeAheadOption) => React.ReactNode', required: false, description: 'Custom rich rendering per result — return any node (e.g. a lookup detail card) to replace the default label/description; return null to fall back to default', interactive: false },
            { name: 'onSelect', type: '(option: TypeAheadOption) => void', required: false, description: 'Fires with the full option picked (single-select); use option.data to react — e.g. fill a denormalized address', interactive: false },
            { name: 'onSearch', type: '(term: string) => void', required: false, description: 'Async/server-search mode: called with the raw term as the user types so the caller can fetch and supply options', interactive: false },
            { name: 'freeText', type: 'boolean', required: false, description: 'Free-text combobox: input mirrors value verbatim, no-match not flagged invalid; with onSearch options show unfiltered (server-filtered), without it the static list filters client-side', interactive: false },
            { name: 'className', type: 'string', required: false, description: 'Custom className for container', interactive: false },
          ],
          features: [
            'Autocomplete search/filter',
            'Custom rich result rendering via renderOption (e.g. inline lookup detail cards)',
            'Async / server-side search via onSearch + freeText combobox mode',
            'Keyboard navigation (arrows, enter, esc, tab)',
            'Auto-highlight first filtered option',
            'Tab-to-confirm selection and move to next field',
            'Portal rendering for use in modals/dialogs',
            'Fixed positioning to escape parent containers',
            'Single and multi-select modes',
            'Optional descriptions per option',
            'Group options by category',
            'Icon support per option',
            'Clear button',
            'Click outside to close',
            'Highlighted option on hover',
            'Dark mode support',
          ],
        };
      case 'toggle':
        return {
          props: [
            { name: 'checked', type: 'boolean', required: false, description: 'Whether the toggle is on (default: false)', interactive: false },
            { name: 'onChange', type: '(checked: boolean) => void', required: false, description: 'Callback when toggle state changes', interactive: false },
            { name: 'variant', type: "'simple' | 'centered' | 'with-icons' | 'with-label'", required: false, description: 'Visual variant (default: simple)', interactive: true, key: 'variant' },
            { name: 'size', type: "'sm' | 'md' | 'lg'", required: false, description: 'Size of the toggle (default: md)', interactive: true, key: 'size' },
            { name: 'disabled', type: 'boolean', required: false, description: 'Disable the toggle', interactive: true, key: 'disabled' },
            { name: 'label', type: 'string', required: false, description: 'Label text (for with-label variant)', interactive: false },
            { name: 'description', type: 'string', required: false, description: 'Description text (for with-label variant)', interactive: false },
            { name: 'name', type: 'string', required: false, description: 'Input name attribute', interactive: false },
            { name: 'ariaLabel', type: 'string', required: false, description: 'Accessibility label', interactive: false },
            { name: 'className', type: 'string', required: false, description: 'Custom className', interactive: false },
          ],
          features: [
            'Four visual variants',
            'Three size options (sm, md, lg)',
            'Simple variant - basic toggle',
            'Centered variant - centered knob',
            'With-icons variant - shows check/x icons',
            'With-label variant - includes label and description',
            'Disabled state',
            'Smooth animations',
            'Dark mode support',
            'Accessible',
          ],
        };
      case 'buttongroup':
        return {
          props: [
            { name: 'options', type: 'ButtonGroupOption[]', required: true, description: 'Array of button options with label, value, and optional icon', interactive: false },
            { name: 'value', type: 'string', required: false, description: 'Currently selected value', interactive: false },
            { name: 'onChange', type: '(value: string) => void', required: false, description: 'Callback when selection changes', interactive: false },
            { name: 'withIcons', type: 'boolean', required: false, description: 'Include icons in buttons', interactive: true, key: 'withIcons' },
            { name: 'className', type: 'string', required: false, description: 'Custom className for container', interactive: false },
          ],
          features: [
            'Grouped buttons with shared borders',
            'Single selection (radio behavior)',
            'Optional icons per button',
            'Active state highlighting',
            'Rounded corners on first/last buttons',
            'Hover effects',
            'Focus ring support',
            'Dark mode support',
            'Accessible',
          ],
        };
      case 'tabbar':
        return {
          props: [
            { name: 'tabs', type: 'Tab[]', required: true, description: 'Array of tab objects with id, label, current, icon, badge', interactive: false },
            { name: 'onTabChange', type: '(tabId: string) => void', required: false, description: 'Callback when tab is clicked', interactive: false },
            { name: 'withIcons', type: 'boolean', required: false, description: 'Include icons in tabs', interactive: true, key: 'withIcons' },
            { name: 'withBadges', type: 'boolean', required: false, description: 'Show badge counts on tabs', interactive: true, key: 'withBadges' },
            { name: 'noBorder', type: 'boolean', required: false, description: 'Remove border and shadow', interactive: true, key: 'noBorder' },
            { name: 'className', type: 'string', required: false, description: 'Custom className for container', interactive: false },
          ],
          features: [
            'Desktop and mobile responsive',
            'Mobile: dropdown select',
            'Desktop: horizontal tabs with border',
            'Active tab indicator (bottom border)',
            'Optional icons per tab',
            'Optional badge counts',
            'Disabled tab support',
            'Hover effects',
            'Dark mode support',
            'Accessible',
          ],
        };
      case 'statusbadge':
        return {
          props: [
            { name: 'label', type: 'string', required: true, description: 'Text to display in the badge', interactive: false },
            { name: 'color', type: 'BadgeColor', required: false, description: 'Color of the status dot (default: gray)', interactive: true, key: 'color' },
            { name: 'className', type: 'string', required: false, description: 'Custom className for badge', interactive: false },
          ],
          features: [
            '8 color options (red, yellow, green, blue, indigo, purple, pink, gray)',
            'Colored status dot indicator',
            'Rounded badge design',
            'Compact size',
            'Ring border styling',
            'Dark mode support',
            'Perfect for status indicators',
            'Inline display',
          ],
        };
      case 'tagbadge':
        return {
          props: [
            { name: 'label', type: 'string', required: true, description: 'Text to display in the tag', interactive: false },
            { name: 'color', type: 'TagColor', required: false, description: 'Color theme for the tag (default: gray)', interactive: true, key: 'color' },
            { name: 'onRemove', type: '() => void', required: false, description: 'Callback when remove button clicked (shows X button)', interactive: true, key: 'removable' },
            { name: 'className', type: 'string', required: false, description: 'Custom className for tag', interactive: false },
          ],
          features: [
            '8 color options (gray, red, yellow, green, blue, indigo, purple, pink)',
            'Optional remove button (X icon)',
            'Pill-shaped design',
            'Full background color',
            'Ring border styling',
            'Hover effects on remove button',
            'Dark mode support',
            'Perfect for tags, categories, filters',
          ],
        };
      case 'contactcard':
        return {
          props: [
            { name: 'name', type: 'string', required: true, description: 'Person\'s full name', interactive: false },
            { name: 'title', type: 'string', required: true, description: 'Job title or position', interactive: false },
            { name: 'role', type: 'string', required: true, description: 'Department or role category', interactive: false },
            { name: 'roleColor', type: 'BadgeColor', required: false, description: 'Color for role badge (default: green)', interactive: true, key: 'roleColor' },
            { name: 'imageUrl', type: 'string', required: true, description: 'URL for profile image', interactive: false },
            { name: 'email', type: 'string', required: true, description: 'Email address (creates mailto link)', interactive: false },
            { name: 'phone', type: 'string', required: true, description: 'Phone number (creates tel link)', interactive: false },
            { name: 'maxWidth', type: "'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full'", required: false, description: 'Maximum width of card (default: sm)', interactive: false },
            { name: 'className', type: 'string', required: false, description: 'Custom className', interactive: false },
          ],
          features: [
            'Profile image with rounded styling',
            'Name, title, and role display',
            'Colored role badge',
            'Email and phone action buttons',
            'Clickable email (mailto) and phone (tel) links',
            'Responsive width options',
            'Card styling with shadow and border',
            'Dark mode support',
            'Grid-friendly layout',
          ],
        };
      case 'statbutton':
        return {
          props: [
            { name: 'label', type: 'string', required: true, description: 'Label text for the stat', interactive: false },
            { name: 'value', type: 'string | number', required: true, description: 'The stat value to display', interactive: false },
            { name: 'icon', type: 'React.ComponentType', required: false, description: 'Icon component to display', interactive: true, key: 'withIcon' },
            { name: 'onClick', type: '() => void', required: false, description: 'Callback when button is clicked', interactive: false },
            { name: 'className', type: 'string', required: false, description: 'Custom className', interactive: false },
          ],
          features: [
            'Two-part segmented button',
            'Label on left, value on right',
            'Optional icon in label section',
            'Clickable for drill-down actions',
            'Hover effects',
            'Focus ring support',
            'Dark mode support',
            'Perfect for dashboard KPIs',
            'Compact design',
          ],
        };
      case 'statsgrid':
        return {
          props: [
            { name: 'stats', type: 'StatItem[]', required: true, description: 'Array of stat items to display in grid', interactive: false },
            { name: 'columns', type: 'number', required: false, description: 'Number of columns (1-8, default: 5)', interactive: true, key: 'columns', inputType: 'number' },
            { name: 'rows', type: 'number', required: false, description: 'Number of rows (default: 2)', interactive: true, key: 'rows', inputType: 'number' },
            { name: 'timePeriod', type: 'TimePeriod', required: false, description: 'Current time period selection', interactive: true, key: 'showTimePeriod' },
            { name: 'onTimePeriodChange', type: '(period: TimePeriod) => void', required: false, description: 'Callback when time period changes', interactive: false },
            { name: 'loading', type: 'boolean', required: false, description: 'Show loading spinners instead of values', interactive: true, key: 'loading' },
            { name: 'title', type: 'string', required: false, description: 'Optional title (hidden when time period selector shown)', interactive: false },
            { name: 'className', type: 'string', required: false, description: 'Custom className', interactive: false },
          ],
          features: [
            'Configurable grid layout (columns x rows)',
            'Time period dropdown (Today, Tomorrow, This Week)',
            'Green values with thumbs-up icon for zero counts',
            'Red values for non-zero counts',
            'Loading spinner state',
            'Clickable cells with hover states',
            'Selection highlighting',
            'Dark mode support',
            'Responsive design',
          ],
        };
      case 'trackerbutton':
        return {
          props: [
            { name: 'name', type: 'string', required: true, description: 'Display name (e.g., date)', interactive: false },
            { name: 'min', type: 'number', required: true, description: 'Current value', interactive: false },
            { name: 'max', type: 'number', required: true, description: 'Maximum value', interactive: false },
            { name: 'descriptor', type: 'string', required: false, description: 'Label below bar (default: capacity)', interactive: false },
            { name: 'bgColor', type: 'string', required: false, description: 'Tailwind bg class for bar background', interactive: false },
            { name: 'fillColor', type: 'string', required: false, description: 'Tailwind bg class for fill (default: bg-blue-500)', interactive: true, key: 'fillColor' },
            { name: 'barThickness', type: 'number', required: false, description: 'Height of bar in pixels (default: 8)', interactive: true, key: 'barThickness', inputType: 'number' },
            { name: 'size', type: "'xs' | 'sm' | 'md' | 'lg' | 'xl'", required: false, description: 'Font size (default: sm)', interactive: true, key: 'size' },
            { name: 'onClick', type: '() => void', required: false, description: 'Callback when button is clicked', interactive: false },
          ],
          features: [
            'Progress bar with fill percentage',
            'Name and min/max display',
            'Descriptor with percentage below bar',
            'Customizable bar colors',
            'Clickable with hover effects',
            'Dark mode support',
            'Ideal for capacity tracking',
          ],
        };
      case 'filterbar':
        return {
          props: [
            { name: 'children', type: 'ReactNode', required: true, description: 'Filter components for the main row', interactive: false },
            { name: 'expandedContent', type: 'ReactNode', required: false, description: 'Content shown when expanded (use FilterBar.Row)', interactive: false },
            { name: 'label', type: 'string', required: false, description: 'Label shown at start (default: "Filters:")', interactive: false },
            { name: 'className', type: 'string', required: false, description: 'Additional CSS classes', interactive: false },
          ],
          features: [
            'Sticky positioning at top of viewport',
            'Expandable with chevron toggle',
            'FilterBar.Row for additional rows',
            'FilterBar.Spacer to push items right',
            'FilterBar.Divider for vertical separators',
            'FilterBar.Group for labeled filter groups',
            'Accepts any filter components as children',
            'Dark mode support',
            'Consistent styling across pages',
          ],
        };
      case 'modernfiltermodal':
        return {
          props: [
            { name: 'isOpen', type: 'boolean', required: true, description: 'Whether the modal is open', interactive: false },
            { name: 'onClose', type: '() => void', required: true, description: 'Callback when modal closes', interactive: false },
            { name: 'onSubmit', type: '(conditions: FilterCondition[], view: string) => void', required: true, description: 'Callback when filters are applied', interactive: false },
            { name: 'fields', type: 'string[]', required: true, description: 'Array of field names for filtering', interactive: false },
            { name: 'views', type: 'string[]', required: false, description: 'Array of view options', interactive: false },
            { name: 'initialView', type: 'string', required: false, description: 'Default selected view', interactive: false },
            { name: 'initialConditions', type: 'FilterCondition[]', required: false, description: 'Pre-populated filter conditions', interactive: false },
            { name: 'savedFilters', type: 'SavedFilters', required: false, description: 'Object of saved filter presets', interactive: false },
            { name: 'onSaveFilter', type: '(name: string, conditions: FilterCondition[]) => void', required: false, description: 'Callback to save a filter', interactive: false },
            { name: 'onDeleteFilter', type: '(folder: string, index: number) => void', required: false, description: 'Callback to delete a saved filter', interactive: false },
          ],
          features: [
            'Centered modal dialog',
            'Draggable modal window',
            'Advanced filter builder interface',
            'Multiple filter conditions (AND logic)',
            'Field, comparator, value triplets',
            'Compare options: Equal, Contains, Greater than, etc.',
            'View/preset selector dropdown',
            'Save and load filter presets',
            'Folder organization for saved filters',
            'Auto-add new condition rows',
            'Reset filters button',
            'Apply and close actions',
            'Backdrop overlay',
            'ESC to close',
          ],
        };
      case 'charts':
        return {
          props: [
            { name: 'chartType', type: 'ChartType', required: false, description: 'Type of chart to display', interactive: true, key: 'chartType' },
            { name: 'data', type: 'ChartData[]', required: true, description: 'Array of data points for the chart', interactive: false },
            { name: 'dataKeys', type: 'DataKey[]', required: false, description: 'Data series configuration (Bar/Line/Area only)', interactive: false },
            { name: 'title', type: 'string', required: false, description: 'Chart title', interactive: false },
            { name: 'height', type: 'number', required: false, description: 'Chart height in pixels (default: 400)', interactive: false },
            { name: 'showLegend', type: 'boolean', required: false, description: 'Show chart legend (Bar chart only)', interactive: true, key: 'showLegend' },
            { name: 'horizontal', type: 'boolean', required: false, description: 'Horizontal bars (Bar chart only)', interactive: true, key: 'horizontal' },
            { name: 'colors', type: 'string[]', required: false, description: 'Custom color array (Pie/Donut only)', interactive: false },
          ],
          features: [
            '6 chart types: Bar, Line, Area, Pie, Donut, Radial',
            'Built with Recharts library',
            'Responsive sizing',
            'Interactive tooltips on hover',
            'Customizable colors',
            'Optional legends',
            'Grid lines',
            'Dark mode support',
            'Card container with shadow',
            'Click handlers (Bar chart)',
          ],
        };
      case 'orderdetailsdrawer':
        return {
          props: [
            { name: 'pronumber', type: 'number | null', required: true, description: 'The PRO number of the order to display; ProDetailSlideOver fetches and refreshes it itself', interactive: false },
            { name: 'isOpen', type: 'boolean', required: true, description: 'Whether the slide-over is open', interactive: false },
            { name: 'onClose', type: '() => void', required: true, description: 'Callback to close the slide-over', interactive: false },
          ],
          features: [
            'Full-width expandable drawer for order details',
            'Displays order information with appointment numbers',
            'Shows arrival and departure times for each leg',
            'Groups order legs with visual separators',
            'Inline driver search and assignment panel',
            'Street turn opportunity detection and display',
            'Real-time active viewer indicators',
            'Edit mode for updating leg details',
            'Save/Cancel actions with validation',
            'Optimistic UI updates',
            'Integration with Dispatch Dashboard and Planner',
            'Responsive design with dark mode support',
            'Auto-save functionality for leg edits',
            'sendToGwiMove validation and handling',
          ],
        };
      case 'movecard':
        return {
          props: [
            { name: 'move', type: 'MoveRisk', required: true, description: 'The move/order data object containing PRO, route, status, deadlines, etc.', interactive: false },
            { name: 'priority', type: 'number', required: true, description: 'Display priority number for the move', interactive: false },
            { name: 'visualPriority', type: 'number', required: false, description: 'Visual priority shown on card (1,2,3 among not-started moves only)', interactive: false },
            { name: 'isCurrentMove', type: 'boolean', required: true, description: 'Whether this is the driver\'s currently active move', interactive: false },
            { name: 'isGroupedDisplay', type: 'boolean', required: false, description: 'Whether this card is displayed as part of a grouped (linked) move set', interactive: false },
            { name: 'group', type: 'MoveGroup', required: false, description: 'The move group this card belongs to (for linked consecutive legs)', interactive: false },
            { name: 'projectedStatus', type: "ProjectedStatus ('on_track' | 'at_risk' | 'will_miss' | 'missed')", required: false, description: 'Cascading ETA-based projected status for this move', interactive: false },
            { name: 'projectedETA', type: 'string', required: false, description: 'Formatted projected arrival time (e.g., "3:45 PM")', interactive: false },
            { name: 'isETAToDestination', type: 'boolean', required: false, description: 'Whether the ETA is to destination (true) or to origin (false)', interactive: false },
            { name: 'isLinkedMove', type: 'boolean', required: false, description: 'Whether this move is linked to the previous move (same PRO, consecutive legs)', interactive: false },
            { name: 'isDueTomorrow', type: 'boolean', required: false, description: 'Whether this move is due tomorrow (not today)', interactive: false },
            { name: 'linkedArrivalTime', type: 'string', required: false, description: 'Arrival time at shared location for linked moves', interactive: false },
            { name: 'onProClick', type: '(pronumber: number, legnum: number) => void', required: false, description: 'Callback when PRO number is clicked (opens order details). Omit for read-only.', interactive: false },
            { name: 'onRemove', type: '(move: MoveRisk, group?: MoveGroup) => void', required: false, description: 'Callback for remove button. Omit to hide remove button.', interactive: false },
            { name: 'onQuickAssign', type: '(move: MoveRisk, targetUnitnumber: string) => void', required: false, description: 'Callback for quick-assign popup. Omit to hide quick-assign.', interactive: false },
            { name: 'onDragStart', type: '(e: DragEvent, move: MoveRisk, sourceDriver: string, group?: MoveGroup) => void', required: false, description: 'Callback when drag starts. Omit to disable dragging.', interactive: false },
            { name: 'onDragEnd', type: '(e: DragEvent) => void', required: false, description: 'Callback when drag ends', interactive: false },
          ],
          features: [
            'Risk-colored status cards (not started, in progress, completed)',
            'Cascading ETA display with projected status badges (on track, at risk, will miss, missed)',
            'PRO number link to order details',
            'Route display with abbreviated location names',
            'Deadline time and type display',
            'Container and equipment type info',
            'Drag-and-drop support (optional — omit callbacks to disable)',
            'Quick-assign popup for reassigning to another driver (optional)',
            'Remove from driver button with grouped move awareness (optional)',
            'Tomorrow badge for next-day moves',
            'Linked move indicator for consecutive legs on same PRO',
            'Dark mode support',
          ],
        };
      case 'compacthoswidget':
        return {
          props: [
            { name: 'currentDutyStatus', type: 'string | null', required: true, description: 'Driver duty status (driving, offduty, sleeperbed, onduty, yardmove, personalconveyance)', interactive: false },
            { name: 'breakTimeRemainingMins', type: 'number | null', required: true, description: 'Minutes remaining until break required (max 8 hours)', interactive: false },
            { name: 'driveTimeRemainingMins', type: 'number | null', required: true, description: 'Minutes of drive time remaining (max 11 hours)', interactive: false },
            { name: 'shiftTimeRemainingMins', type: 'number | null', required: true, description: 'Minutes of shift time remaining (max 14 hours)', interactive: false },
            { name: 'cycleTimeRemainingMins', type: 'number | null', required: true, description: 'Minutes of cycle time remaining (max 70 hours)', interactive: false },
            { name: 'samsaraDriverId', type: 'string | null', required: true, description: 'Samsara driver ID for linking to HOS report (null hides link)', interactive: false },
          ],
          features: [
            'Duty status badge with color coding (D=green, OFF=gray, SB=purple, ON=blue)',
            'Circular progress rings with time values inside',
            'FMCSA 70-hour/8-day rule max values',
            'Clickable duty badge links to Samsara HOS report',
            'Dark mode support',
            'Compact design for header integration',
          ],
        };
      case 'driverlane':
        return {
          props: [
            { name: 'driver', type: 'DriverWithMoves', required: true, description: 'Driver data with all their moves, HOS info, and endorsements', interactive: false },
            { name: 'showCompleted', type: 'boolean', required: true, description: 'Whether to show completed moves', interactive: true, key: 'showCompleted' },
            { name: 'displaySettings', type: 'DisplaySettings', required: true, description: 'Display settings controlling status badges, endorsements, ghost cards', interactive: false },
            { name: 'orientation', type: "'horizontal' | 'vertical'", required: false, description: 'Layout orientation — horizontal for scrolling rows, vertical for stacked columns (default: horizontal)', interactive: true, key: 'orientation', inputType: 'radio', options: ['horizontal', 'vertical'] },
            { name: 'renderLocationMap', type: '(props: { driverLat, driverLng, ... }) => ReactNode', required: false, description: 'Render prop for location map popup (keeps Google Maps dependency out)', interactive: false },
            { name: 'onProClick', type: '(pronumber: number, legnum: number) => void', required: false, description: 'Callback when PRO number is clicked. Omit for read-only.', interactive: false },
            { name: 'onRemove', type: '(move: MoveRisk, group?: MoveGroup) => void', required: false, description: 'Callback for remove action. Omit to hide remove buttons.', interactive: false },
            { name: 'onQuickAssign', type: '(move: MoveRisk, targetUnitnumber: string) => void', required: false, description: 'Callback for quick-assign. Omit to hide quick-assign popups.', interactive: false },
            { name: 'onDragStart / onDragEnd', type: 'DragEvent handlers', required: false, description: 'Drag-and-drop callbacks. Omit to disable dragging.', interactive: false },
            { name: 'onDragOver / onDrop', type: 'DragEvent handlers', required: false, description: 'Drop target callbacks for cross-driver reassignment', interactive: false },
            { name: 'onReorder', type: '(driverUnitnumber: string, newOrder: MoveGroup[]) => void', required: false, description: 'Callback for reordering moves within same driver', interactive: false },
            { name: 'openAvailableMovesDrawer', type: '(unitNumber, driverName, mapData?) => void', required: false, description: 'Callback for "Add Available Move" pills. Omit to hide pills.', interactive: false },
            { name: 'onOpenDeadheadMap', type: '(prevMove, nextMove, travelMinutes, driverUnit, driverName) => void', required: false, description: 'Callback for deadhead travel time clicks', interactive: false },
            { name: 'onGhostCardClick', type: '(suggestion: GhostSuggestion) => void', required: false, description: 'Callback for ghost card clicks. Omit to hide ghost cards.', interactive: false },
            { name: 'showGhostCards', type: 'boolean', required: false, description: 'Whether to render ghost suggestion cards (default: true)', interactive: false },
            { name: 'hideHeader', type: 'boolean', required: false, description: 'Hide the driver header row (name, stats, HOS). Use when embedding in an external container like a drawer.', interactive: true, key: 'hideHeader' },
          ],
          features: [
            'Cascading ETA calculation across all moves (accounts for deadhead, buffers)',
            'HOS utilization display (drive time remaining, cycle time)',
            'Over-capacity detection (estimated travel exceeds HOS remaining)',
            'Automatic move grouping for consecutive legs on same PRO',
            'Deadhead travel time indicators between moves',
            'Add Available Move pills for long deadhead gaps',
            'Drag-and-drop reordering within same driver (optional)',
            'Driver location map popup via render prop (optional)',
            'Duty status badge (Driving, On Duty, Off Duty, Sleeper, etc.)',
            'Driver endorsement badges (HAZ, TANK, 93K, DAY)',
            'Missed/Late move badges based on cascading ETA',
            'HOS Violation badge',
            'Ghost card support at before_first, between, and after_last positions (optional)',
            'Horizontal (scrolling row) and vertical (stacked column) layouts',
            'CMS and Samsara external links',
            'Dark mode support',
          ],
        };
      case 'risksection':
        return {
          props: [
            { name: 'title', type: 'string', required: true, description: 'Section title (e.g., "Critical Risk", "At Risk", "On Track")', interactive: false },
            { name: 'icon', type: 'ReactNode', required: true, description: 'Icon displayed next to the title', interactive: false },
            { name: 'headerClass', type: 'string', required: true, description: 'CSS class for the section header (controls color-coding)', interactive: false },
            { name: 'drivers', type: 'DriverWithMoves[]', required: true, description: 'Array of drivers to display in this section', interactive: false },
            { name: 'showCompleted', type: 'boolean', required: true, description: 'Whether to show completed moves in each driver lane', interactive: false },
            { name: 'displaySettings', type: 'DisplaySettings', required: true, description: 'Display settings passed through to each DriverLane', interactive: false },
            { name: 'isSectionExpanded', type: 'boolean', required: true, description: 'Whether the section content is visible', interactive: false },
            { name: 'onToggleSection', type: '() => void', required: true, description: 'Callback to toggle section expanded/collapsed state', interactive: false },
            { name: 'orientation', type: "'horizontal' | 'vertical'", required: false, description: 'Layout orientation passed through to each DriverLane', interactive: false },
            { name: 'renderLocationMap', type: 'render prop', required: false, description: 'Location map render prop passed through to each DriverLane', interactive: false },
            { name: 'onGhostCardClick', type: '(suggestion: GhostSuggestion) => void', required: false, description: 'Ghost card click handler passed through to each DriverLane', interactive: false },
            { name: 'showGhostCards', type: 'boolean', required: false, description: 'Ghost card visibility passed through to each DriverLane', interactive: false },
          ],
          features: [
            'Collapsible section with animated chevron',
            'Driver count display in header',
            'Color-coded headers via headerClass prop (Critical=red, At Risk=amber, On Track=green, Completed=gray)',
            'Pass-through of all DriverLane props (orientation, callbacks, etc.)',
            'Auto-hides when driver array is empty',
            'Dark mode support',
          ],
        };
      case 'settingsdrawer-dsm':
        return {
          props: [
            { name: 'isOpen', type: 'boolean', required: true, description: 'Whether the settings drawer is open', interactive: false },
            { name: 'onClose', type: '() => void', required: true, description: 'Callback to close the drawer', interactive: false },
            { name: 'displaySettings', type: 'DisplaySettings', required: true, description: 'Current display settings state', interactive: false },
            { name: 'onSettingsChange', type: '(settings: DisplaySettings) => void', required: true, description: 'Callback when any setting is changed', interactive: false },
          ],
          features: [
            'Slide-out drawer with backdrop overlay',
            'Status type toggles (Not Started, Between Moves, Current Move)',
            'Endorsements toggle',
            'Ghost cards per position selector (1, 2, or 3)',
            'Smooth slide animation (0.25s ease-in-out)',
            'Portal-based rendering (renders to document.body)',
            'Dark mode support',
          ],
        };
      case 'removeconfirmmodal':
        return {
          props: [
            { name: 'move', type: 'MoveRisk', required: true, description: 'The move being removed', interactive: false },
            { name: 'group', type: 'MoveGroup | null', required: true, description: 'The move group (null for single moves, MoveGroup for linked legs)', interactive: false },
            { name: 'loading', type: 'boolean', required: true, description: 'Whether the removal is in progress', interactive: false },
            { name: 'onClose', type: '() => void', required: true, description: 'Callback to close the modal', interactive: false },
            { name: 'onConfirm', type: '() => void', required: true, description: 'Callback to confirm the removal', interactive: false },
          ],
          features: [
            'Grouped move awareness — shows all linked moves when removing from a group',
            'Leg numbering for multi-move groups (Leg 1 of 3, etc.)',
            'Move detail display (PRO, type, route, deadline, status)',
            'Loading state with spinner animation',
            'Dynamic button text ("Remove from Driver" vs "Remove 3 Moves")',
            'Scrollable move list for large groups',
            'Dark mode support',
          ],
        };
      case 'timezone-lookup':
        return {
          props: [
            { name: 'lat', type: 'number', required: true, description: 'Latitude coordinate (-90 to 90)', interactive: false },
            { name: 'lng', type: 'number', required: true, description: 'Longitude coordinate (-180 to 180)', interactive: false },
          ],
          features: [
            'Coordinate-to-timezone lookup using timezonefinder library',
            'Returns timezone identifier (e.g., America/Chicago)',
            'Provides current UTC offset accounting for DST',
            'Shows timezone abbreviation (e.g., CST, CDT)',
            'Indicates whether DST is currently active',
            'Supports US, Canada, and Mexico timezones',
            'Returns 404 for coordinates outside coverage (e.g., oceans)',
          ],
        };
      case 'trimble-comparison':
        return {
          props: [
            { name: 'origin', type: 'LocationInput', required: true, description: 'Origin address or lat/lng', interactive: false },
            { name: 'destination', type: 'LocationInput', required: true, description: 'Destination address or lat/lng', interactive: false },
          ],
          features: [
            'Side-by-side comparison of Google Maps and Trimble Maps',
            'Diff highlighting (green <2%, yellow 2-5%, red >5%)',
            'Toll cost display (Trimble only)',
            'State-by-state mileage breakdown',
            'Route map toggle between providers',
          ],
        };
      default:
        return null;
    }
  };

  const componentDetails = getComponentDetails();

  // Group components by category
  const componentsByCategory = useMemo(() => {
    const grouped: Record<string, Component[]> = {};
    components.forEach(comp => {
      if (!grouped[comp.category]) {
        grouped[comp.category] = [];
      }
      grouped[comp.category].push(comp);
    });
    return grouped;
  }, []);

  const categories = ['Core', 'UI', 'Display', 'Modals', 'Charts', 'Fleet Management', 'System Specific', 'Development'];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.relative')) {
        setOpenDropdown(null);
      }
    };

    if (openDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openDropdown]);

  return (
    <div className="flex flex-col overflow-hidden" style={{ height: 'calc(100vh - 5rem)' }}>
      {/* Top Navigation */}
      <nav className="bg-slate-700 dark:bg-slate-800 border-b border-gray-700">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-12 items-center justify-between">
            <div className="flex items-center">
              <div className="flex space-x-4">
                {categories.map((category) => (
                  <div key={category} className="relative">
                    <button
                      onClick={() => setOpenDropdown(openDropdown === category ? null : category)}
                      className="flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
                    >
                      {category}
                      <ChevronDownIcon className={`h-4 w-4 transition-transform ${openDropdown === category ? 'rotate-180' : ''}`} />
                    </button>

                    {openDropdown === category && (
                      <div className="absolute left-0 mt-2 w-56 origin-top-left rounded-md bg-panel shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-700 z-50">
                        <div className="py-1">
                          {componentsByCategory[category]?.map((component) => (
                            <button
                              key={component.id}
                              onClick={() => {
                                setSelectedComponent(component);
                                setOpenDropdown(null);
                              }}
                              className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                                selectedComponent?.id === component.id
                                  ? 'bg-indigo-50 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300'
                                  : 'text-label hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                            >
                              {component.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* 2 column wrapper */}
      <div className="flex w-full flex-1 overflow-hidden">
        {/* Left column - Component Details */}
        <div className="w-96 shrink-0 overflow-y-auto border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-6 sm:px-6">
          {selectedComponent && componentDetails ? (
            <div className="space-y-6">
              {/* Component Title and Description */}
              <div>
                <h2 className="text-2xl font-bold text-heading">{selectedComponent.name}</h2>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-400">{selectedComponent.description}</p>
              </div>

              {componentDetails.features && (
                <div>
                  <button
                    onClick={() => setSectionsExpanded(prev => ({ ...prev, features: !prev.features }))}
                    className="flex w-full items-center justify-between text-sm font-semibold text-body hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  >
                    <span>Features</span>
                    <ChevronDownIcon
                      className={`h-4 w-4 transition-transform ${sectionsExpanded.features ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {sectionsExpanded.features && (
                    <ul className="mt-2 space-y-1 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-400">
                      {componentDetails.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {componentDetails.props && (
                <>
                  {/* Regular Props */}
                  {componentDetails.props.filter((p: any) => !p.interactive).length > 0 && (
                    <div>
                      <button
                        onClick={() => setSectionsExpanded(prev => ({ ...prev, props: !prev.props }))}
                        className="flex w-full items-center justify-between text-sm font-semibold text-body hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                      >
                        <span>Props</span>
                        <ChevronDownIcon
                          className={`h-4 w-4 transition-transform ${sectionsExpanded.props ? 'rotate-180' : ''}`}
                        />
                      </button>
                      {sectionsExpanded.props && (
                        <div className="mt-2 space-y-3">
                          {componentDetails.props.filter((p: any) => !p.interactive).map((prop: any, idx: number) => (
                            <div key={idx} className="border-l-2 border-gray-200 dark:border-gray-700 pl-3">
                              <div className="flex items-start gap-2">
                                <div className="flex-1">
                                  <div className="flex items-baseline gap-2">
                                    <code className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">{prop.name}</code>
                                    {prop.required && (
                                      <span className="text-xs text-red-600 dark:text-red-400">required</span>
                                    )}
                                  </div>
                                  <div className="text-xs text-muted mt-0.5">{prop.type}</div>
                                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{prop.description}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Configurable Props */}
                  {componentDetails.props.filter((p: any) => p.interactive).length > 0 && (
                    <div>
                      <button
                        onClick={() => setSectionsExpanded(prev => ({ ...prev, configurableProps: !prev.configurableProps }))}
                        className="flex w-full items-center justify-between text-sm font-semibold text-body hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                      >
                        <span>Configurable Props</span>
                        <ChevronDownIcon
                          className={`h-4 w-4 transition-transform ${sectionsExpanded.configurableProps ? 'rotate-180' : ''}`}
                        />
                      </button>
                      {sectionsExpanded.configurableProps && (
                        <div className="mt-2 space-y-3">
                          {componentDetails.props.filter((p: any) => p.interactive).map((prop: any, idx: number) => (
                      <div key={idx} className="border-l-2 border-gray-200 dark:border-gray-700 pl-3">
                        <div className="flex items-start gap-2">
                          <div className="flex-1">
                            <div className="flex items-baseline gap-2">
                              <code className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">{prop.name}</code>
                              {prop.required && (
                                <span className="text-xs text-red-600 dark:text-red-400">required</span>
                              )}
                            </div>
                            <div className="text-xs text-muted mt-0.5">{prop.type}</div>
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{prop.description}</p>
                          </div>
                          {prop.interactive && prop.key && (
                            <>
                              {/* Text input for string props */}
                              {prop.inputType === 'text' && selectedComponent?.id === 'tantable' ? (
                                <input
                                  type="text"
                                  value={tanTableProps[prop.key as keyof typeof tanTableProps] as string}
                                  onChange={(e) => {
                                    setTanTableProps(prev => ({
                                      ...prev,
                                      [prop.key]: e.target.value
                                    }));
                                  }}
                                  className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-panel text-body focus:outline-none focus:ring-1 focus:ring-indigo-500 w-full"
                                  placeholder={prop.description}
                                />
                              ) : prop.inputType === 'radio' && prop.options && selectedComponent?.id === 'tantable' ? (
                                <div className="flex flex-col gap-1">
                                  {prop.options.map((option: string) => (
                                    <label key={option} className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name={`tantable-${prop.key}`}
                                        checked={tanTableProps[prop.key as keyof typeof tanTableProps] === option}
                                        onChange={() => {
                                          setTanTableProps(prev => ({
                                            ...prev,
                                            [prop.key]: option
                                          }));
                                        }}
                                        className="h-3 w-3 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                      />
                                      <span className="text-xs text-label capitalize">
                                        {option}
                                      </span>
                                    </label>
                                  ))}
                                </div>
                              ) : prop.key === 'position' && (selectedComponent?.id === 'drawer' || selectedComponent?.id === 'drawer-push') ? (
                                <div className="flex flex-col items-end gap-1">
                                  <div className="text-xs text-muted">
                                    {selectedComponent?.id === 'drawer'
                                      ? (drawerProps.position === 'left' ? 'Left' : 'Right')
                                      : (drawerPushProps.position === 'left' ? 'Left' : 'Right')
                                    }
                                  </div>
                                  <Toggle
                                    checked={selectedComponent?.id === 'drawer' ? drawerProps.position === 'right' : drawerPushProps.position === 'right'}
                                    onChange={(checked) => {
                                      if (selectedComponent?.id === 'drawer') {
                                        setDrawerProps(prev => ({
                                          ...prev,
                                          position: checked ? 'right' : 'left'
                                        }));
                                      } else {
                                        setDrawerPushProps(prev => ({
                                          ...prev,
                                          position: checked ? 'right' : 'left'
                                        }));
                                      }
                                    }}
                                    size="sm"
                                  />
                                </div>
                              ) : prop.key === 'placement' && selectedComponent?.id === 'dropdown' ? (
                                <div className="flex flex-col gap-1">
                                  {(['bottom', 'bottom-start', 'bottom-end'] as const).map((placement) => (
                                    <label key={placement} className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name="dropdown-placement"
                                        checked={dropdownProps.placement === placement}
                                        onChange={() => {
                                          setDropdownProps(prev => ({
                                            ...prev,
                                            placement
                                          }));
                                        }}
                                        className="h-3 w-3 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                      />
                                      <span className="text-xs text-label">
                                        {placement === 'bottom' ? 'Bottom' : placement === 'bottom-start' ? 'Bottom Start' : 'Bottom End'}
                                      </span>
                                    </label>
                                  ))}
                                </div>
                              ) : prop.key === 'type' && selectedComponent?.id === 'toast' ? (
                                <div className="flex flex-col gap-1">
                                  {(['success', 'error', 'info', 'warning'] as const).map((type) => (
                                    <label key={type} className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name="toast-type"
                                        checked={toastProps.type === type}
                                        onChange={() => {
                                          setToastProps(prev => ({
                                            ...prev,
                                            type
                                          }));
                                        }}
                                        className="h-3 w-3 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                      />
                                      <span className="text-xs text-label capitalize">{type}</span>
                                    </label>
                                  ))}
                                </div>
                              ) : prop.key === 'placement' && selectedComponent?.id === 'tooltip' ? (
                                <div className="flex flex-col gap-1">
                                  {(['top', 'right', 'bottom', 'left'] as const).map((placement) => (
                                    <label key={placement} className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name="tooltip-placement"
                                        checked={tooltipProps.placement === placement}
                                        onChange={() => {
                                          setTooltipProps(prev => ({
                                            ...prev,
                                            placement
                                          }));
                                        }}
                                        className="h-3 w-3 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                      />
                                      <span className="text-xs text-label capitalize">{placement}</span>
                                    </label>
                                  ))}
                                </div>
                              ) : prop.key === 'defaultView' && selectedComponent?.id === 'calendar' ? (
                                <div className="flex flex-col gap-1">
                                  {(['month', 'week', 'day', 'year'] as const).map((view) => (
                                    <label key={view} className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name="calendar-view"
                                        checked={calendarProps.defaultView === view}
                                        onChange={() => {
                                          setCalendarProps(prev => ({
                                            ...prev,
                                            defaultView: view
                                          }));
                                        }}
                                        className="h-3 w-3 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                      />
                                      <span className="text-xs text-label capitalize">{view}</span>
                                    </label>
                                  ))}
                                </div>
                              ) : prop.key === 'mode' && selectedComponent?.id === 'datetimepicker' ? (
                                <div className="flex flex-col gap-1">
                                  {(['date', 'time', 'datetime'] as const).map((mode) => (
                                    <label key={mode} className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name="datetimepicker-mode"
                                        checked={dateTimePickerProps.mode === mode}
                                        onChange={() => {
                                          setDateTimePickerProps(prev => ({
                                            ...prev,
                                            mode
                                          }));
                                        }}
                                        className="h-3 w-3 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                      />
                                      <span className="text-xs text-label capitalize">{mode}</span>
                                    </label>
                                  ))}
                                </div>
                              ) : prop.key === 'selectionType' && selectedComponent?.id === 'datetimepicker' ? (
                                <div className="flex flex-col gap-1">
                                  {(['single', 'range'] as const).map((selType) => (
                                    <label key={selType} className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name="datetimepicker-selectionType"
                                        checked={dateTimePickerProps.selectionType === selType}
                                        onChange={() => {
                                          setDateTimePickerProps(prev => ({
                                            ...prev,
                                            selectionType: selType
                                          }));
                                          // Reset values when changing selection type
                                          setDateTimePickerValue(null);
                                          setDateTimePickerRangeValue({});
                                        }}
                                        className="h-3 w-3 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                      />
                                      <span className="text-xs text-label capitalize">{selType}</span>
                                    </label>
                                  ))}
                                </div>
                              ) : prop.key === 'use24Hour' && selectedComponent?.id === 'datetimepicker' ? (
                                <div className="flex flex-col items-end gap-1">
                                  <div className="text-xs text-muted">
                                    {dateTimePickerProps.use24Hour ? '24-hour' : '12-hour'}
                                  </div>
                                  <Toggle
                                    checked={dateTimePickerProps.use24Hour}
                                    onChange={(checked) => {
                                      setDateTimePickerProps(prev => ({
                                        ...prev,
                                        use24Hour: checked
                                      }));
                                    }}
                                    size="sm"
                                  />
                                </div>
                              ) : prop.key === 'disabled' && selectedComponent?.id === 'datetimepicker' ? (
                                <div className="flex flex-col items-end gap-1">
                                  <div className="text-xs text-muted">
                                    {dateTimePickerProps.disabled ? 'Disabled' : 'Enabled'}
                                  </div>
                                  <Toggle
                                    checked={dateTimePickerProps.disabled}
                                    onChange={(checked) => {
                                      setDateTimePickerProps(prev => ({
                                        ...prev,
                                        disabled: checked
                                      }));
                                    }}
                                    size="sm"
                                  />
                                </div>
                              ) : prop.key === 'showTimeQualifier' && selectedComponent?.id === 'datetimepicker' ? (
                                <div className="flex flex-col items-end gap-1">
                                  <div className="text-xs text-muted">
                                    {dateTimePickerProps.showTimeQualifier ? 'Enabled' : 'Disabled'}
                                  </div>
                                  <Toggle
                                    checked={dateTimePickerProps.showTimeQualifier}
                                    onChange={(checked) => {
                                      setDateTimePickerProps(prev => ({
                                        ...prev,
                                        showTimeQualifier: checked
                                      }));
                                    }}
                                    size="sm"
                                  />
                                </div>
                              ) : prop.key === 'labelType' && selectedComponent?.id === 'datetimepicker' ? (
                                <div className="flex flex-col gap-1">
                                  {(['auto', 'none', 'custom', 'date', 'datetime', 'date-range', 'datetime-range', 'time', 'time-range'] as const).map((labelType) => (
                                    <label key={labelType} className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name="datetimepicker-labelType"
                                        checked={dateTimePickerProps.labelType === labelType}
                                        onChange={() => {
                                          setDateTimePickerProps(prev => ({
                                            ...prev,
                                            labelType
                                          }));
                                        }}
                                        className="h-3 w-3 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                      />
                                      <span className="text-xs text-label">{labelType}</span>
                                    </label>
                                  ))}
                                  {dateTimePickerProps.labelType === 'custom' && (
                                    <input
                                      type="text"
                                      value={dateTimePickerProps.customLabel}
                                      onChange={(e) => {
                                        setDateTimePickerProps(prev => ({
                                          ...prev,
                                          customLabel: e.target.value
                                        }));
                                      }}
                                      placeholder="Enter custom label..."
                                      className="mt-2 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-panel text-body focus:ring-indigo-500 focus:border-indigo-500"
                                    />
                                  )}
                                </div>
                              ) : prop.key === 'autoApply' && selectedComponent?.id === 'datetimepicker' ? (
                                <div className="flex flex-col items-end gap-1">
                                  <div className="text-xs text-muted">
                                    {dateTimePickerProps.autoApply ? 'Enabled' : 'Disabled'}
                                  </div>
                                  <Toggle
                                    checked={dateTimePickerProps.autoApply}
                                    onChange={(checked) => {
                                      setDateTimePickerProps(prev => ({
                                        ...prev,
                                        autoApply: checked
                                      }));
                                    }}
                                    size="sm"
                                  />
                                </div>
                              ) : prop.key === 'openDirection' && selectedComponent?.id === 'datetimepicker' ? (
                                <div className="flex flex-col gap-1">
                                  {(['down', 'up', 'left', 'right', 'downleft', 'downright', 'upleft', 'upright'] as const).map((direction) => (
                                    <label key={direction} className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name="datetimepicker-openDirection"
                                        checked={dateTimePickerProps.openDirection === direction}
                                        onChange={() => {
                                          setDateTimePickerProps(prev => ({
                                            ...prev,
                                            openDirection: direction
                                          }));
                                        }}
                                        className="h-3 w-3 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                      />
                                      <span className="text-xs text-label">{direction}</span>
                                    </label>
                                  ))}
                                </div>
                              ) : prop.key === 'inputVariant' && selectedComponent?.id === 'datetimepicker' ? (
                                <div className="flex flex-col gap-1">
                                  {(['default', 'compact', 'modal'] as const).map((variant) => (
                                    <label key={variant} className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name="datetimepicker-inputVariant"
                                        checked={dateTimePickerProps.inputVariant === variant}
                                        onChange={() => {
                                          setDateTimePickerProps(prev => ({
                                            ...prev,
                                            inputVariant: variant
                                          }));
                                        }}
                                        className="h-3 w-3 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                      />
                                      <span className="text-xs text-label">{variant}</span>
                                    </label>
                                  ))}
                                </div>
                              ) : prop.key === 'variant' && selectedComponent?.id === 'toggle' ? (
                                <div className="flex flex-col gap-1">
                                  {(['simple', 'centered', 'with-icons', 'with-label'] as const).map((variant) => (
                                    <label key={variant} className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name="toggle-variant"
                                        checked={toggleProps.variant === variant}
                                        onChange={() => {
                                          setToggleProps(prev => ({
                                            ...prev,
                                            variant
                                          }));
                                        }}
                                        className="h-3 w-3 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                      />
                                      <span className="text-xs text-label">{variant}</span>
                                    </label>
                                  ))}
                                </div>
                              ) : prop.key === 'size' && selectedComponent?.id === 'toggle' ? (
                                <div className="flex flex-col gap-1">
                                  {(['sm', 'md', 'lg'] as const).map((size) => (
                                    <label key={size} className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name="toggle-size"
                                        checked={toggleProps.size === size}
                                        onChange={() => {
                                          setToggleProps(prev => ({
                                            ...prev,
                                            size
                                          }));
                                        }}
                                        className="h-3 w-3 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                      />
                                      <span className="text-xs text-label uppercase">{size}</span>
                                    </label>
                                  ))}
                                </div>
                              ) : prop.key === 'color' && selectedComponent?.id === 'statusbadge' ? (
                                <div className="flex flex-col gap-1">
                                  {(['green', 'yellow', 'red', 'blue', 'indigo', 'purple', 'pink', 'gray'] as const).map((color) => (
                                    <label key={color} className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name="statusbadge-color"
                                        checked={statusBadgeProps.color === color}
                                        onChange={() => {
                                          setStatusBadgeProps(prev => ({
                                            ...prev,
                                            color
                                          }));
                                        }}
                                        className="h-3 w-3 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                      />
                                      <span className="text-xs text-label capitalize">{color}</span>
                                    </label>
                                  ))}
                                </div>
                              ) : prop.key === 'color' && selectedComponent?.id === 'tagbadge' ? (
                                <div className="flex flex-col gap-1">
                                  {(['gray', 'red', 'yellow', 'green', 'blue', 'indigo', 'purple', 'pink'] as const).map((color) => (
                                    <label key={color} className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name="tagbadge-color"
                                        checked={tagBadgeProps.color === color}
                                        onChange={() => {
                                          setTagBadgeProps(prev => ({
                                            ...prev,
                                            color
                                          }));
                                        }}
                                        className="h-3 w-3 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                      />
                                      <span className="text-xs text-label capitalize">{color}</span>
                                    </label>
                                  ))}
                                </div>
                              ) : prop.key === 'roleColor' && selectedComponent?.id === 'contactcard' ? (
                                <div className="flex flex-col gap-1">
                                  {(['green', 'blue', 'purple', 'red', 'yellow', 'indigo', 'pink', 'gray'] as const).map((color) => (
                                    <label key={color} className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name="contactcard-rolecolor"
                                        checked={contactCardProps.roleColor === color}
                                        onChange={() => {
                                          setContactCardProps(prev => ({
                                            ...prev,
                                            roleColor: color
                                          }));
                                        }}
                                        className="h-3 w-3 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                      />
                                      <span className="text-xs text-label capitalize">{color}</span>
                                    </label>
                                  ))}
                                </div>
                              ) : prop.key === 'chartType' && selectedComponent?.id === 'charts' ? (
                                <div className="flex flex-col gap-1">
                                  {(['bar', 'line', 'area', 'pie', 'donut', 'radial'] as const).map((type) => (
                                    <label key={type} className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name="chart-type"
                                        checked={chartProps.chartType === type}
                                        onChange={() => {
                                          setChartProps(prev => ({
                                            ...prev,
                                            chartType: type
                                          }));
                                        }}
                                        className="h-3 w-3 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                      />
                                      <span className="text-xs text-label capitalize">{type}</span>
                                    </label>
                                  ))}
                                </div>
                              ) : prop.key === 'fillColor' && selectedComponent?.id === 'trackerbutton' ? (
                                <div className="flex flex-col gap-1">
                                  {(['bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-red-500', 'bg-indigo-500', 'bg-purple-500'] as const).map((color) => (
                                    <label key={color} className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name="trackerbutton-fillcolor"
                                        checked={trackerButtonProps.fillColor === color}
                                        onChange={() => {
                                          setTrackerButtonProps(prev => ({
                                            ...prev,
                                            fillColor: color
                                          }));
                                        }}
                                        className="h-3 w-3 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                      />
                                      <span className={`text-xs text-gray-700 dark:text-gray-300`}>{color.replace('bg-', '').replace('-500', '')}</span>
                                    </label>
                                  ))}
                                </div>
                              ) : prop.key === 'size' && selectedComponent?.id === 'trackerbutton' ? (
                                <div className="flex flex-col gap-1">
                                  {(['xs', 'sm', 'md', 'lg', 'xl'] as const).map((s) => (
                                    <label key={s} className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name="trackerbutton-size"
                                        checked={trackerButtonProps.size === s}
                                        onChange={() => {
                                          setTrackerButtonProps(prev => ({
                                            ...prev,
                                            size: s
                                          }));
                                        }}
                                        className="h-3 w-3 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                      />
                                      <span className="text-xs text-label uppercase">{s}</span>
                                    </label>
                                  ))}
                                </div>
                              ) : prop.inputType === 'number' && selectedComponent?.id === 'trackerbutton' ? (
                                <input
                                  type="number"
                                  min={2}
                                  max={24}
                                  value={trackerButtonProps.barThickness}
                                  onChange={(e) => {
                                    const val = parseInt(e.target.value) || 8;
                                    setTrackerButtonProps(prev => ({
                                      ...prev,
                                      barThickness: Math.max(2, Math.min(24, val))
                                    }));
                                  }}
                                  className="w-16 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-panel text-body focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                />
                              ) : prop.key === 'orientation' && selectedComponent?.id === 'driverlane' ? (
                                <div className="flex flex-col gap-1">
                                  {(['horizontal', 'vertical'] as const).map((orient) => (
                                    <label key={orient} className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name="driverlane-orientation"
                                        checked={driverLaneProps.orientation === orient}
                                        onChange={() => {
                                          setDriverLaneProps(prev => ({
                                            ...prev,
                                            orientation: orient
                                          }));
                                        }}
                                        className="h-3 w-3 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                      />
                                      <span className="text-xs text-label capitalize">{orient}</span>
                                    </label>
                                  ))}
                                </div>
                              ) : prop.inputType === 'number' && selectedComponent?.id === 'statsgrid' ? (
                                <input
                                  type="number"
                                  min={1}
                                  max={prop.key === 'columns' ? 8 : 10}
                                  value={statsGridProps[prop.key as keyof typeof statsGridProps] as number}
                                  onChange={(e) => {
                                    const val = parseInt(e.target.value) || 1;
                                    setStatsGridProps(prev => ({
                                      ...prev,
                                      [prop.key]: Math.max(1, Math.min(prop.key === 'columns' ? 8 : 10, val))
                                    }));
                                  }}
                                  className="w-16 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-panel text-body focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                />
                              ) : !prop.inputType || prop.inputType === 'checkbox' ? (
                                <input
                                  type="checkbox"
                                  checked={
                                    selectedComponent?.id === 'drawer'
                                      ? (drawerProps[prop.key as keyof typeof drawerProps] as boolean || false)
                                      : selectedComponent?.id === 'drawer-push'
                                        ? (drawerPushProps[prop.key as keyof typeof drawerPushProps] as boolean || false)
                                        : selectedComponent?.id === 'dropdown'
                                          ? (dropdownProps[prop.key as keyof typeof dropdownProps] as boolean || false)
                                          : selectedComponent?.id === 'toast'
                                            ? (toastProps[prop.key as keyof typeof toastProps] as boolean || false)
                                            : selectedComponent?.id === 'tooltip'
                                              ? (tooltipProps[prop.key as keyof typeof tooltipProps] as boolean || false)
                                              : selectedComponent?.id === 'multiselect'
                                                ? (multiSelectProps[prop.key as keyof typeof multiSelectProps] as boolean || false)
                                                : selectedComponent?.id === 'typeahead'
                                                  ? (typeAheadProps[prop.key as keyof typeof typeAheadProps] as boolean || false)
                                                  : selectedComponent?.id === 'toggle'
                                                    ? (toggleProps[prop.key as keyof typeof toggleProps] as boolean || false)
                                                    : selectedComponent?.id === 'buttongroup'
                                                      ? (buttonGroupProps[prop.key as keyof typeof buttonGroupProps] as boolean || false)
                                                      : selectedComponent?.id === 'tabbar'
                                                        ? (tabBarProps[prop.key as keyof typeof tabBarProps] as boolean || false)
                                                        : selectedComponent?.id === 'tagbadge'
                                                          ? (tagBadgeProps[prop.key as keyof typeof tagBadgeProps] as boolean || false)
                                                          : selectedComponent?.id === 'statbutton'
                                                            ? (statButtonProps[prop.key as keyof typeof statButtonProps] as boolean || false)
                                                            : selectedComponent?.id === 'statsgrid'
                                                              ? (statsGridProps[prop.key as keyof typeof statsGridProps] as boolean || false)
                                                              : selectedComponent?.id === 'charts'
                                                                ? (chartProps[prop.key as keyof typeof chartProps] as boolean || false)
                                                                : selectedComponent?.id === 'driverlane'
                                                                  ? (driverLaneProps[prop.key as keyof typeof driverLaneProps] as boolean || false)
                                                                  : Boolean(tanTableProps[prop.key as keyof typeof tanTableProps])
                                  }
                                  onChange={(e) => {
                                    if (selectedComponent?.id === 'drawer') {
                                      setDrawerProps(prev => ({
                                        ...prev,
                                        [prop.key]: e.target.checked
                                      }));
                                    } else if (selectedComponent?.id === 'drawer-push') {
                                      setDrawerPushProps(prev => ({
                                        ...prev,
                                        [prop.key]: e.target.checked
                                      }));
                                    } else if (selectedComponent?.id === 'dropdown') {
                                      setDropdownProps(prev => ({
                                        ...prev,
                                        [prop.key]: e.target.checked
                                      }));
                                    } else if (selectedComponent?.id === 'toast') {
                                      setToastProps(prev => ({
                                        ...prev,
                                        [prop.key]: e.target.checked
                                      }));
                                    } else if (selectedComponent?.id === 'tooltip') {
                                      setTooltipProps(prev => ({
                                        ...prev,
                                        [prop.key]: e.target.checked
                                      }));
                                    } else if (selectedComponent?.id === 'multiselect') {
                                      setMultiSelectProps(prev => ({
                                        ...prev,
                                        [prop.key]: e.target.checked
                                      }));
                                    } else if (selectedComponent?.id === 'typeahead') {
                                      setTypeAheadProps(prev => ({
                                        ...prev,
                                        [prop.key]: e.target.checked
                                      }));
                                    } else if (selectedComponent?.id === 'toggle') {
                                      setToggleProps(prev => ({
                                        ...prev,
                                        [prop.key]: e.target.checked
                                      }));
                                    } else if (selectedComponent?.id === 'buttongroup') {
                                      setButtonGroupProps(prev => ({
                                        ...prev,
                                        [prop.key]: e.target.checked
                                      }));
                                    } else if (selectedComponent?.id === 'tabbar') {
                                      setTabBarProps(prev => ({
                                        ...prev,
                                        [prop.key]: e.target.checked
                                      }));
                                    } else if (selectedComponent?.id === 'tagbadge') {
                                      setTagBadgeProps(prev => ({
                                        ...prev,
                                        [prop.key]: e.target.checked
                                      }));
                                    } else if (selectedComponent?.id === 'statbutton') {
                                      setStatButtonProps(prev => ({
                                        ...prev,
                                        [prop.key]: e.target.checked
                                      }));
                                    } else if (selectedComponent?.id === 'statsgrid') {
                                      setStatsGridProps(prev => ({
                                        ...prev,
                                        [prop.key]: e.target.checked
                                      }));
                                    } else if (selectedComponent?.id === 'charts') {
                                      setChartProps(prev => ({
                                        ...prev,
                                        [prop.key]: e.target.checked
                                      }));
                                    } else if (selectedComponent?.id === 'driverlane') {
                                      setDriverLaneProps(prev => ({
                                        ...prev,
                                        [prop.key]: e.target.checked
                                      }));
                                    } else {
                                      setTanTableProps(prev => ({
                                        ...prev,
                                        [prop.key]: e.target.checked
                                      }));
                                    }
                                  }}
                                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                />
                              ) : null}
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Implementations Section */}
                  {componentImplementations[selectedComponent.id] && componentImplementations[selectedComponent.id].length > 0 && (
                    <div>
                      <button
                        onClick={() => setSectionsExpanded(prev => ({ ...prev, implementations: !prev.implementations }))}
                        className="flex w-full items-center justify-between text-sm font-semibold text-body hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                      >
                        <span>Implementations</span>
                        <ChevronDownIcon
                          className={`h-4 w-4 transition-transform ${sectionsExpanded.implementations ? 'rotate-180' : ''}`}
                        />
                      </button>
                      {sectionsExpanded.implementations && (
                        <div className="mt-2 space-y-2">
                          <p className="text-xs text-muted mb-3">
                            This component is used in {componentImplementations[selectedComponent.id].length} location{componentImplementations[selectedComponent.id].length !== 1 ? 's' : ''} throughout the codebase.
                          </p>
                          {componentImplementations[selectedComponent.id].map((impl, idx) => (
                            <div key={idx} className="border-l-2 border-indigo-200 dark:border-indigo-700 pl-3 py-1">
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="text-xs font-medium text-heading">
                                    {impl.route}
                                  </div>
                                  {impl.isReusable && (
                                    <div className="text-xs text-muted mt-0.5">
                                      (Reusable component - may be used on multiple pages)
                                    </div>
                                  )}
                                </div>
                                <div className="ml-2 flex-shrink-0">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300">
                                    {impl.count} {impl.count === 1 ? 'use' : 'uses'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          ) : selectedComponent ? (
            <div>
              <h3 className="text-lg font-semibold text-heading">Details</h3>
              <p className="mt-4 text-sm text-muted italic">
                Documentation coming soon for {selectedComponent.name}
              </p>
            </div>
          ) : (
            <div className="flex h-full items-center justify-center">
              <p className="text-sm text-muted">No component selected</p>
            </div>
          )}
        </div>

        {/* Main area - Component Preview */}
        <div className="flex flex-1 flex-col overflow-hidden bg-gray-50 dark:bg-gray-800">
          {selectedComponent ? (
            <>
              {/* Top 60% - Example */}
              <div className="flex flex-col px-4 pt-6 pb-4 sm:px-6 lg:px-8" style={{ height: '60%' }}>
                <div className="flex flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                  {renderComponentExample()}
                </div>
              </div>

              {/* Bottom 40% - Usage Code */}
              <div className="flex flex-col border-t border-gray-200 dark:border-gray-700 px-4 py-4 sm:px-6 lg:px-8" style={{ height: '40%' }}>
                <h3 className="mb-2 text-sm font-semibold text-heading">Usage Example</h3>
                <div className="flex-1 overflow-auto rounded-md bg-gray-900">
                  <pre className="p-3 text-xs text-gray-100">
                    {generateUsageCode()}
                  </pre>
                </div>
              </div>
            </>
          ) : (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <h3 className="text-lg font-medium text-heading">Select a component</h3>
                <p className="mt-2 text-sm text-muted">
                  Choose a component from the navigation above to view its preview and details
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComponentLibrary;
